// Core DB access for the Intake tab.
// All core reads/writes go through api.sentavus.com (which holds the core service_role
// key server-side) — the key NEVER reaches the browser bundle. Guarded by the shared
// scheduler secret, same as the parse endpoint.

import { getAccessToken } from './index.js'
import { applySearchFilterParams } from '../lib/searchWire.js'
import { searchedDistricts } from '../lib/districtFilter.js'
import { invalidNumericBounds } from '../lib/deskFilters.js'
import { makeCoalescer } from '../lib/coalesce.js'
import { ttlFromHeader, loadFromHeader, isDeskMutation, markDeskDirty } from '../lib/deskFreshness.js'
import { tbilisiParts } from '../lib/tbilisiTime.js' // getMeetingsFeed's dateTo cut (review 2026-09-23)

const SNTV_API  = "https://api.sentavus.com";
// 🚨 B53 step 2c (2026-07-26): there is deliberately NO shared key here any more. The old
// `SCHED_KEY = import.meta.env.VITE_SCHEDULER_SECRET` shipped in the PUBLIC bundle (Vite
// inlines VITE_* at build time), so anyone could read it out of the JS and call every
// /api/core/* endpoint. Browser calls now authenticate with the agent's own verified
// session (x-viewer-token) and nothing else. Never add a VITE_-prefixed credential back.

// The agent's OWN scheduler session token (Track 1, 2026-07-19) — sent so the server can
// VERIFY who is viewing (x-viewer-token → schedAuthUser) instead of trusting a client
// claim. Absent when not logged in → the server falls back to its Stage-A legacy path.
//
// ⚠️ Delegates to db/index.js `getAccessToken` (cached + 3s deadline) and MUST keep doing
// so. This used to call `supabase.auth.getSession()` on every request under a comment
// claiming it was "a local read (no network)" — false once the token expires, and it
// deadlocked the whole Desk behind gotrue's refresh lock (2026-07-26; see that function's
// header). `coreApi` awaits this before it fetches, so anything unbounded here freezes
// every core call with no request, no error and no way for a .catch() to recover.
const viewerToken = getAccessToken

// Concurrent duplicate GETs share one network call — see the module header of
// coalesce.js for what was measured and why this is deliberately not a cache.
// A request carrying a body or an abort signal is never shared: the signal belongs
// to one caller, and aborting a shared promise would cancel a stranger's read.
const coalescedGet = makeCoalescer()

// ── The transport limiter (B934's client half, 2026-08-26) ─────────────────────────────────
// Measured on a cold Desk load BEFORE this: 73 requests, PEAK 37 IN FLIGHT (the API is
// HTTP/2, so the browser's per-host cap does not apply), summed request time 142 s against a
// 9.3 s wall clock. Inside that burst a 145 ms call (proposal-match-counts, isolated) took
// 7,069 ms — 49× queueing inflation — and the ONE call the agent actually waits on
// (agent-listings: the shimmer clears on it) paid ~1.6× for its own page's satellites.
//
// So: at most MAX_INFLIGHT requests leave this client at once, and the FEED always jumps the
// queue. This does not reduce total work — thumbnails and chips fill in behind real cards a
// beat later, where nobody is watching — it decides who goes first, which is the only thing
// the burst was getting wrong.
//
// ⚠️ NOT a server protector — the server has its own four-slot gate (withSearchSlot) — and
// NOT a cache (coalesce.js's header owns that rule). It orders THIS tab's politeness.
// ⚠️ An aborted-while-queued request still acquires and releases its slot: fetch() with an
// already-aborted signal rejects immediately, and the finally releases. Skipping the acquire
// for aborted signals instead would fork two lifecycles for one request — not worth it.
const MAX_INFLIGHT = 6
let inflightCount = 0
const laneHi = []   // agent-listings — the paint the agent is waiting on
const laneLo = []   // everything else, FIFO
function acquireSlot(hi) {
    if (inflightCount < MAX_INFLIGHT) { inflightCount++; return Promise.resolve() }
    return new Promise(res => (hi ? laneHi : laneLo).push(res))
}
function releaseSlot() {
    const next = laneHi.shift() || laneLo.shift()
    if (next) next()          // hands the slot over — count unchanged
    else inflightCount--
}

async function coreApi(path, { method = "GET", body, signal } = {}) {
    const send = async () => {
        // Priority by PATH, deliberately crude: the feed request is the one the shimmer
        // waits on. Everything else — thumbs, terms, cooperations, shares, dormancy,
        // descriptions, counts — is decoration that can land a beat later.
        await acquireSlot(path.startsWith('agent-listings'))
        try {
        const token = await viewerToken()
        const r = await fetch(`${SNTV_API}/api/core/${path}`, {
            method,
            headers: {
                "Content-Type": "application/json",
                ...(token ? { "x-viewer-token": token } : {}),
            },
            body: body ? JSON.stringify(body) : undefined,
            signal,
        });
        const data = await r.json().catch(() => ({}));
        // Desk cache TTL + load signal (owner 2026-09-24, see lib/deskFreshness.js). Attached
        // NON-ENUMERABLE so no existing caller sees a new key — a spread, JSON.stringify or
        // Object.keys over `data` is unchanged. Only the Desk feed readers look at them.
        // ⚠️ Cross-origin: the browser exposes these only if the server lists them in
        // Access-Control-Expose-Headers; when absent the 300 s fallback applies.
        if (data && typeof data === 'object') {
          try {
            Object.defineProperty(data, '__deskCacheTtlMs', { value: ttlFromHeader(r.headers?.get?.('x-desk-cache-ttl')), enumerable: false })
            Object.defineProperty(data, '__deskLoad', { value: loadFromHeader(r.headers?.get?.('x-desk-load')), enumerable: false })
          } catch { /* frozen / exotic body — no hint, fallback TTL */ }
        }
        // The agent's OWN save: no cached Desk answer written before now may be served as fresh
        // (owner 2026-09-24: "own saves still refresh immediately"). The deny-list of read-only
        // POSTs lives in deskFreshness.js — an unlisted new mutation marks dirty, which fails safe.
        if (r.ok && isDeskMutation(method, path)) markDeskDirty()
        // ⚠️ `message` IS THE SERVER'S ENGLISH STRING, and several callers render it straight into a
        // Georgian screen (`setErr(e.message || 'ვერ წაიშალა')` — note the fallback can never fire,
        // because message is always truthy). Carrying `code` and `status` alongside gives a caller
        // something STABLE to branch on, so it can choose its own wording instead of forwarding
        // prose that was written for a log. Additive: nothing that reads `e.message` changes.
        //
        // ⚠️ These fields survive coalescing: sharers of one in-flight GET receive this SAME
        // error object, so anything that branches on `code`/`status` behaves identically whether
        // it issued the request or joined one. Never mutate a caught error in place — with
        // sharing, a annotation written by one caller would be read by another.
        if (!r.ok) {
          const err = new Error(data.error || `${path} ${r.status}`);
          err.code = data.code || null;
          err.status = r.status;
          // The WHOLE error body, for the callers whose error carries more than a code —
          // the contract's `translation_drift` answers `issues[]` naming the clause and
          // language (core/CONTRACT_API_CONTRACT.md). Additive: `message`/`code`/`status`
          // are untouched, and a caller that never reads `body` sees nothing new.
          err.body = data;
          throw err;
        }
        return data;
        } finally { releaseSlot() }
    }
    if (method === "GET" && !body && !signal) return coalescedGet(path, send)
    return send()
}

// ── Desk feed cache (stale-while-revalidate) ─────────────────────────────────
// MOVED to ./deskCache.js on 2026-08-09 and re-exported here so no caller changed. The reason
// for the split is testability: this file imports ./index.js → the supabase client, so the
// eviction policy could never run under `node --test`. It now has one.
export { readDeskCache, writeDeskCache, CACHE_VER, CACHE_PREFIX } from './deskCache.js'

// Nightly dedup-cron alerts (2026-07-12): /api/health/deep is an intentionally open
// endpoint (no key) that carries dedup_alerts — last-48h failure lines appended by
// nightly_dedup.sh on the box. The Dedup tab shows a warning badge when non-empty,
// because a silently-dead cron is worse than no cron. Errors → [] (badge absent, the
// tab itself still works).
export async function getDedupHealthAlerts() {
    try {
        const r = await fetch(`${SNTV_API}/api/health/deep`);
        const d = await r.json().catch(() => ({}));
        return Array.isArray(d.dedup_alerts) ? d.dedup_alerts : [];
    } catch { return []; }
}

// System alerts for the NOTIFICATIONS BELL (2026-08-05, owner-requested).
// Same open /api/health/deep, but for the destination the owner actually looks at.
// WHY: on 2026-08-05 the nightly dedup audit had failed twice (08-02, 08-05) and
// discovery had starved two portals for ~60h. Every one of those alerts WAS
// recorded and WAS being served by this endpoint — but `dedup_alerts` only ever
// rendered as a badge inside the Dedup tab, and `worker_alerts` had no client
// reader at all. A failure surface nobody stands in front of is not a surface.
// (That is the same rule lib/worker_alerts.js states about console.error.)
//
// Two server streams, ONE shaped list, newest-first:
//   worker_alerts — {worker, severity, message, context, created_at} (table, 0164)
//   dedup_alerts  — raw log lines "<iso> <worker>: <message>" from the cron scripts
// A `key` is included so the bell can badge only what is NEW since notifSeenAt,
// and severity is preserved so 'warn' can render calmer than 'error'.
export async function getSystemAlerts() {
    try {
        const r = await fetch(`${SNTV_API}/api/health/deep`);
        const d = await r.json().catch(() => ({}));
        const out = [];
        for (const a of Array.isArray(d.worker_alerts) ? d.worker_alerts : []) {
            out.push({
                key: `w-${a.worker}-${a.created_at}`,
                worker: a.worker || 'worker',
                severity: a.severity || 'error',
                message: a.message || '',
                at: a.created_at,
            });
        }
        for (const line of Array.isArray(d.dedup_alerts) ? d.dedup_alerts : []) {
            // "2026-08-05T03:51:37+00:00 nightly_dedup: phone_dup_scan.js audit FAILED (…)"
            const m = String(line).match(/^(\S+)\s+([a-z_]+):\s*(.*)$/i);
            const at = m && !Number.isNaN(Date.parse(m[1])) ? m[1] : new Date().toISOString();
            out.push({
                key: `d-${at}-${String(line).slice(-24)}`,
                worker: m ? m[2] : 'dedup',
                severity: /skipped|warn/i.test(line) ? 'warn' : 'error',
                message: m ? m[3] : String(line),
                at,
            });
        }
        // COLLAPSE REPEATS. A watchdog re-raises a standing condition on every tick:
        // measured live 2026-08-05, "portal starved: bog.ge…" appeared 5× in 90
        // minutes and 10 of 11 rows were two messages repeated. worker_alerts.js
        // throttles per PROCESS (10 min), which cannot dedupe across ticks, and the
        // endpoint returns them all. Uncollapsed this stream would BE the noise the
        // bell is supposed to cut through — the same failure as the ended-listing
        // flood. One row per (worker, message): newest timestamp, with the repeat
        // count kept so "still broken, 5 times" stays visible.
        const byMsg = new Map();
        for (const a of out) {
            const k = `${a.worker}|${a.message}`;
            const prev = byMsg.get(k);
            if (!prev) { byMsg.set(k, { ...a, repeats: 1 }); continue; }
            prev.repeats++;
            if (new Date(a.at) > new Date(prev.at)) { prev.at = a.at; prev.key = a.key; }
        }
        return [...byMsg.values()].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 20);
    } catch { return []; }
}

// ── New developments (primary market) — the developers' own inventory, core 1634 ──────────
// Desk-only for now (owner 2026-09-21). Prices come back in the currency the developer quoted,
// with their payment plan; nothing here converts or merges them. See lib/core_developments.js.
export async function getDevelopments() {
    return (await coreApi("developments")).rows || [];
}
export async function getDevelopment(id) {
    return coreApi(`developments/${encodeURIComponent(id)}`);
}
// Seller / contact (core 1681): the named companies for the Seller filter, and each card's strongest seller row.
export async function getSellerCompanies() {
    return (await coreApi("sellers/companies")).companies || [];
}
export async function getSellersByProperty(propertyIds) {
    const ids = [...new Set(propertyIds)].filter(Boolean);
    const out = {};
    for (let i = 0; i < ids.length; i += 150)
        Object.assign(out, (await coreApi(`sellers/by-property?property_ids=${ids.slice(i, i + 150).join(",")}`)).sellers || {});
    return out;
}
// Ranges for developer flats that ride the feed as ordinary apartment cards (core 1663): property_id → card.
export async function getDevelopmentCards(propertyIds) {
    const ids = [...new Set(propertyIds)].filter(Boolean);
    const out = {};
    for (let i = 0; i < ids.length; i += 150)
        Object.assign(out, (await coreApi(`developments/cards?property_ids=${ids.slice(i, i + 150).join(",")}`)).cards || {});
    return out;
}

// The developer's floor plans for one card (core 1725): floor image + every flat outline, `mine` = the card's flats.
export async function getDevelopmentFloorplan(propertyId) {
    return (await coreApi(`developments/floorplan?property_id=${encodeURIComponent(propertyId)}`)).plans || [];
}

// ── Reference data ────────────────────────────────────────────────────────────

export async function getDistricts() {
    return (await coreApi("districts")).districts || [];
}

let _featuresPromise = null;
export async function getFeatures() {
    // Feature vocab is static reference data — fetch once per session, not on every modal open.
    // RentalTermsByAgent (and other panels) fire this alongside getListingTermsBatch every time
    // the lineage modal opens; a fresh full-vocab fetch each time added latency to the
    // restrictions panel (2026-07-21). Cache the in-flight PROMISE (concurrent callers share one
    // request) and hand each caller its OWN array copy, so an in-place sort never corrupts the
    // cache. A failed fetch clears the cache so the next call retries instead of caching the miss.
    if (!_featuresPromise) {
        _featuresPromise = coreApi("features").then(r => {
            // restriction_expansion (0217) rides the same response — it IS vocabulary. Kept
            // in its own cache rather than bolted onto the features array so getFeatures'
            // contract (an array of features) is unchanged for its ~10 existing callers.
            _expansion = r.expansion || [];
            _kindFeatures = r.kind_features || [];   // 0759, same reason: it IS vocabulary
            _conditionLadder = r.condition_ladder || null;   // 0788-0790, same reason again
            return r.features || [];
        }).catch(e => { _featuresPromise = null; throw e; });
    }
    return (await _featuresPromise).slice();
}

// parent_subject → { child_subject: tier } for the restriction search (0217). Empty until
// getFeatures() has resolved once, and empty against a server older than this deploy — both
// degrade to direct-stance-only matching, which is exactly the pre-0217 behavior. Callers
// must therefore treat "no expansion" as normal, never as an error.
let _expansion = [];
// 0759 — which feature groups a kind can answer at all. Rides the features response because it is
// vocabulary about vocabulary, kept in its own cache so getFeatures' contract (an array of
// features) is unchanged for its ~10 callers.
// ⚠️ Empty against a server older than this deploy, and capabilityFilter treats empty as
// "everything applies" — so the panel degrades to exactly today's answer rather than to a blank.
let _kindFeatures = [];
// 0788-0790 — the condition ladder as DATA: stages, rungs, own buckets, the class ladder and
// the sliding completion buckets. Rides the features response because it is vocabulary about
// vocabulary (see _kindFeatures directly above).
// ⚠️ null against a server older than this deploy, and every consumer must read null as "use the
// hard-coded CONDITION_LADDERS" — which is exactly the pre-0788 panel. A vocabulary fetch that
// half-fails must never leave the condition facet with nothing in it.
let _conditionLadder = null;
export async function getConditionLadder() {
  await getFeatures();
  return _conditionLadder;
}
export async function getKindFeatureCapability() {
  await getFeatures();
  return _kindFeatures.slice();
}
export async function getRestrictionExpansion() {
    await getFeatures();
    const map = {};
    for (const e of _expansion) {
        if (!e?.parent_subject || !e?.child_subject) continue;
        (map[e.parent_subject] ||= {})[e.child_subject] = e.tier;
    }
    return map;
}

// Approximate coordinates for a listing with no precise lat/lng (a real map, not an empty
// placeholder, per the Desk gallery's map-slide fallback). Server caches + rate-limits against
// Nominatim; this just wraps one call — cascading through multiple query strings, if needed,
// is the caller's job (Georgian district text doesn't always resolve, city-level usually does).
export async function geocode(query) {
    if (!query?.trim()) return null
    return (await coreApi(`geocode?q=${encodeURIComponent(query.trim())}`)).coords || null
}

// Address autocomplete (Add-property modal): up to 5 Georgia-bounded Nominatim candidates.
// district/city (optional) scope the search server-side — structured street query first
// (handles house numbers), then free-text, then the street name with the number stripped.
// Server-side rate limit is ~1 req/sec (shared geocode queue) — debounce before calling.
export async function geocodeSuggest(query, { district, city } = {}) {
    if (!query?.trim()) return []
    const p = new URLSearchParams({ q: query.trim() })
    if (district) p.set('district', district)
    if (city) p.set('city', city)
    return (await coreApi(`geocode/suggest?${p}`)).suggestions || []
}

// Wide-range search over the NEW core DB (phone number, or a myhome/ss/korter listing id —
// see server.js GET /api/core/listings/search). The OLD drafts DB search (searchDrafts in
// db/index.js) stays id/url/cadastral only, unchanged — this is additive, surfaced alongside
// it in the meeting-intake property picker (DealsTab.jsx DraftPicker).
// agentId (optional) scopes results to that agent's own listings (authored reshares +
// ads they scraped) — server-side, same ownership definition as getAgentListings 'mine'.
// Admin/manager callers omit it and search everything.
// widen: 'identity' (B260) — pass WITH agentId for identity queries (phone / pasted
// listing id): the ownership filter is skipped so market listings answer too, while the
// agent stays the viewer for contact masking. Never a substitute for omitting agentId —
// omitting it is the ADMIN mode and returns unmasked contacts.
export async function searchCoreListings(q, { agentId, widen } = {}) {
    if (!q || q.trim().length < 3) return [];
    const p = new URLSearchParams({ q: q.trim() });
    if (agentId) p.set('agent_id', agentId);
    if (widen) p.set('widen', widen);
    return (await coreApi(`listings/search?${p}`)).results || [];
}

// Property viewings summary, PROJECTED server-side (Track 2 meetings-PII): the viewing
// fact + representing agent for everyone, the client name/phone for the hosting agent
// (or management) only. Replaces the client-side scheduler-table read that shipped every
// client's identity to every agent. The viewer is the verified x-viewer-token (coreApi).
// Chunked (BACKLOG B70): source_urls are FULL listing URLs (~95-105 bytes each once
// percent-encoded), not uuids, so this blows nginx's 8KB request line at only ~80 of
// them — and the caller `.catch`es, silently blanking the modal's meetings section. The
// count is NOT "cards on screen": it's sibling fan-out on ONE property (every reshare is
// its own listing row), so a hot listing reposted by many agents reaches it. 40 per
// request because URLs are ~3x a uuid; ids keep the usual 80.
export async function getPropertyViewings(urls = [], coreListingIds = []) {
    const wanted = [...new Set((urls || []).filter(Boolean))];
    const coreIds = [...new Set((coreListingIds || []).filter(Boolean))];
    if (!wanted.length && !coreIds.length) return [];
    const batches = [];
    for (let i = 0; i < wanted.length; i += 40) batches.push({ u: wanted.slice(i, i + 40), c: [] });
    for (let i = 0; i < coreIds.length; i += 80) batches.push({ u: [], c: coreIds.slice(i, i + 80) });
    const pages = await Promise.all(batches.map(b => {
        const p = new URLSearchParams();
        if (b.u.length) p.set('source_urls', b.u.join(','));
        if (b.c.length) p.set('core_listing_ids', b.c.join(','));
        return coreApi(`property-viewings?${p}`);
    }));
    // de-dup: a viewing can match on both its source_url and its core_listing_id
    const byId = new Map();
    for (const pg of pages) for (const v of (pg.viewings || [])) byId.set(v.id, v);
    return [...byId.values()];
}

// ── Meetings as a core entity (0194/0195; core/MEETINGS_MODEL.md) ────────────
// ⚠️ These carry RELATIONSHIPS ONLY — property, client, requirement, the two
// representative snapshots, attendees, time, status. No name, no phone, ever. The
// contact an agent may see is resolved by the surfaces that already own that
// question (reveal / property contacts / the agents roster). Adding a contact
// field to this payload invents a second permission model — see the ⚠️ header in
// lib/core_meeting.js.
//
// createMeeting AUTO-FILES the access requests for any side the creator does not
// represent (server-side). The response's `access_requests[]` says what was filed;
// a meeting is created regardless — it never waits for approval.
// owner 2026-09-23: for a viewing/negotiation/signing purpose the server refuses (400)
// `property_required`, `client_required` or `representatives_required` (body.missing: sides)
// — both representative ids must be sent explicitly; the create form never leaves them to
// the server's resolution any more.
export async function createMeeting({ propertyId, clientPartyId, requirementId, scheduledAt, notes, status, attendeePartyIds, listingId, propertyRepPartyId, propertyRepAgentId, guestAgentIds, notAttending, clientRepPartyId, clientRepAgentId, purposeCode, commissionSplit, previousMeetingId, allowUnavailable } = {}) {
    return coreApi('meeting', {
        method: 'POST',
        body: {
            // WHY this meeting exists (0427) — required by the creation UI for new
            // meetings; the preview's suggested_purpose pre-selects the chip, the
            // agent confirms. Codes from meeting_purpose (getMeetingVocab).
            purpose_code: purposeCode || null,
            property_id: propertyId || null,
            client_party_id: clientPartyId || null,
            requirement_id: requirementId || null,
            // The ad the booking came from (0357) — provenance the deal-close door reads so
            // it stops asking which of the property's postings this was about. The server
            // drops it silently until the migration is applied.
            listing_id: listingId || null,
            // A CHOSEN representative (owner 2026-08-04). Only ever one of the ids the
            // preview offered — the server re-derives that list and rejects anything else,
            // so this resolves an ambiguity it refused to guess at rather than asserting one.
            property_representative_party_id: propertyRepPartyId || null,
            // 0662 — the roster tier. A colleague picked from the full agent list arrives as
            // a scheduler agents.id; the server resolves the 0040 bridge and records the
            // choice as `basis:'declared'`, and refuses the write outright unless the caller
            // is management or already represents this flat. Only ever one of the two is set.
            property_representative_agent_id: propertyRepAgentId || null,
            // ⚠️ The CLIENT rep has a second, wider door (0449): it may also be an agent
            // from ANOTHER agency, whom the server could not resolve and therefore records
            // as `basis:'declared'`. It still may not be one of our own agents smuggled in
            // as an outsider — the server checks the roster bridge and 400s.
            client_representative_party_id: clientRepPartyId || null,
            // A colleague picked from the roster arrives as a scheduler agents.id; the
            // server resolves the 0040 bridge to a party. Only one of the two is ever set.
            client_representative_agent_id: clientRepAgentId || null,
            // The co-broke ratio (0449): [{side:'us'|'external', party_id?, percent}].
            // Omitted entirely when absent, because on the PATCH door `undefined` means
            // "not mentioned" and `null` means "clear" — two different acts, and sending
            // the wrong one deletes a split nobody meant to touch.
            ...(commissionSplit ? { commission_split: commissionSplit } : {}),
            // 2026-09-23: the server refuses (409 listing_unavailable) a viewing on a flat that
            // is off the market unless the agent saw the warning and went ahead anyway.
            ...(allowUnavailable ? { allow_unavailable: true } : {}),
            scheduled_at: scheduledAt || null,
            notes: notes || null,
            status: status || 'scheduled',
            attendee_party_ids: attendeePartyIds || [],
            // WHO IS GOING, chosen at creation (owner 2026-08-19). Scheduler agent ids, and
            // a SEPARATE field from the line above on purpose: `attendee_party_ids` writes
            // involved:true (the commission set), these write involved:false. A guest walks
            // in; they do not join the deal.
            guest_agent_ids: guestAgentIds || [],
            // 0690 — the visit this one follows (repeat booking). Omitted, not null, when
            // unknown: the server then chains to the visit it inherited the reps from.
            ...(previousMeetingId ? { previous_meeting_id: previousMeetingId } : {}),
            // A representative who is not going (owner 2026-08-19). [{party_id?, agent_id?}].
            // ⚠️ Attendance, NOT membership: the server keeps these people `involved` and
            // only writes attending:false. Taking someone out of the deal is a different act
            // with a different door (the representative slots).
            not_attending: notAttending || [],
            viewer_agent_id: _viewerAgentId || null,
        },
    }); // { meeting, access_requests[] }
}

// WHO WILL BE RECORDED, before the agent presses create (owner 2026-08-01). Runs the create
// handler's OWN resolvers server-side, so the preview cannot disagree with what gets saved —
// which is the whole point, and why this is a server call rather than a client-side guess.
// Also returns the authoritative will-it-ask flags (the seed's needs*Access are guesses).
export async function previewMeeting({ propertyId, clientPartyId, requirementId } = {}) {
    const p = new URLSearchParams()
    if (propertyId) p.set('property_id', propertyId)
    if (clientPartyId) p.set('client_party_id', clientPartyId)
    if (requirementId) p.set('requirement_id', requirementId)
    if (![...p.keys()].length) return null
    if (_viewerAgentId) p.set('viewer_agent_id', _viewerAgentId)
    // owner 2026-09-23: also { viewer, may_self_declare_property_representative, cooperation_live }
    // — the create form offers "Me" on an unrepresented flat and asks for cooperation terms.
    return coreApi(`meeting/preview?${p}`); // { property_representative, client_representative, will_request_* }
}

// Meeting MARKERS for the Listings activity rail — [{ id, at, status, agent_ids, note? }].
// Everyone sees everyone's (owner ruling 2026-08-01: a colleague being out on a viewing
// is the same class of fact as their posting pins), which is exactly why the payload
// carries no property, no client and no representative. Don't reach for this when you
// need meeting DETAIL — getMeetingsFeed is the viewer-scoped door.
//
// ⚠️ `note` is the ONE viewer-scoped field here (2026-08-04): the meeting's latest comment,
// present only for meetings this viewer is on, and OMITTED — not null — otherwise. Treat its
// absence as "no answer", never as "no comment".
// The agent roster from the SERVER (scheduler service key), for surfaces that must be able
// to name an agent even when the SPA's own `agents` read came back empty. Identity fields
// only — see lib/sched_roster.js for what it refuses to return and why.
//
// ⚠️ A FALLBACK, not a second source of truth. Call it only when the real roster is empty;
// everything that edits an agent still goes through db/index.js and the scheduler project.
export async function getSchedulerRoster() {
    const r = await fetch(`${SNTV_API}/api/scheduler/roster`, {
        headers: { ...(await viewerToken().then(tk => tk ? { 'x-viewer-token': tk } : {})) },
    });
    if (!r.ok) throw new Error(`roster ${r.status}`);
    return (await r.json()).agents || [];
}

export async function getMeetingMarkers({ from, to } = {}) {
    const p = new URLSearchParams({ from, to });
    return (await coreApi(`meetings/activity?${p}`)).markers || [];
}

// `propertyId` asks for ONE FLAT'S OWN HISTORY, and it is not merely a filter (owner ruling
// 2026-08-15): the server widens the answer for an agent who REPRESENTS that property, so
// they see what colleagues have shown there and not only the viewings they personally
// attended. Scoped to a named property on purpose — widening the whole feed would mean
// resolving representation for every property in it, on the Desk's busiest read. Asking
// about one flat costs one resolver call. Without it, the old rule stands: you see the
// meetings you are on. The server expands the property to its CLUSTER.
export async function getMeetings({ status, since, propertyId } = {}) {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (since) p.set('since', since);
    if (propertyId) p.set('property_id', propertyId);
    const qs = p.toString();
    return (await coreApi(`meetings${qs ? '?' + qs : ''}`)).meetings || [];
}

// Status / time / notes — and, since 2026-08-01, the CLIENT side (see
// setMeetingClient). The PROPERTY side and its representative snapshot remain
// immutable (a meeting about a different property is a different meeting) — the
// server rejects those.
//
// `attendance: [{ party_id | scheduler_agent_id, attending, remove }]` states who is going
// (tri-state; null = not stated), ADDS a guest — an agent who attends without being in the
// deal — or takes one off again (0404). Guests are `involved:false` server-side and enter no
// commission and no ranking. A response to an attendance write carries the whole `attendees`
// list, which is what the caller should render: it never learns a new guest's party id
// otherwise.
export async function updateMeeting(id, patch) {
    return coreApi(`meeting/${id}`, { method: 'PATCH', body: patch });
}

// WHAT HAPPENED TO THIS MEETING, AND WHEN (0580, owner 2026-08-15).
// Two streams merged server-side, newest first: `change` entries from the append-only
// `event` log — which has recorded every edit since 0194 and which nothing could read
// back per meeting until now — and `comment` entries from `meeting_comment`.
// Entries carry `actor_name` already resolved: a party id on a timeline is not a fact
// anyone can read, and every caller would otherwise resolve it a different way.
// ⚠️ A comment with `imported: true` came from the old `meeting.notes` blob. Its author
// and its time are genuinely UNKNOWN, and the UI must say so rather than draw it as
// something a person wrote at that instant.
export async function getMeetingHistory(id) {
    return (await coreApi(`meeting/${id}/history`)).history || [];
}

// A comment that stands alone. One written WITH a status change rides the PATCH instead
// (its `comment` field), so the cause and the record cannot land separately.
export async function addMeetingComment(id, body, statusTag = null) {
    return coreApi(`meeting/${id}/comment`, { method: 'POST', body: { body, status_tag: statusTag } });
}

// ── Agents at other agencies (0449) ─────────────────────────────────────────
// The co-broke counterparty. Two results, and the difference matters to the UI:
//   known[]      — parties we have already recorded; they HAVE a party_id and can be
//                  picked straight into a meeting.
//   directory[]  — rows from the scraped agency-roster dictionary (0204, 3,709 phones).
//                  They have NO party_id: they are names we saw on a portal, not
//                  identities we hold. Picking one posts it to createExternalAgent,
//                  which is what turns it into a party.
// Searching for fewer than two characters returns nothing rather than everything.
export async function searchExternalAgents(q) {
    if (!q || q.trim().length < 2) return { known: [], directory: [] };
    return coreApi(`external-agents?q=${encodeURIComponent(q.trim())}`);
}

// Find-or-create the party for an outside agent. Keyed on the PHONE server-side (through
// resolve_party), so two of our agents naming the same person land on one party instead of
// two rivals — the failure this system already measured on property contacts.
//
// ⚠️ Answers 409 with `is_ours:true` when the phone turns out to belong to a colleague.
// That is not an error to swallow: the caller should offer them as an ordinary internal
// representative instead, never stamp a roster member as a counterparty.
export async function createExternalAgent({ name, phone, agency, source } = {}) {
    return coreApi('external-agent', { method: 'POST', body: { name, phone, agency, source } });
}

// ── Purpose + reason vocabularies and nudges (0427) ──────────────────────────
// Vocabulary is ROWS, not constants — the lists live in meeting_purpose /
// meeting_reason so adding one is an INSERT, not a deploy. Cached per session:
// vocabulary changes are rare, and the chips must not flicker on every modal open.
let _meetingVocab = null;
export async function getMeetingVocab() {
    if (_meetingVocab) return _meetingVocab;
    _meetingVocab = await coreApi('meeting-vocab'); // { purposes[], reasons[] }
    return _meetingVocab;
}

// ── The BINDER / ბე (0443/0444, renamed 0445/0446) ───────────────────────────
// ⚠️ NOT a "deposit". In this schema that word already means the refundable RENTAL
// security deposit (listing_terms.deposit_amount / deposit_months) — paid upfront by a
// tenant, returned at the end of tenancy less any damage. A binder is earnest money
// securing a SALE. Two unrelated facts; the entity was briefly misnamed and 0445 fixed it.
// Owner ruling 2026-08-06: "a binder is a financial event, not a workflow stage.
// The event survives; the workflow does not." So it is NOT a patch on the meeting:
// `meeting_id` is nullable server-side, a binder may exist before a deal and
// outlives one that fails. Never fold these back into updateMeeting().
//
// ⚠️ The money rules live in the DATABASE (0444) — deadline required, shares total
// 100, a confirmed amount needs a reason, settled is final. Do NOT restate them
// here. A second copy is a copy that drifts, and the one that drifts is the one
// the agent is looking at. What the client MAY do is disable a button early; what
// it must never do is decide.
// ── B378 · the by-PROPERTY read is cached; nothing else is ──────────────────────────────
// `DealsTab` asks "does this flat hold a binder?" in an effect on EVERY viewing modal open,
// and the deal screen is opened constantly. The answer is almost always none, it changes
// rarely, and the card stack means one property can be asked for several times in a row.
//
// ⚠️ ONLY the property key is cached, and the reason is what each caller does with the answer.
// The by-property read feeds a READ-ONLY overlay. The by-MEETING read feeds the editor — it is
// re-fetched immediately after every write to show what the gateway actually stored (which is
// not always what was sent: the money rules live in the database). Caching that would show an
// agent their own edit as they typed it rather than as it was recorded, which is the one place
// this data must never be approximate.
//
// ⚠️ WHAT IT CAN BE WRONG ABOUT, STATED RATHER THAN GLOSSED: a binder recorded by a DIFFERENT
// agent on this property in the last `BINDER_TTL_MS`. This viewer's own writes clear the entry,
// so it can never be stale about anything they did. 60 seconds is chosen to be shorter than
// the time it takes to notice and reopen a screen — not to be clever.
const _binderByProp = new Map();      // propertyId → { at, promise }
const BINDER_TTL_MS = 60 * 1000;
// Every write path calls this. A cache with no invalidation is how a display starts lying.
function forgetBinderCache() { _binderByProp.clear(); }

export async function listBinders({ meetingId, propertyId, id, open } = {}) {
    const q = meetingId ? `meeting_id=${meetingId}`
        : propertyId ? `property_id=${propertyId}`
            : id ? `id=${id}` : open ? 'open=1' : '';
    if (!q) throw new Error('listBinders needs meetingId, propertyId, id or open');
    if (!propertyId || meetingId || id || open) {
        return coreApi(`binders?${q}`); // { binders[] } — each carries shares[] + computed remaining
    }
    const hit = _binderByProp.get(propertyId);
    if (hit && Date.now() - hit.at < BINDER_TTL_MS) return hit.promise;
    // The PROMISE is cached, not the result, so N modals opening at once share one round-trip
    // instead of racing. A rejection evicts itself: an offline blip must not be remembered as
    // "this flat has no binder" for the next minute.
    const promise = coreApi(`binders?${q}`);
    _binderByProp.set(propertyId, { at: Date.now(), promise });
    promise.catch(() => { if (_binderByProp.get(propertyId)?.promise === promise) _binderByProp.delete(propertyId); });
    return promise;
}

export async function recordBinder(body) {
    const r = await coreApi('binder', { method: 'POST', body }); // { ok, id }
    forgetBinderCache();
    return r;
}

// The moment money stops being something an agent is typing and becomes a fact the
// office relies on. Idempotent server-side. After this, changing the amount costs a reason.
export async function confirmBinder(id) {
    return coreApi(`binder/${id}/confirm`, { method: 'POST', body: {} });
}

// `reason` is REQUIRED by the gateway when ops touch amount/currency on a CONFIRMED
// binder — a 400 comes back carrying the gateway's own sentence, which is what the
// caller should show. Do not translate it into a generic failure: a rule the agent
// cannot read is a rule they will fight.
export async function amendBinder(id, ops, reason) {
    const r = await coreApi(`binder/${id}/amend`, { method: 'POST', body: { ops, reason: reason || null } });
    forgetBinderCache();
    return r;
}

export async function setBinderStatus(id, status, { reason, settledOn } = {}) {
    const r = await coreApi(`binder/${id}/status`, {
        method: 'POST', body: { status, reason: reason || null, settled_on: settledOn || null },
    });
    // A settle or a forfeit is exactly the transition the read-only overlay must not miss:
    // it is the difference between "this flat holds money" and "it did".
    forgetBinderCache();
    return r;
}

export async function getBinderHistory(id) {
    return coreApi(`binder/${id}/history`); // { history[] } — append-only, UPDATE/DELETE refused by trigger
}

// Statuses are ROWS (0443), same reason as the meeting vocabulary: adding one is an
// INSERT. Cached per session like getMeetingVocab.
let _binderVocab = null;
// 🚦 MOVE THE BINDER THROUGH ITS TWO STATE MACHINES (1100/1105). Every rule is in the gateway,
// so this sends the move and reports what the gateway said — it must NEVER pre-judge a
// transition locally. A panel that decides for itself which moves are legal is a second
// definition of the rules, and the two drift the first time the machine changes.
// The server answers with the updated row, so the caller adopts rather than refetches.
export async function advanceBinder(id, { agreementState, moneyState, refundReason, reason } = {}) {
    const r = await coreApi(`binder/${id}/advance`, {
        method: 'POST',
        body: {
            agreement_state: agreementState || null,
            money_state: moneyState || null,
            refund_reason: refundReason || null,
            reason: reason || null,
        },
    });
    // Same reason as setBinderStatus: this is the difference between "this flat holds money"
    // and "it did", and the read-only overlay must not miss it.
    forgetBinderCache();
    return r;
}

export async function getBinderVocab() {
    if (_binderVocab) return _binderVocab;
    // { statuses[], holders[], agreement_states[], money_states[], refund_reasons[] }
    _binderVocab = await coreApi('binder-vocab');
    return _binderVocab;
}

// ── Contracts (core/CONTRACT_API_CONTRACT.md, "Authed (Desk)") ─────────────────────────────
// A contract is the meeting's terms rendered ONCE into an immutable version, opened by each
// party through their own bearer link (`link.url` — present only for a viewer with can_act)
// and signed on the public page. Nothing here caches: a contract's status is DERIVED server-
// side from links and signatures, and a stale copy would say "sent" over a signed document.
// The type chooser (owner 2026-09-10: press "create contract", then choose a type). Latest
// active version per code; each row is the `template` shape of contract-draft.
// ── THE AGENCY'S OWN FORMS (owner 2026-09-11) ─────────────────────────────────────────────
// "canonical contracts that agency provide, no one edits" — the blank template, rendered with
// nothing filled in, to be read and agreed BEFORE a deal is typed into it.
// 🔑 IT NEEDS NO TOKEN, and that is the point rather than an omission: a form carries no person,
// no property and no figure, so there is nothing in it to protect. The address is therefore
// STABLE and PERMANENT — the same link for every client, forever, which is what makes it
// quotable in a message and what a signing link can never be.
export function contractFormUrl(code, lang) {
    const l = Array.isArray(lang) ? lang.filter(Boolean).join(',') : (lang || '')
    return `${SNTV_API}/form/${encodeURIComponent(code)}${l ? `?lang=${encodeURIComponent(l)}` : ''}`
}
export async function listContractTemplates() {
    return coreApi('contract-templates'); // { ok, templates[] }
}
export async function getContractDraft(meetingId, templateCode = 'rent_residential_v1', { from = null, propertyId = null, clientId = null, feeModel = null } = {}) {
    // { ok, can_act, template, fields[], parties[], existing[], related, related_candidates[] }
    // `from` (1056): the rental contract an agency service agreement should be drawn from.
    // `feeModel` (owner 2026-10-10): 'owner' — only the owner pays; 'both' — owner and tenant each pay their part
    const suffix = `?template=${encodeURIComponent(templateCode)}${from ? `&from=${encodeURIComponent(from)}` : ''}${clientId ? `&client=${encodeURIComponent(clientId)}` : ''}${feeModel ? `&fee_model=${encodeURIComponent(feeModel)}` : ''}`
    return propertyId && !meetingId
      ? coreApi(`property/${encodeURIComponent(propertyId)}/contract-draft${suffix}`)
      : coreApi(`meeting/${meetingId}/contract-draft${suffix}`);
}
export async function savePaymentAccount(meetingId, account, propertyId = null) {
    const base = propertyId && !meetingId ? `property/${propertyId}` : `meeting/${meetingId}`
    return coreApi(`${base}/payment-account`, { method: 'POST', body: account });
}
export async function createContract(payload) {
    // { meeting_id, template_code, langs, values, parties } → { ok, contract: ContractDetail }
    return coreApi('contract', { method: 'POST', body: payload });
}
// 🔁 A NEW EDITION OF A SIGNED LEASE (owner 2026-10-08, core 3097/3098). The seed is the signed edition's own
// agreed terms with the move-in reset and the carried facts stamped; nothing is written until the agent creates
// it (createContract with `replaces_contract_id`).
//   → { ok, replaces:{id,revision_no,completed_at,sha256}, template:{code,version}, meeting_id, property_id, langs, sections, values, prev_values }
export async function getRevisionSeed(contractId) {
    return coreApi(`contract/${encodeURIComponent(contractId)}/revision-seed`);
}
// ⚖️ the owner's (admin's) release of ONE revision for signing while the replacement clause is under legal review
export async function releaseRevision(contractId, note = '') {
    return coreApi(`contract/${encodeURIComponent(contractId)}/revision-release`, { method: 'POST', body: { note } });
}
// THE FORM DRAFT on the server (core 1936, owner 2026-09-22): the composer's answers, a moment after
// each change, so an unfinished contract survives any device and shows on the meeting card.
export async function putContractFormDraft(payload) {
    // { template_code, template_version, meeting_id?, property_id?, client_party_id?, state } → { ok, id, updated_at }
    return coreApi('contract-form-draft', { method: 'PUT', body: payload });
}
// every unfinished form the viewer may see, for the Contracts tab (owner 2026-09-22)
export async function listContractFormDrafts() {
    return coreApi('contract-form-drafts'); // { ok, drafts: [{ id, template_code, template_kind, title, meeting_id, property_id, client_party_id, address, created_by_name, updated_at, mine }] }
}
export async function deleteContractFormDraft(id) {
    return coreApi(`contract-form-draft/${encodeURIComponent(id)}`, { method: 'DELETE' }); // { ok, deleted }
}
export async function listContracts(meetingId) {
    return coreApi(`contracts?meeting_id=${encodeURIComponent(meetingId)}`); // { ok, contracts[] }
}
// ── The CONTRACTS TAB's list (owner 2026-09-11, ხელშეკრულებები) ────────────────────────────
// The SAME route with no `meeting_id` is the whole book instead of one meeting's shelf:
// `{ ok, contracts:[ContractDetail-lite], has_more, scanned, q }`, newest first, and
// `{ contracts: [], no_match: true }` when a search matched nothing.
// ⚠️ `listContracts(meetingId)` above is deliberately left alone — the meeting panel asks a
// narrower question and must keep its own call, so widening one never changes the other.
// ⚠️ An EMPTY param is omitted, never sent blank: `status=` is a value the server would have
// to interpret, and "all" is the absence of the filter, not a filter whose value is "".
// ⚠️ EXACT SEARCH OVERRIDES THE SIDEBAR (owner 2026-09-11): "if I paste a hash, don't apply
// Signed + Rental + This month to it." The server enforces that — it ignores every browse
// filter while `q` is set — and this wrapper does NOT send them either, so the request itself
// says which question is being asked. Two layers by design: the server's copy is the rule, this
// one is what keeps the URL honest.
export async function listAllContracts({ q, status, kind, lang, since, until, mine, agent, counts, limit, offset } = {}) {
    const p = new URLSearchParams();
    const qq = typeof q === 'string' ? q.trim() : '';
    if (qq) p.set('q', qq);
    if (!qq) {
        if (status) p.set('status', status);
        if (kind) p.set('kind', Array.isArray(kind) ? kind.join(',') : kind);
        if (lang) p.set('lang', lang);
        if (since) p.set('since', since);
        if (until) p.set('until', until);
        if (mine) p.set('mine', '1');
        else if (agent) p.set('agent', agent);
    }
    if (counts) p.set('counts', '1');
    if (limit) p.set('limit', String(limit));
    if (offset) p.set('offset', String(offset));
    const qs = p.toString();
    return coreApi(`contracts${qs ? `?${qs}` : ''}`);
}
// A READ-ONLY link for a party (or a colleague) to READ the draft — never to sign it. The
// signing link is `mintContractLinks` above and is a different credential; do not conflate
// them. → { ok, token, url, contract }
export async function mintContractReviewLink(id, { note, days } = {}) {
    return coreApi(`contract/${id}/review-link`, { method: 'POST',
        body: { note: note || null, days: days || null } });
}
// Revoking is per TOKEN, not per contract — one recipient's link dies, the rest live.
export async function revokeContractReviewLink(id, token, reason) {
    return coreApi(`contract/${id}/review-link/${encodeURIComponent(token)}/revoke`,
        { method: 'POST', body: { reason: reason || null } }); // { ok, contract }
}
export async function getContract(id) {
    return coreApi(`contract/${id}`); // { ok, contract }
}
// One link per party for the CURRENT version; idempotent (a live link is returned, not replaced).
export async function mintContractLinks(id) {
    return coreApi(`contract/${id}/links`, { method: 'POST', body: {} }); // { ok, contract }
}
// How the agent sent it — a declared channel, recorded after the tap, never a delivery receipt.
export async function markContractDelivered(id, contractPartyId, via) {
    return coreApi(`contract/${id}/delivered`, { method: 'POST',
        body: { contract_party_id: contractPartyId, via } }); // via: whatsapp | email | copied
}
// Version N+1 from the merged values; every link of N is revoked and re-minted. "Editable
// until signed" (owner 2026-09-10): a plain save while nobody has signed; once ANY party
// signed the current version the server answers 409 `signed_partially` until `confirm` is
// true (their signature stays on the old version); fully `signed` → 409 `signed`, no override.
// `body` is `{ values, sections }` since the builder (2026-09-10): `sections` is the ASSEMBLY
// (API §Sections — off keys, rewritten clauses, custom sections); absent → the server keeps the
// current assembly. A bare values object is still accepted for the pre-builder caller shape.
// 🏠 What the system says a contract's address is in English/Russian (owner 2026-09-29): { auto: { address: { en, ru } }, canonical }.
// Read-only; the same function the save and the render use.
export async function contractAddressTranslation(body) {
    return coreApi('contract/address-translation', { method: 'POST', body })
}
export async function rerenderContract(id, body, confirm = false) {
    const b = body && typeof body === 'object' && ('values' in body || 'sections' in body || 'parties' in body || 'overrides' in body) ? body : { values: body };
    // `parties:[{role,names}]` — a name's spelling in another script may be added or corrected
    // on a save (API §"Three languages"); sent only when the caller carries it.
    const payload = { values: b.values, sections: b.sections, confirm };
    if (Array.isArray(b.parties)) payload.parties = b.parties;
    // `langs` (API §"Design build", 2026-09-11): a NEW language set — "+ РУС" on an existing
    // unsigned contract adds a language on save. Sent only when the caller carries it; the
    // server keeps the current set otherwise.
    if (Array.isArray(b.langs)) payload.langs = b.langs;
    // The local overrides (migration 1123): the reason a value diverges from the source it was
    // inherited from. Sent only when the caller carries the key, because an ABSENT `overrides`
    // means "leave the recorded ones alone" while an empty object would mean the same thing by
    // accident — and one day it will not.
    if (b.overrides && typeof b.overrides === 'object') payload.overrides = b.overrides;
    // 🏠 the list carries the whole set of residents, so a removed one is removed (never by omission)
    if (b.residents_listed === true) payload.residents_listed = true;
    return coreApi(`contract/${id}/rerender`, { method: 'POST', body: payload }); // { ok, contract }
}
// 🔄 WHAT THE SOURCE SAYS TODAY, and how it stands against this document (migration 1116/1123).
// Read-only: it never writes a version, so the agent sees what a refresh WOULD change before
// anything changes. The answer separates a recorded override (with its reason and the source
// value it was decided against) from plain drift, and offers `three_way` where the source has
// moved since the decision — the owner's old-source / new-source / local reconciliation.
// ⚠️ Refused (409) on a signed or void contract, by design: an executed document is never rebased.
export async function getContractSourceRefresh(id) {
    return coreApi(`contract/${id}/source-refresh`); // { ok, source, snapshot_version, values, differs, overridden, drifted }
}
export async function voidContract(id, reason) {
    return coreApi(`contract/${id}/void`, { method: 'POST', body: { reason } }); // { ok, contract }
}
// The closed contract as a PDF (migration 1046 — owner: "both parties get this contract as a
// PDF in their emails", and the agent can hold the same file). `GET /api/core/contract/:id/pdf`
// is an AUTHED route answering application/pdf, so it cannot be a plain href: the viewer token
// goes in the header exactly as coreApi sends it, and the caller turns the Blob into an object
// URL to open or download. Errors keep coreApi's shape (`message` = the server's error code,
// `code`, `status`, `body`) so the page can name `pdf_engine_missing` (503).
export async function fetchContractPdfBlob(id) {
    const token = await viewerToken()
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/pdf`, {
        headers: { ...(token ? { 'x-viewer-token': token } : {}) },
    })
    if (!r.ok) {
        const data = await r.json().catch(() => ({}))
        const err = new Error(data.error || `contract/${id}/pdf ${r.status}`)
        err.code = data.code || null
        err.status = r.status
        err.body = data
        throw err
    }
    return r.blob()
}
// The HANDOVER act's own PDF (reviews 2026-10-09): only for an act signed AFTER the lease (a late in-person act or a
// remote lease's); one signed with the lease is inside the contract PDF. 404 `no_handover_act_after_signing` otherwise.
export async function fetchHandoverActPdfBlob(id) {
    const token = await viewerToken()
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/handover-pdf`, {
        headers: { ...(token ? { 'x-viewer-token': token } : {}) },
    })
    if (!r.ok) {
        const data = await r.json().catch(() => ({}))
        const err = new Error(data.error || `contract/${id}/handover-pdf ${r.status}`)
        err.code = data.code || null
        err.status = r.status
        err.body = data
        throw err
    }
    return r.blob()
}
// The RETURN act's own PDF (2026-09-21): the final inspection with both parties' signatures.
// Same auth and error shape as fetchContractPdfBlob; 404 `no_return_act` before one exists.
export async function fetchReturnActPdfBlob(id) {
    const token = await viewerToken()
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/return-pdf`, {
        headers: { ...(token ? { 'x-viewer-token': token } : {}) },
    })
    if (!r.ok) {
        const data = await r.json().catch(() => ({}))
        const err = new Error(data.error || `contract/${id}/return-pdf ${r.status}`)
        err.code = data.code || null
        err.status = r.status
        err.body = data
        throw err
    }
    return r.blob()
}
// Email the signed PDF to every party that has an address. Answers
// `{ ok, results:[{role, sent, to?, reason?}], contract }`; 503 `mail_not_configured` when the
// box has no mailer (the page then tells the agent to download and send by hand), 409
// `not_signed` before the last signature.
export async function sendContractPdf(id) {
    return coreApi(`contract/${id}/send-pdf`, { method: 'POST', body: {} })
}

// ── The CONDITION-PHOTO ANNEX (design §9.4 / §8.1, owner 2026-09-10: "take images of current
// state of apartment and append to the contract"). `ContractDetail.annex` is `null` or
// `{ version, sha256, rendered_at, photos:[{id, sha256, caption, room, taken_at, width, height,
// bytes}], signed_roles:[…], can_change }` — a SEPARATE signed artifact with its own frozen
// version. Every mutation answers `{ ok, contract }` (the whole detail, adopt it), and the
// three refusals a caller must branch on ride `err.message` / `err.status` / `err.body`:
//   409 `annex_signed` `{signed_roles}` — a signature froze it; retry with `confirm:true`
//       discards EVERY signature (contract AND annex — they are signed in one flow);
//   409 `signed`                        — the contract is fully signed: nothing changes;
//   415 unsupported type · 503 `pii_key_missing` / `storage_not_configured`.
// All four are raw fetches (binary in or out), not coreApi, so each sends x-viewer-token
// ITSELF and reproduces coreApi's error shape by hand (`annexError`).
async function annexError(r, fallback) {
    const data = await r.json().catch(() => ({}))
    const err = new Error(data.error || fallback)
    err.code = data.code || null
    err.status = r.status
    err.body = data
    return err
}
async function annexHeaders(extra = {}) {
    const token = await viewerToken()
    return { ...(token ? { 'x-viewer-token': token } : {}), ...extra }
}
// ── The extension download (owner 2026-09-29, server lib/extension_release.js) ─────────────
// build.bat publishes each new build; the Desk offers it here so nobody has to send zips.
// `{ version: null }` means nothing has been published yet — callers render nothing.
export async function getExtensionLatest() {
    return coreApi('extension/latest')
}
// The zip as a Blob, authed like the annex files above (a raw fetch, so it sends the viewer
// token itself). The caller saves it; see ExtensionUpdate.jsx.
export async function fetchExtensionZip() {
    const r = await fetch(`${SNTV_API}/api/core/extension/download`, { headers: await annexHeaders() })
    if (!r.ok) throw await annexError(r, `extension/download ${r.status}`)
    return r.blob()
}
// The photo's bytes — `size='d'` the 1600 px derivative (thumbnails, the grid), `'o'` the
// ORIGINAL. Authed like the PDF: the caller turns the Blob into an object URL and REVOKES it.
export async function fetchContractAnnexPhotoBlob(id, pid, size = 'd') {
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/annex/photo/${pid}?size=${size === 'o' ? 'o' : 'd'}`, {
        headers: await annexHeaders(),
    })
    if (!r.ok) throw await annexError(r, `contract/${id}/annex/photo ${r.status}`)
    return r.blob()
}
// Upload ONE photo: the raw file as the body, AS-IS — never downscaled, because the server
// stores and hashes the ORIGINAL (§9.4: "originals preserved, not only rendered/compressed
// copies"). Caption/room ride as encodeURIComponent'd headers (a custom header is ASCII-only
// and preflighted — see uploadListingImage's nginx note); `takenAt` is what
// src/lib/exifRead.js read from the file, when it read anything.
export async function uploadContractAnnexPhoto(id, file, { caption = null, room = null, takenAt = null, geo = null, confirm = false } = {}) {
    // caption/room ride the QUERY STRING, not custom headers: a custom header makes the request
    // preflighted and needs the nginx CORS allow-list (see uploadListingImage's note) —
    // x-photo-taken-at and x-photo-geo are already allowed there, these two are not.
    const q = new URLSearchParams()
    if (confirm) q.set('confirm', '1')
    if (caption) q.set('caption', String(caption))
    if (room) q.set('room', String(room))
    const qs = q.toString()
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/annex/photo${qs ? '?' + qs : ''}`, {
        method: 'POST',
        headers: await annexHeaders({
            'Content-Type': file.type || 'application/octet-stream',
            ...(takenAt ? { 'x-photo-taken-at': takenAt } : {}),
            // ⚠️ The two halves of this fallback were CROSSED until 2026-09-12: we sent the
            // timestamp and the server read only the coordinates. Both travel now. Our read of
            // the original still loses to the server's read of the bytes it stored.
            ...(geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng) ? { 'x-photo-geo': `${geo.lat},${geo.lng}` } : {}),
        }),
        body: file,
    })
    if (!r.ok) {
        // ⚠️ Same 413 as uploadListingImage: nginx's client_max_body_size answers with an HTML
        // page, so there is no `error` to read — name the file and its size instead, and give
        // the caller a stable code to branch on.
        if (r.status === 413) {
            const mb = (file.size / 1048576).toFixed(1)
            const err = new Error('too_large')
            err.code = 'too_large'; err.status = 413; err.body = { name: file.name, mb }
            throw err
        }
        throw await annexError(r, `contract/${id}/annex/photo ${r.status}`)
    }
    return r.json() // { ok, contract }
}
export async function removeContractAnnexPhoto(id, pid, { confirm = false } = {}) {
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/annex/photo/${pid}${confirm ? '?confirm=1' : ''}`, {
        method: 'DELETE',
        headers: await annexHeaders(),
    })
    if (!r.ok) throw await annexError(r, `contract/${id}/annex/photo ${r.status}`)
    return r.json() // { ok, contract }
}
// Caption and/or room. Only the keys the caller passes are sent, so a caption edit never
// blanks the room and vice versa.
export async function uploadContractReferenceMedia(id, file) {
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/reference-media`, {
        method: 'POST', headers: await annexHeaders({ 'Content-Type': file.type }), body: file,
    })
    if (!r.ok) throw await annexError(r, `contract/${id}/reference-media ${r.status}`)
    return r.json()
}

export async function recordRemoteInitialPayment(id, body) { return coreApi(`contract/${id}/initial-payment`, { method: 'POST', body }) }
export async function recordTenantAgencyFeePayment(id, body) { return coreApi(`contract/${id}/tenant-agency-fee-payment`, { method: 'POST', body }) }
export async function recordRemoteTransferInitiation(id, body) { return coreApi(`contract/${id}/initial-transfer`, { method: 'POST', body }) }
export async function extendRemoteInitialPaymentDeadline(id, body) { return coreApi(`contract/${id}/initial-payment-deadline-extension`, { method: 'POST', body }) }
export async function recordRemoteKeyRequest(id, body) { return coreApi(`contract/${id}/key-collection-request`, { method: 'POST', body }) }
export async function recordRemoteHandoverTime(id, body) { return coreApi(`contract/${id}/annex/handover-time`, { method: 'POST', body }) }
export async function recordRemoteDefect(id, body) { return coreApi(`contract/${id}/post-handover-defect`, { method: 'POST', body }) }

export async function patchContractAnnexPhoto(id, pid, { caption, room, confirm = false } = {}) {
    const body = {}
    if (caption !== undefined) body.caption = caption
    if (room !== undefined) body.room = room
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/annex/photo/${pid}${confirm ? '?confirm=1' : ''}`, {
        method: 'PATCH',
        headers: await annexHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
    })
    if (!r.ok) throw await annexError(r, `contract/${id}/annex/photo ${r.status}`)
    return r.json() // { ok, contract }
}

// THE HANDOVER RECORD (migration 1086): one SECTION of the checklist at a time — condition,
// inventory, keys, meter or defect. The caller sends the WHOLE list for that section, so a row
// left out is stamped removed server-side; that is why this is a PUT and not a PATCH.
// Frozen-annex handling is the photographs' (409 annex_signed → retry with confirm:true, which
// discards every signature on the contract AND the annex).
// `occurredAt` is the ACT's own date — when the keys changed hands, or when the tenant vacated.
// It rides the item save because that is the call the act already makes; the server carries it
// forward to every later version, so it is answered once and not per photograph.
export async function putContractAnnexItems(id, section, rows, { confirm = false, kind = 'handover', occurredAt = null } = {}) {
    const r = await fetch(`${SNTV_API}/api/core/contract/${id}/annex/items${confirm ? '?confirm=1' : ''}`, {
        method: 'PUT',
        headers: await annexHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ section, rows, kind, ...(occurredAt ? { occurred_at: occurredAt } : {}) }),
    })
    if (!r.ok) throw await annexError(r, `contract/${id}/annex/items ${r.status}`)
    return r.json() // { ok, contract }
}

// The three-tier follow-up nudges, computed live server-side (no nudge table —
// resolving the meeting IS dismissing the nudge):
//   critical — appointment passed, record still says scheduled/rescheduled
//   high     — pending whose next_action_date passed, or pending gone stale
//   medium   — status missing its reason (recent window only)
// Own meetings for agents, everything for management — scoped by the verified
// viewer server-side, like the meetings feed.
export async function getMeetingNudges() {
    const qs = _viewerAgentId ? `?viewer_agent_id=${encodeURIComponent(_viewerAgentId)}` : '';
    return coreApi(`meetings/nudges${qs}`); // { critical[], high[], medium[], config }
}

// Deposit (ბე) figures REWRITTEN after they were first recorded (owner ruling B,
// 2026-08-06). Not a nudge and deliberately not merged into that payload: a nudge is
// work the viewer can finish, and this is a fact they may need to challenge — nothing
// here is "resolved" by tapping it. Same viewer scoping as the nudges route.
//
// ⚠️ A first save is NOT in here; only changes. The server draws that line (before.amount
// null → silent) so every client agrees on what counts as tampering.
export async function getBinderChanges({ days = 30 } = {}) {
    const p = new URLSearchParams({ days: String(days) });
    if (_viewerAgentId) p.set('viewer_agent_id', _viewerAgentId);
    return coreApi(`meetings/binder-changes?${p}`); // { window_days, changes[] }
}

// Attach a client to a meeting created without one, or swap a wrong one (owner
// ruling 2026-08-01). The server re-snapshots the client representative exactly
// like create, files an access request when the editor doesn't represent the new
// client's requirement, and events the change (meeting.client_attached /
// meeting.client_changed) — an edit here is logged history, never a silent
// rewrite. requirementId alone is enough: the party is derived server-side.
export async function setMeetingClient(id, { clientPartyId, requirementId } = {}) {
    return coreApi(`meeting/${id}`, {
        method: 'PATCH',
        body: { client_party_id: clientPartyId || null, requirement_id: requirementId || null },
    }); // { meeting, access_requests[] }
}

// ADMIN ONLY, and a SOFT delete (0199): the row survives so
// access_request.meeting_id and the event log keep their subject — a hard delete
// would orphan the reveal audit ("released for meeting X" → subject unknown).
// Gone from every read path, so to a user it is deleted. The server re-checks the
// role; this is not gated in the UI alone.
export async function deleteMeeting(id) {
    return coreApi(`meeting/${id}`, { method: 'DELETE' });
}

// ── PropertyShare — the client-facing offer link (0196/0198; MEETINGS_MODEL §5) ─
// ⚠️ offered_price is FROZEN on the share at mint. It is NOT the listing's live
// price, and the page must never re-read one: the client calls two days later
// saying "it says $205,000 now" and nobody can tell who changed it.
// ⚠️ address_precision is decided SERVER-SIDE (exact only if the sender actually
// represents the property) — a client may ask for less, never more. Do not add a
// UI that claims otherwise.
// ⚠️ `senderMemo` is PRIVATE (0379) and `message` is not. They are one argument apart here
// and one field apart in the modal, and they are opposites: message is written FOR the client
// and rendered on their page; senderMemo is the agent's note ABOUT them and never leaves the
// agency. The server keeps it out of the client payload by whitelist, not by filtering — so
// nothing here may ever pass a memo where a message is expected.
// `recipientName`/`recipientPhone` are the B397 picker's "somebody new" path and are
// sent ONLY when no party is settled — the server mints via resolve_party, which
// inserts unconditionally and files a merge candidate rather than guessing. A picked
// candidate (recipientPartyId) always wins; never send both.
export async function createPropertyShare({ propertyId, offeredPrice, offeredCurrency, offeredMaxOccupants, message, senderMemo, recipientPartyId, recipientName, recipientPhone, meetingId, addressPrecision } = {}) {
    return coreApi('property-share', {
        method: 'POST',
        body: {
            property_id: propertyId,
            offered_price: offeredPrice ?? null,
            offered_currency: offeredCurrency || null,
            // Frozen on the share like the price (0257), NOT a live read of
            // listing_terms. `?? null` not `|| null`: 0 is already normalised to null
            // server-side, but a falsy-coalesce here would be a silent second opinion.
            offered_max_occupants: offeredMaxOccupants ?? null,
            message: message || null,
            sender_memo: senderMemo || null,
            recipient_party_id: recipientPartyId || null,
            recipient_name: recipientName || null,
            recipient_phone: recipientPhone || null,
            meeting_id: meetingId || null,
            address_precision: addressPrecision || null,
            viewer_agent_id: _viewerAgentId || null,
        },
    }); // { id, token, address_precision }
}

// Engagement counters arrive DERIVED from the append-only event rows — opens,
// last_opened_at, approximate devices, clicks. Never stored counters: the
// sequence is the intelligence.
// `propertyId` takes one id OR an array (the Desk's card footer asks for a whole page of
// cards at once). Chunked at 80 like listCooperations/getProposalMatchCounts — an id list
// interpolated into a query string is exactly the shape that 414s past nginx's 8KB request
// line and comes back as a silent empty answer (memory: desk-notifications-url-cap).
// Rows carry property_id, so the caller groups them itself.
export async function getPropertyShares({ propertyId } = {}) {
    const ids = Array.isArray(propertyId) ? [...new Set(propertyId.filter(Boolean))]
        : (propertyId ? [propertyId] : []);
    if (ids.length > 1) {
        const chunks = [];
        for (let i = 0; i < ids.length; i += 80) chunks.push(ids.slice(i, i + 80));
        const results = await Promise.all(chunks.map(part =>
            coreApi(`property-shares?property_id=${part.map(encodeURIComponent).join(',')}`)));
        return results.flatMap(r => r.shares || []);
    }
    const q = ids.length ? `?property_id=${encodeURIComponent(ids[0])}` : '';
    return (await coreApi(`property-shares${q}`)).shares || [];
}

// Portal logins kept PER AGENT (core 3781) - the ones a browser's automatic sign-in uses when the agent holds no work
// number with a login (the company browsers signed in as argo: the top-up MacBook / Chromebook). Managers only; the list
// never carries a password.
export async function listAgentPortalLogins() {
    return (await coreApi('portal-logins')).logins || []
}
export async function setAgentPortalLogin({ agentEmail, platform, login, password }) {
    return coreApi('portal-logins', { method: 'POST', body: { agent_email: agentEmail, platform, login, password } })
}

// The card's reshare-pool marker (owner 2026-10-27): per property, its pools / open push rows / our reposts in 30 days.
// POST, so a page of ids never meets the URL cap. Returns { [property_id]: row }; a property in no pool is absent.
export async function getReshareStatus(propertyIds) {
    const ids = [...new Set((propertyIds || []).filter(Boolean))];
    if (!ids.length) return {};
    const chunks = [];
    for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
    const results = await Promise.all(chunks.map(part => coreApi('reshare-pool-status', { method: 'POST', body: { property_ids: part } })));
    const out = {};
    for (const r of results) for (const row of r.rows || []) out[row.property_id] = row;
    return out;
}

// What my clients did with the links I sent — feeds the notification bell.
// One row per (share, kind): a client opening a link nine times is ONE "they
// looked", not nine notifications. `meeting_requested` is the actionable kind.
// What the listing already says, so the create-link modal can inherit it instead
// of making the agent retype it. Returns { intent, max_occupants } — SEED ONLY;
// what actually reaches the client is whatever the agent submits, frozen at mint.
export async function getPropertyShareSeed(propertyId) {
    return coreApi(`property-share/seed?property_id=${encodeURIComponent(propertyId)}`);
}

export async function getShareActivity() {
    return (await coreApi('share-activity')).activity || [];
}

export async function revokePropertyShare(id) {
    return coreApi(`property-share/${id}/revoke`, { method: 'POST' });
}

// ── Per-row notification state (0447) ────────────────────────────────────────
// Replaces the single localStorage "last opened the panel" clock, under which the
// badge cleared by GLANCING. Two Sets, held client-side and applied to the feed.
//
// ⚠️ The server refuses `dismissed` on a class-A row with a 409 — a row owed to a
// person is resolved, never dismissed. That refusal is the contract, so do not
// "handle" it by retrying as `read`: if a 409 ever arrives, the UI offered a
// dismiss control on a row that must not have one, and that is the bug to fix.
export async function getNotificationRead() {
    const r = await coreApi('notification-read');
    return { read: new Set(r.read || []), dismissed: new Set(r.dismissed || []) };
}

export async function setNotificationRead(rowKey, state = 'read') {
    return coreApi('notification-read', { method: 'POST', body: { row_key: rowKey, state } });
}

// Review-queue pointers (management only; [] for an agent). A backlog is a STATE, so
// this reports size AND rate — size alone says "big", which nobody acts on.
export async function getReviewQueues() {
    return (await coreApi('review-queues')).queues || [];
}

export async function undoNotificationRead(rowKey) {
    return coreApi('notification-read/undo', { method: 'POST', body: { row_key: rowKey } });
}



// The public URL an agent sends. Points at the API HOST, not the Desk origin.
//
// ⚠️ IT IS DELIBERATELY NOT /share.html?t=… ANY MORE (2026-07-29). That URL still
// works and is still what actually renders — /s/:token bounces straight to it —
// but it can NEVER produce a WhatsApp/Facebook link preview: share.html is a
// static Vite entry whose content is fetched by React, and chat-app crawlers run
// no JavaScript, so every shared link showed up as a bare grey URL. /s/:token is
// server-rendered by lib/core_share.js purely so the OG tags exist in the raw
// HTML. Sending the Desk URL again silently loses the preview.
export function propertyShareUrl(token) {
    return `${SNTV_API}/s/${token}`;
}

// ── A sent link, BACK to the card it points at ─────────────────────────────
// The client replies "I like this one" and pastes the link — this turns it into
// { property_id, listing_id, state, … } so the agent lands on the card. One
// lookup, three call sites planned (paste in search, the bell, the share page's
// logo); they must not each grow their own.
//
// ⚠️ POST, WITH THE LINK IN THE BODY. The token is a bearer credential for the
// client page (0280 revokes SELECT on property_share from client roles for
// exactly this reason), so it must not travel in a URL where nginx logs it.
// Do not "simplify" this to a GET.
//
// Accepts whatever the agent pasted — the full /s/ URL, the older
// share.html?t=… form, or the bare 32-hex token. The server owns that parsing so
// every caller agrees on what counts as a link.
//
// Returns { found:false } for a token that matches nothing — NOT an error, and
// the caller should say "no link matches that" rather than "something failed".
// Resolves regardless of state: revoked/expired/off-market links still answer,
// with `state` naming which, because those are the ones agents chase hardest.
// Sender-private fields (recipient, memo, quoted price, engagement) are OMITTED
// entirely when the viewer did not send it — check `is_sender`, and never read a
// missing key as "empty".
export async function resolvePropertyShareLink(link) {
    return coreApi('property-share/resolve', {
        method: 'POST',
        body: { link }, // coreApi stringifies — passing a string here double-encodes it
    });
}

// Does this text look like a share link at all? Cheap local check so a search box
// can decide whether to spend a round-trip — the SERVER still owns the real
// parse (above), and this must stay a superset-free mirror of that regex.
export function looksLikeShareLink(text) {
    const s = String(text == null ? '' : text).trim();
    return /^[a-f0-9]{32}$/i.test(s) || /\/s\/[a-f0-9]{32}\b/i.test(s) || /[?&]t=[a-f0-9]{32}\b/i.test(s);
}

// ── Negotiation — the Offer/Deal timeline (0200/0201; MEETINGS_MODEL §6, §11) ─
// ⚠️ APPEND-ONLY. There is no update and no delete: a negotiation is a
// conversation, and a wrong number is corrected by ANOTHER event, never a
// rewrite. The UI must never present a field that overwrites a price — see the
// header of NegotiationModal.jsx.
//
// Identity is the RELATIONSHIP (property, client), not the meeting — a meeting is
// one event in it. Opening is idempotent: the same pair returns the existing live
// thread (`existing: true`) rather than starting a second conversation.
// requirementId alone is enough for the client side; the server derives the party
// from it, exactly as createMeeting does.
export async function openNegotiation({ propertyId, clientPartyId, requirementId, intent } = {}) {
    return coreApi('negotiation', {
        method: 'POST',
        body: {
            property_id: propertyId,
            client_party_id: clientPartyId || null,
            requirement_id: requirementId || null,
            intent: intent || null,
            viewer_agent_id: _viewerAgentId || null,
        },
    }); // { negotiation, existing }
}

// Rows arrive with the stage FOLDED from their events (never a stored column) plus
// both sides' latest positions, the gap and its history. `client.name` is null when
// the client belongs to another agent — `client.rep_name` is who to talk to instead.
export async function getNegotiations({ propertyId, clientPartyId, openOnly } = {}) {
    const p = new URLSearchParams();
    if (propertyId) p.set('property_id', propertyId);
    if (clientPartyId) p.set('client_party_id', clientPartyId);
    if (openOnly) p.set('open', '1');
    const qs = p.toString();
    return (await coreApi(`negotiations${qs ? '?' + qs : ''}`)).negotiations || [];
}

// One thread + every event + the fold + the cooperation floor. `below_floor` is
// ADVISORY and must never be rendered as a block — the agent decides.
export async function getNegotiation(id) {
    return coreApi(`negotiation/${id}`);
}

// ⚠️ meetingId is the provenance that makes "Created from: Meeting #…" possible
// (0201). Per-EVENT, because one negotiation spans several meetings; null is
// ordinary, not missing.
// ⚠️ actor_side is WHOSE position this is, not who typed it — an agent entering
// the owner's counter sends actor_side:'owner'. The server stamps recorded_by
// from the verified viewer; the client cannot claim it.
export async function appendNegotiationEvent(id, { kind, actorSide, amount, currency, message, meetingId } = {}) {
    return coreApi(`negotiation/${id}/event`, {
        method: 'POST',
        body: {
            kind,
            actor_side: actorSide || null,
            amount: amount ?? null,
            currency: currency || null,
            message: message || null,
            meeting_id: meetingId || null,
            viewer_agent_id: _viewerAgentId || null,
        },
    }); // { event }
}

// The Desk's meetings feed, PROJECTED server-side (Track 2 meetings-PII): replaces the
// client-side getViewings() select('*') that shipped every client's name/phone AND every
// agent's commission to any agent. Same {status,dateFrom,dateTo} filters; rows arrive with
// client PII / event timeline / per-agent commission only for the hosting agent (or mgmt).
// ⚠️ Returns BOTH shapes, merged (2026-07-24, MEETINGS_MODEL.md §1):
//   • legacy scheduler `viewings` — the 189 historical meetings, still the only
//     home of commission/binder/close, projected server-side for PII
//   • core `meeting` rows — everything created since meetings moved to core
// Adapted to ONE row shape here rather than in the feed, so MeetingsFeed keeps a
// single renderer and the six existing callers are untouched. Core rows are
// tagged `__core: true`; anything that WRITES (edit, close, commission) must
// branch on it — a core meeting has no scheduler row behind it.
//
// ⚠️ The backfill HAS since happened (2026-07-24): 178 legacy rows now also exist
// as core meetings, stamped `legacy_viewing_id`. The merge therefore dedupes —
// see the note inside. The two shapes still coexist because the legacy rows are
// the only home of commission/binder/close; if that money ever moves to core,
// delete the legacy half of the merge, not the adapter's callers.
export async function getMeetingsFeed({ status, dateFrom, dateTo } = {}) {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    const qs = p.toString();
    // Independent: a core outage must not blank the historical feed, and vice
    // versa — one empty half is a degraded list, two failed halves is a blank tab.
    const [legacy, core] = await Promise.allSettled([
        coreApi(`meetings-feed${qs ? '?' + qs : ''}`).then(d => d.viewings || []),
        getMeetings({ status, since: dateFrom }),
    ]);
    // ⚠️ BOTH halves failed = the feed did not load, which is NOT "no meetings" (owner 2026-10-18: "where are all
    // these meetings" — the follow-up box listed five while the list said "no meetings yet"). Said as an error so
    // the Desk can show it with a retry; one failed half stays a degraded list, as above.
    if (legacy.status === 'rejected' && core.status === 'rejected') {
        throw new Error(core.reason?.message || legacy.reason?.message || 'meetings feed failed');
    }
    const legacyRows = legacy.status === 'fulfilled' ? legacy.value : [];
    let coreRows = core.status === 'fulfilled' ? core.value.map(adaptCoreMeeting) : [];
    // dateTo was silently DROPPED for core rows until 2026-08-15 (getMeetings speaks only
    // status/since), so a date-bounded view was bounded for legacy rows and open-ended for
    // core ones. Bounded here on the appointment date, created_at as the timeless fallback.
    //
    // ⚠️ dateFrom AND dateTo BOUND DIFFERENT FIELDS, and a caller that wants one question
    // answered must know which (2026-09-07). `dateFrom` reaches the server as `since` and
    // filters created_at; `dateTo` is this client-side cut and filters the APPOINTMENT date.
    // That is right for the board — it shows what is happening on those days — and wrong for
    // anything scoring what an agent DID, because a meeting booked today for next week is
    // work done today. StatsTab passed both and silently scored 0 for every future booking.
    // If you are counting effort rather than filling a calendar, pass dateFrom only.
    if (dateTo) {
        // review 2026-09-23: compare TBILISI days. Slicing the UTC ISO put a 00:00–03:59
        // Tbilisi meeting on the previous day, so it survived a cut meant to exclude it.
        // A bare 'YYYY-MM-DD' dateTo is already a Tbilisi day; a full instant is converted.
        const s = String(dateTo);
        const cut = s.length > 10 ? (tbilisiParts(s).date || s.slice(0, 10)) : s;
        const dayOf = iso => (iso ? (tbilisiParts(iso).date || String(iso).slice(0, 10)) : '');
        coreRows = coreRows.filter(m => dayOf(m.scheduled_at || m.created_at) <= cut);
    }

    // ⚠️ DEDUPE THE BACKFILL, OR EVERY HISTORICAL MEETING APPEARS TWICE.
    // The 2026-07-24 backfill copied 178 of the 189 scheduler `viewings` into
    // core, stamping each with `legacy_viewing_id`. This function merges BOTH
    // lists, so from that moment every backfilled meeting was in the feed twice —
    // and not only visually: the merged array feeds the sidebar category counts,
    // the CSV export and the agent ranking board, all of which double-counted.
    // The blank-looking twin was the core copy (backfilled rows carry no
    // property_id, so the card had no listing to draw).
    //
    // The LEGACY row wins for these 178: it still holds the client name, phone,
    // commission, binder and event timeline that core deliberately does not
    // store, so it is strictly the richer render — and it is the row the legacy
    // edit form can actually write to. The core twin exists so the chain has
    // something to hang access requests and negotiations on, not to be displayed.
    //
    // Keyed on rows PRESENT in this response, so if the legacy half fails
    // (allSettled above) the core twins still render — degraded, never missing.
    const legacyIds = new Set(legacyRows.map(v => v.id));
    const deduped = coreRows.filter(m => !(m.legacy_viewing_id && legacyIds.has(m.legacy_viewing_id)));

    const merged = [...legacyRows, ...deduped]
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

    // Visit ordinal (0427 designer system, 2026-08-06): "მე-3 ვიზიტი" on the card
    // face. Computed HERE, once, over the merged feed — the one place both the
    // Meetings feed and the home cockpit draw from — never per-card. Key is the
    // client+property PAIR (party id for core rows, phone for legacy ones);
    // meetings missing either half get no ordinal rather than a wrong one.
    // ⚠️ THE SERVER'S NUMBER WINS WHERE IT EXISTS (B337 / migration 0435). This local count
    // is bounded by the 500 rows the viewer loaded, so a 4th viewing read as "მე-2 ვიზიტი"
    // once the earlier two aged out — a confident wrong number on a card face, shown to an
    // agent walking into the meeting. `meeting_visit_ordinal_v` counts the whole book and
    // the meetings endpoint now publishes it as `visit_no`.
    //
    // The fallback below is NOT dead code and must not be deleted: it is the only count a
    // LEGACY scheduler `viewings` row can have. Those live in a different database, keyed on
    // client_phone, and the view cannot see them — the same two-database seam as B340/B338.
    // So core rows get the book's answer and legacy rows get the page's, which is the best
    // either can honestly do today.
    const byPair = new Map();
    for (const v of merged) {
        if (v.visit_no != null) { v.__visit_no = v.visit_no; continue; }   // server-counted
        const who = v.client_party_id || v.client_phone || null;
        const where = v.property_id || v.property_snapshot?.core_property_id || null;
        if (!who || !where) continue;
        const k = `${who}|${where}`;
        (byPair.get(k) || byPair.set(k, []).get(k)).push(v);
    }
    for (const group of byPair.values()) {
        group.sort((a, b) => (a.scheduled_at || a.created_at || '').localeCompare(b.scheduled_at || b.created_at || ''));
        group.forEach((v, i) => { v.__visit_no = i + 1; });
    }
    return merged;
}

// core `meeting` → the row shape MeetingsFeed already draws. The card reads
// property facts from `property_snapshot`, so we synthesize one from what the
// server is willing to say about the property — DISTRICT and shape, never the
// street address (the server withholds it; see core_meeting.js).
//
// ⚠️ client_name / client_phone stay NULL and that is correct, not a gap: a core
// meeting owns relationships, not contacts. The card shows the client
// REPRESENTATIVE instead — always visible, and the person another agent actually
// coordinates with. Do not "fix" this by joining a name in.
function adaptCoreMeeting(m) {
    return {
        ...m,
        __core: true,
        // Namespaced so a core id can never collide with a scheduler viewings id
        // in the same list (React keys, and the open-modal lookup).
        id: `core:${m.id}`,
        core_meeting_id: m.id,
        client_name: null, client_phone: null,
        // ⚠️ B61 CONTRACT — do not drop. rankingScore.js reads `has_client`, falling
        // back to client_name/client_phone when it is absent. A core meeting has
        // neither name nor phone BY DESIGN, so without this every core meeting
        // would score as "no client" and silently vanish from agent ranking —
        // exactly the leaderboard regression B61 fixed, arriving by a new route.
        // A linked client party IS a client; that is the whole claim being made.
        has_client: !!m.client_party_id,
        property_snapshot: {
            core_property_id: m.property_id || null,
            // The street address now arrives for meetings the viewer is ON (core_meeting.js,
            // owner ruling 2026-08-01) and is null for everyone else — so this is the
            // server's answer, not a client decision. It stays null on the rest.
            address: m.property?.address || null,
            district_code: m.property?.district_code || null,
            rooms_count: m.property?.rooms ?? null,
            area_sq_m: m.property?.area_sqm ?? null,
            apt_floor: m.property?.floor ?? null,
        },
        // The feed derives the scheduled time from an events[] entry; core stores
        // it as a column, so present one synthetic event rather than teaching the
        // renderer a second way to find a date.
        events: m.scheduled_at
            ? [{ kind: 'scheduled', event_at: m.scheduled_at }]
            : [],
    };
}

// Listings authored/resold by our own roster agents (bridged parties, migration 0040) — powers
// the Desk "Listings" filter pill. scope='mine' needs the scheduler agent id (resolved by the
// caller via agents.auth_user_id === session.user.id); scope='all' is admin/manager-only,
// gated by the caller (role check), not here.
// `total` is the full count matching scope+since (not just this page) — lets the caller know
// whether there's more to page through instead of a fixed row cap silently dropping the rest.
// `filters` (optional) — the Desk advanced-search panel's state; any present field routes the
// server through search_agent_listings (core migration 0048), so the predicate runs over the
// FULL authored set in SQL, not just fetched pages. unknown_excluded = listings hidden because
// a filtered fact is MISSING (data gap), counted separately from "didn't match" — the Desk
// shows it as "N hidden for missing data".
// `unknownOffset` pages the POSSIBLE-MATCHES shelf, which has its own cursor because it is
// a separate result set with its own ordering (RPC v17's shelf rank), not a continuation of
// the matched page. ⚠️ It advances in CLUSTER units — the same unit `unknown_excluded`
// counts in — so it is `+= 60`, never `+= possibleListings.length` (a cluster can carry
// several listings, and mixing the two units drifts the cursor forward past whole pages).
// ⚠️ ONE SERIALISER FOR THE SEARCH FILTERS, extracted 2026-08-15 (B719). This block used to
// live inside getAgentListings, which meant the map could not ask the server the same
// question the feed asks without a tenth hand-maintained copy of it — and CLAUDE.md already
// names that as the Desk's standing disease (the server got its registry in B261; the client
// still needs ~9 sites per filter, and this extraction removes one of them).
// Pure move: the body below is byte-for-byte what getAgentListings ran, and it now runs it
// by calling this. The map path (getAgentListingPins) is the second caller.
// Moved verbatim to src/lib/searchWire.js (F-104) so the cache key can be built from the same output.
// Re-exported here so every existing import keeps working.
export { applySearchFilterParams }
// B05 (search/filter audit 2026-10-23): a range bound that is set but is not a number is REFUSED here,
// before the request, for the feed and the map alike. Sent as-is, the server read "abc" as no bound
// and answered with the wider corpus under a filter the agent could still see. The error is named so
// the Desk can say which bound to fix (invalidNumericBounds, lib/deskFilters.js).
function refuseInvalidBounds(filters) {
    const bad = invalidNumericBounds(filters)
    if (!bad.length) return
    const e = new Error(`not a number: ${bad.map(b => `${b.key}=${JSON.stringify(String(b.value))}`).join(', ')}`)
    e.name = 'InvalidFilterError'; e.invalid = bad
    throw e
}

export async function getAgentListings({ scope = 'mine', agentId, since, limit, offset, unknownOffset, unknownLimit, filters, sort, signal, leadStatus, claimedByMe, prefetch = false, workedByMe, status, rangeFacets } = {}) {
    refuseInvalidBounds(filters)
    const q = new URLSearchParams({ scope })
    if (prefetch) q.set('prefetch', '1')   // F-085: the server stamps pf=1 so foreground and speculative requests are measured apart
    // 0687: ask the search RPC for the price/area/land HISTOGRAMS on `search_meta.range_facets`.
    // OPT-IN ON PURPOSE -- send it only for page 0 of a view that actually draws the panel.
    // Measured on the 29,537-cluster corpus it costs ~848 ms at the RPC and ~955 ms end to end;
    // with the flag off the RPC is the old answer at the old price, which is what let the
    // server move to v47 in one step. The RPC ignores it for offset > 0.
    if (rangeFacets) q.set('range_facets', '1')
    // B576 (2026-08-16): our-work lifecycle — 'active' | 'archived'. LEADS have their own
    // lifecycle vocabulary (`leadStatus` → property_lead.status, just below), so this is
    // deliberately NOT sent on that scope; the server ignores it there for the same reason.
    // What 'archived' means lives in migration 0597 and is not restated on the client.
    if (status && scope !== 'leads') q.set('status', status)
    // scope=leads (0187, 2026-07-23): crawler leads through the SAME endpoint/pipeline.
    // leadStatus='dead' → Archive view; default = open/working/engaged.
    if (leadStatus) q.set('lead_status', leadStatus)
    // "Mine" is a LENS over the Leads inventory, not a stage (SEARCH_GRAMMAR §3b). Ownership
    // is the lead_claim relationship, so the server filters on a LIVE claim (p_claimed_by →
    // RPC v32) — never on authorship, and never on who created the lead. Leads-only: on the
    // other scopes "mine" is already the scope itself, and sending it there would mean a
    // second, different definition of the same word.
    if (claimedByMe && scope === 'leads') q.set('claimed', 'me')
    // WORKING is a different question from CLAIMED, and the distinction is the whole point
    // (DOMAIN_VOCABULARY §0, owner 2026-08-02): a claim is an INTENTION to work, `worked` is
    // evidence that work HAPPENED. The server maps this to `p_worked_by` on RPC v33 (0362),
    // which filters on the `represents_property` edge the five work triggers already write —
    // cooperation, agent-recorded restriction, meeting, repost, contact-add.
    // ⚠️ Verified against live data 2026-08-02 before wiring: 4,517 edges across 28 parties,
    // and the lens returns 115/74/98/44/82 for the five busiest. It is a real set, unlike the
    // 3-row claim lens it replaces.
    // ⚠️ It only pays off across the ACTIVE statuses. Against `open` alone it returns ~0 for
    // everyone — working a lead moves it out of `open` — which reads exactly like a broken
    // filter. Active (open/working/engaged) is the default here, so this is right by
    // construction; do not "optimise" the lens onto a narrower status set.
    if (workedByMe && scope === 'leads') q.set('worked', 'me')
    // thumbs=0 (2026-07-15, lazy thumbnails): skip the server's page-wide thumbnail stage —
    // rows arrive with images: [] and the Desk fetches strips for VISIBLE cards only via
    // getListingThumbs below. Harmless against an older server (unknown param ignored,
    // images just arrive inline as before).
    q.set('thumbs', '0')
    // B1214 step 7 (2026-09-06): a page carries one FULL row per cluster and its siblings as SUMMARIES
    // (__summary: true — the fields the card face, chip band and chain model read). Measured before:
    // 482 rows for 123 properties, 859 KB. The lineage modal fetches its own detail; getListingRows()
    // below hydrates siblings on demand. Harmless against an older server (unknown param ignored).
    q.set('light', '1')
    if (agentId) q.set('agent_id', agentId)
    if (since) q.set('since', since)
    if (limit) q.set('limit', limit)
    if (offset) q.set('offset', offset)
    // `sort` as a TOP-LEVEL param, not only inside `filters` (2026-07-31). The unfiltered
    // feed passes filters=null, so a sort carried only in that object could never be sent —
    // which is why picking a sort on an unfiltered Properties feed reordered the loaded page
    // and nothing more. Kept separate from the filters branch on purpose: `filters` being
    // null vs an object drives the cache, the local-filter fast path and the shelf, and
    // making a sort synthesise a filters object would move all three by accident.
    if (sort) q.set('sort', sort)
    if (unknownOffset) q.set('unknown_offset', unknownOffset)
    // Shelf SIZE (2026-08-10), separate from the offset above. `!= null`, NOT truthiness —
    // 0 is the whole point (the Desk's page-0 request: skip the shelf entirely, fetch it
    // lazily after the matched cards paint). Omitted → server omits p_unknown_limit → the
    // RPC's own default (60) applies, unchanged for every caller that doesn't pass this.
    if (unknownLimit != null) q.set('unknown_limit', unknownLimit)
    applySearchFilterParams(q, filters, { scope, unknownLimit, unknownOffset, sort, since })
    const d = await coreApi(`agent-listings?${q}`, { signal })
    // possibleListings (0055) — the unknown-verdict tier under active filters: rows whose data
    // is too coarse/missing to confirm OR deny the filter (e.g. coded at raion level under an
    // ubani filter). Shown after exact matches, never mixed into them.
    // rowsReturned = how many of `listings` came from the paginated rows clause (matches
    // `total`'s count) — the unbounded extraRows context is mixed into `listings` too, so the
    // caller's offset cursor must advance by rowsReturned, never by listings.length (2026-07-08).
    // searchMeta (v13): rpc_version + sort echo + kind_counts (whole-corpus per-kind
    // coverage, page-0 only — the client caches it per query, ae3b610 stability rule).
    // cacheTtlMs / deskLoad: the server's load-flexed freshness hint (lib/deskFreshness.js); null = absent.
    // server_vetted (2026-10-22): every row of a FILTERED answer belongs to a cluster the server passed
    // (matched_filter marks authored members only, so it cannot say this for captured ones). feedPipeline
    // reads it, beside the landed filters, to stop re-judging what the server already judged.
    if (filters) for (const l of d.listings || []) if (l && typeof l === 'object') l.server_vetted = true
    return { listings: d.listings || [], total: d.total || 0, rowsReturned: d.rows_returned ?? (d.listings || []).length, countUnit: d.count_unit || 'rows', unknownExcluded: d.unknown_excluded ?? null, possibleListings: d.possible_listings || [], nearbyListings: d.nearby_listings || [], searchMeta: d.search_meta || null, cacheTtlMs: d.__deskCacheTtlMs ?? null, deskLoad: d.__deskLoad ?? null }
}

// Lazy photo strips (2026-07-15, pairs with thumbs=0 above): { listing_id: [urls] } for just
// the ids passed — the Desk calls this for cards actually on screen. POST body, not query
// string (100+ uuids would hit nginx's 8KB request-line cap). The reshare→origin image
// fallback runs server-side (a restricted origin never reaches the client at all).
// B1214 step 7: full card rows for exactly these listing ids, through the same server shaper as a page
// (address_state, family, provenance, visibility). Used to hydrate a light page's summaries when a card
// needs the whole sibling (the expanded chain panel's provenance, a modal that reads the row directly).
export async function getListingRows(ids) {
    const list = [...new Set((ids || []).filter(Boolean))].slice(0, 200)
    if (!list.length) return []
    const r = await coreApi(`agent-listings?scope=all&thumbs=0&ids=${list.map(encodeURIComponent).join(',')}`)
    return r?.listings || []
}
export async function getListingThumbs(listingIds) {
    const d = await coreApi('listing-thumbs', { method: 'POST', body: { listing_ids: listingIds } })
    return d.thumbs || {}
}

// Photo strip for a PROPERTY — { property_id: [url] } — for surfaces that hold a property
// and no listing (meeting cards). Same route, same rule, server picks the newest member
// listing that actually has photos.
//
// ⚠️ This exists because scavenging the thumb from listings already loaded in the feed made
// the picture depend on the VIEWER: an agent's own listings sit in their feed page, an
// admin's agency-wide page is the newest 100 of thousands, so admins got the grey map
// placeholder on properties with a dozen photos. Don't "optimise" a surface back onto the
// loaded-rows lookup — it silently un-photographs whoever has the widest scope.
export async function getPropertyThumbs(propertyIds) {
    const ids = [...new Set((propertyIds || []).filter(Boolean))]
    if (!ids.length) return { thumbs: {}, listings: {} }
    const d = await coreApi('listing-thumbs', { method: 'POST', body: { property_ids: ids } })
    // `listings` = one representative listing id per property. Surfaces that hold only a
    // property (the meeting modal) need it to open the property modal, which is keyed by
    // listing. Same query, no extra round trip.
    return { thumbs: d.by_property || {}, listings: d.listing_by_property || {} }
}

// The ad's own text for card-face hover previews (2026-07-30) — { listing_id: {text, at} }.
// Same lazy shape as getListingThumbs and for the same reason: descriptions run to 4,000
// chars server-side and would add ~40-100KB to every feed page for text most cards never
// show. ⚠️ ALWAYS phone/link-redacted server-side, for every viewer, unlike the lineage
// payload which redacts only for non-representatives — the preview cannot be the thing that
// leaks an owner's number. The modal remains the place the full text lives.
export async function getListingDescriptions(listingIds) {
    const d = await coreApi('listing-descriptions', { method: 'POST', body: { listing_ids: listingIds } })
    return d.descriptions || {}
}

// Agent facet counts (0232) — agentId -> cluster count, same unit and ownership rule the
// agent filter itself uses, so the facet's number is the number the click delivers.
export async function getAgentCounts({ since } = {}) {
    const q = new URLSearchParams()
    if (since) q.set('since', since)
    try {
        const d = await coreApi(`agent-counts${q.toString() ? '?' + q : ''}`)
        return d.counts || {}
    } catch (e) { console.error('[agent-counts]', e.message); return {} }
}

// WHOSE WORK MAY THIS VIEWER SEE? (B1294) — the server's answer, never re-derived here.
// `core_office_scope.js` owns the rule: an office a manager currently covers, and the agents
// whose membership had not already ENDED when that tenure began (owner 2026-09-12: "agents who
// were active when they became managers and who they hired"). `since` is the tenure start.
//
// ⚠️ The Desk must NOT rebuild this from `agents.office_id`. That column is where somebody
// works TODAY, so deriving from it both loses the agent who left during the tenure — the one
// whose unfinished work is the manager's to chase — and silently rewrites history whenever
// anyone transfers, which is exactly what 0464 exists to prevent. Ask the server.
//
// Shape: { scope: 'company'|'offices'|'self'|'none', office_ids[], agent_ids[], party_ids[],
// offices:[{id,name}], since }. Fails to `self` — the narrowest answer — because a scope call
// that errors must not hand anybody a wider view than they had.
export async function getOfficeScope() {
    try {
        return await coreApi('office/scope')
    } catch (e) {
        console.error('[office-scope]', e.message)
        return { scope: 'self', office_ids: [], agent_ids: [], party_ids: [], offices: [], since: null }
    }
}

// Per-district listing counts for the Desk City/District dropdowns (core migration 0056) —
// { district_code: n } of OWN rows over the FULL authored set (same scope/since base as
// getAgentListings), NOT just loaded pages. Geography-only by design: no advanced-search
// filters, so the caller's gray-out reflects "nothing exists here", never "nothing matches the
// other filters". The reachable rollup (raion = own + children, ubani = own + parent coarse)
// is done client-side over the district hierarchy — this just returns the raw per-code counts.
// Returns {} on failure so a counts hiccup never blocks the dropdowns (they just don't gray).
//
// ⚠️ THIS IS THE SECOND DEFINITION OF "how many properties are in this district", and it
// disagrees with the search on three axes: it counts OUR AUTHORED listings (the leads stage is
// the pile that is NOT ours), it counts LISTINGS where the feed counts clusters, and it matches
// district_code exactly where the search runs a dispute verdict. Measured 2026-08-02: 1.6–2.6×
// low, and 44 districts grey as empty while holding leads. The search now emits the honest
// number itself on `search_meta.district_counts` (0363/0364) — but only on the filtered path,
// because the default leads view is served by the materialised cluster layer and never reaches
// the search RPC (0339b). Deleting this was tried the same day and reverted: these numbers also
// ORDER the district list, and without them the sort ties at 0 and falls to alphabetical,
// dropping Saburtalo and Vake off the top-8 entirely. Retire it when the materialised layer
// derives from the same definition — see core/BACKLOG.md, not by fixing it in place.
// 🚫 REMOVED 2026-08-02 (B273 move 4c). `getDistrictCounts` is gone: every engine that answers
// "how many properties are in this district" now calls `district_keys_v1` — the search (0366),
// the leads page (0369) and the Properties page (0371) — and the Desk reads
// `search_meta.district_counts` from whichever of them served the view.
//
// ⚠️ DO NOT RE-ADD A SECOND COUNTER, in any form, for any surface that lacks numbers. That is
// the architecture the owner ruled against on 2026-08-02 ("I'd rather spend more engineering
// effort once than spend years fixing inconsistencies caused by two systems trying to answer the
// same question"), and it is what produced the original defect: a facet reading 68 beside a
// click delivering 180. If a new surface needs district numbers, make its engine emit them from
// the same function.
//
// The ORDERING role that kept this alive is now served by caching the honest counts per
// scope+period (IntakeTab, `dcKey`): a first paint sorts on a slightly stale TRUE number instead
// of a fresh FALSE one. `search_district_counts` (0056) still exists in the database with no
// caller — dropping it is a separate, reversible step.

// Fire-and-forget log of a Desk search-box query (search_query_log, migration 0050) —
// the compiler's improvement backlog: residual = what didn't compile, dismissed = chips
// the agent removed (wrong guesses). Swallows failures: a lost log row is fine, an
// error surfacing in the search box is not.
export function logSearchQuery({ rawText, compiled, residual, dismissed, context, resultTotal }) {
    coreApi('search-log', {
        method: 'POST',
        // result_total = clusters the query returned (v10 instrumentation, 0119). The
        // hard/soft residual split is computed server-side from `residual`, not here —
        // the vocabulary never ships to the browser.
        body: { raw_text: rawText, compiled, residual, dismissed, context, result_total: resultTotal },
    }).catch(() => {})
}

// Per-agent recent searches (migration 0211) — server-side so the overlay's list follows
// the agent across devices. Deliberately separate from logSearchQuery above: that one
// feeds the vocabulary backlog (every event, un-deduped), this one is the 10 committed
// queries we show back. Both fail soft — recents are a convenience, not a gate.
export async function getRecentSearches(agentId) {
    if (!agentId) return []
    try {
        const d = await coreApi(`recent-searches?agent_id=${encodeURIComponent(agentId)}`)
        return d.queries || []
    } catch { return [] }
}

export function recordRecentSearchServer(agentId, query) {
    if (!agentId) return
    coreApi('recent-searches', { method: 'POST', body: { agent_id: agentId, query } }).catch(() => {})
}

// Saved searches (0231) — recents' persistent sibling. `params` is the canonical
// serializeSearch() hash: identity server-side is (agent_id, params), so saving an
// already-saved search is an idempotent upsert, and "is this saved" is string equality
// client-side. All three fail soft like recents — the search box must never break over
// a dead convenience list.
export async function getSavedSearches(agentId) {
    if (!agentId) return []
    try {
        const d = await coreApi(`saved-searches?agent_id=${encodeURIComponent(agentId)}`)
        return d.searches || []
    } catch { return [] }
}

export async function saveSearchServer(agentId, { params, label, category }) {
    if (!agentId) return null
    try {
        const d = await coreApi('saved-searches', { method: 'POST', body: { agent_id: agentId, params, label, category } })
        return d.search || null
    } catch { return null }
}

export async function renameSavedSearchServer(agentId, id, label) {
    if (!agentId || !id) return false
    try {
        await coreApi(`saved-searches/${encodeURIComponent(id)}?agent_id=${encodeURIComponent(agentId)}`, { method: 'PATCH', body: { label } })
        return true
    } catch { return false }
}

export async function deleteSavedSearchServer(agentId, id) {
    if (!agentId || !id) return false
    try {
        await coreApi(`saved-searches/${encodeURIComponent(id)}?agent_id=${encodeURIComponent(agentId)}`, { method: 'DELETE' })
        return true
    } catch { return false }
}

// Suggest dropdown server arms (blueprint §11, step 8): clients/business over the
// normalized 0106 columns, complexes raw-text. Deliberately weak (limit 3/arm, no
// ranking) — see the endpoint's weakness-contract header. agent_id scopes the clients
// arm to the caller's own proposals (same privacy rule as /api/core/proposals).
export async function searchSuggest(q, agentId, ctx) {
    const p = new URLSearchParams({ q })
    if (agentId) p.set('agent_id', agentId)
    // B940: the agent's ACTIVE locality rides along so the street arm can rank by context —
    // name-match > active city > active district > linked-property count (owner ruling
    // 2026-08-26: "a very popular street must not win when the user is clearly searching
    // another locality"). city is the canonical Georgian label (entryCityFilter); districts
    // are filter CODES. Harmless against an older server: unknown params are ignored.
    if (ctx?.city) p.set('city', ctx.city)
    if (ctx?.districtCodes?.length) p.set('districts', ctx.districtCodes.join(','))
    return coreApi(`search-suggest?${p}`)
}

// Free-text street fragment → canonical gazetteer street(s) with geo (Tbilisi-scoped).
// Precise resolver: stem + folds + avenue/prominence ranking (server core_street.js →
// core/street-resolve.mjs). Returns { best, candidates:[{name_ka,district,lat,lng,…}],
// ambiguous, count }. Used to attach geo to a picked street for the proximity filter.
export async function resolveStreetQuery(q) {
    const s = (q || '').trim()
    if (s.length < 2) return { best: null, candidates: [], ambiguous: false, count: 0 }
    return coreApi(`streets/resolve?q=${encodeURIComponent(s)}`)
}

// ── AddressBlock's four lookups, session-authed (2026-08-19) ──────────────────────────
// The fill form reaches these through its own token-scoped mirrors in core_fill.js
// (`${token}/streets`, `/geo-district`, `/geo-address`, `/geo-house`) because a public
// visitor has no session. The Desk has one, so it calls the same four core routes
// directly. Same endpoints, same answers — only the door differs, which is exactly why
// AddressBlock takes them as props and knows about neither.
//
// ⚠️ `typing=1` is not optional here. Without it the resolver uses whole-token equality,
// which is right for a finished search residual and wrong for a control being typed into:
// the list vanishes mid-word ("ანა პ" lists, "ანა პო" lists nothing). The fill mirror
// hardcodes typing=1 for the same reason.
export async function addressStreets(q, city) {
    const s = (q || '').trim()
    if (s.length < 2) return { best: null, candidates: [], ambiguous: false, count: 0 }
    return coreApi(`streets/resolve?typing=1&q=${encodeURIComponent(s)}` +
        (city ? `&city=${encodeURIComponent(city)}` : ''))
}
// lat/lng → which district contains this point. The authority for "the pin disagrees
// with the chosen district" — never inferred from the street's own free-text column.
export async function addressDistrictForPoint(lat, lng) {
    return coreApi(`geo/district?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`)
}
// city / district → the area the map should show ({south, west, north, east}), or null when unknown (core 1265).
// Never throws: a missing answer only means the map stays where it is.
export async function geoExtent({ districtCode = null, city = null } = {}) {
    if (!districtCode && !city) return null
    const qs = [districtCode && `district_code=${encodeURIComponent(districtCode)}`, city && `city=${encodeURIComponent(city)}`].filter(Boolean).join('&')
    try {
        const d = await coreApi(`geo/extent?${qs}`)
        return d && Number.isFinite(d.south) && Number.isFinite(d.north) && Number.isFinite(d.west) && Number.isFinite(d.east) ? d : null
    } catch { return null }
}
// ⚠️ Both of the below resolve to NULL rather than throwing, and the callers depend on it:
// a pin is still a good location when nothing can name it, and an unmapped house number
// simply leaves the street-level pin where it is. Turning either into an error would block
// a form over a lookup that was always allowed to fail.
export async function addressForPoint(lat, lng) {
    return coreApi(`geo/address?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`)
        .catch(() => null)
}
export async function addressHouse(street, number, lat, lng) {
    return coreApi(`geo/house?street=${encodeURIComponent(street)}&number=${encodeURIComponent(number)}` +
        `&lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`).catch(() => null)
}

// 0567 — the street facet's LIST for a district set, from its own query (53ms) instead of
// the search RPC that also builds clusters, verdicts and cards (~2,000ms measured live).
// ⚠️ A CATALOGUE, NOT A TALLY: district-scoped only, so it carries {id, label} and NO
// count. "How many match your search" has exactly one source — search_meta.street_counts —
// and this is deliberately unable to answer it.
export async function getStreetCatalog(districtCodes, signal) {
    const codes = (districtCodes || []).filter(Boolean)
    if (!codes.length) return []
    // Failure is SLOWER, never wrong: the facet falls back to the search RPC's own street counts.
    // ⚠️ So a failure must not READ as an answer (search/filter audit 2026-10-23, Desk §6). This
    // returned [] on every error, and [] is the facet's word for "this district has no streets";
    // an aborted older lookup even settled as [] over the newer one still in flight. It now
    // resolves only with a real list and REJECTS otherwise (AbortError for a cancelled lookup),
    // so the caller can tell cancelled, failed and empty apart. The caller still shows no error.
    const d = await coreApi(`street-catalog?district_codes=${encodeURIComponent(codes.join(','))}`, { signal })
    if (!Array.isArray(d?.streets)) throw new Error('street-catalog: no street list in the answer')
    return d.streets
}

// Image search (2026-07-17): raw image bytes → server computes pHash → ranked
// perceptual matches over the capture layer's 38k hashed images. Returns
// DraftPicker-ish rows + hamming distance. agent_id applies the same fail-closed
// ownership visibility as /listings/search.
export async function searchListingsByImage(file, agentId) {
    const p = new URLSearchParams()
    if (agentId) p.set('agent_id', agentId)
    // B648 (owner 2026-08-12: "image search works only from admin"). An image is an IDENTITY
    // query — "what does the system know about this?" — exactly like a pasted listing id or a
    // phone number, and its honest answer includes market listings the agent does not own. B260
    // settled that for /listings/search on 2026-07-31; both call sites of searchCoreListings pass
    // this unconditionally and this one was simply never given it, so an agent's image search was
    // silently clamped to their own inventory while an admin's (no roster row → no agent_id) ran
    // corpus-wide. agent_id still rides along: the server keeps it for everything except the
    // ownership filter.
    p.set('widen', 'identity')
    // ⚠️ Raw fetch, not coreApi (binary body) — so it must send x-viewer-token ITSELF.
    // B53 step 2a (2026-07-26): coreApi adds the token for free; these hand-rolled uploads
    // were the two call sites that would 401 the moment the bundle key goes away (step 2c).
    const token = await viewerToken()
    const r = await fetch(`${SNTV_API}/api/core/image-search?${p}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/octet-stream',
            ...(token ? { 'x-viewer-token': token } : {}),
        },
        body: file,
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(data.error || `image-search ${r.status}`)
    return data.results || []
}

// A reshare's own values overlaid on its origin ad's (find_reshare_origin, migration 0041) —
// the agent's own posting wins field-by-field wherever they set something. Powers the "open a
// reshare" modal in the Desk Listings tab (as opposed to a bare window.open to the source URL).
// agentId (optional) = the viewing agent. When set and the origin ad was scraped by a
// DIFFERENT agent, the server withholds the owner's identity and returns the OWNING AGENT's
// name/phone as origin_contact (contact_via_agent: true) — deals route agent → agent → owner
// (2026-07-12 rule). Admin/manager callers omit it and see everything.
export async function getListingLineage(listingId, { agentId } = {}) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
    return coreApi(`listing/${listingId}/lineage${q}`); // { listing, origin, origin_contact, hops, merged, siblings, price_history }
}

// Every stored image SET for a property, for the lineage modal's set switcher (2026-08-17,
// design/IMAGE_SET_SWITCHER_ANSWERS). A set is one (source, kind, is_thumb) group over the
// property CLUSTER's listings — ss.ge's clean rendition and ss.ge's watermarked rendition are
// two sets, and the owner ruled they are two TABS (not one toggle) even though they hold the
// same pictures on 89.8% of properties.
//
// ⚠️ THIS IS THE ONLY READER THAT ASKS FOR `photo_watermarked`, and that is deliberate. 0194's
// rule is that the watermarked rendition exists for dedup/pHash and every kind='photo' consumer
// excludes it from DISPLAY. This surface is the declared exception: an agent choosing a photo
// has to see the stamped copy to choose against it. Do not read this as licence for other
// surfaces — the card strip and the share link keep going through image_selection.js.
//
// ⚠️ `mark` (clean | logo | unknown) MUST come from the server. Deriving it here from source+kind
// would be a second definition of the owner's clean > unknown > logo > phone-number ladder, which
// lives in image_selection.js. A set with no `mark` renders as `unknown` — the label that claims
// least — never as `clean`.
export async function getPropertyImageSets(propertyId) {
    // { sets: [{ id, listing_id, source, source_listing_id, kind, is_thumb, mark, count, urls }],
    //   distinct_photos }  — distinct_photos is the DEDUPED picture count, which is what the
    //   header must show: summing set counts double-counts every rendition (a flat with 8 photos
    //   on three portals holds 48 rows and 24 pictures).
    return coreApi(`property/${propertyId}/image-sets`);
}

// Same payload as getListingLineage, for N listings in ONE request (2026-07-26).
// The Desk's id/phone/image reach-back expands every hit into its property family; doing
// that one call at a time cost 20 round trips AND 21 of the caller's rate-limit budget for
// a single keystroke. Server-side it reuses the exact same function the single-id route
// does — verified byte-identical over 20/20 payloads on both the admin and the restricted
// (non-representative, masking engaged) paths — so the representation boundary has one
// implementation, not two.
//
// POST because 20 uuids overflow a sane query string. Returns { results: {id: payload},
// errors: {id: message} } — per-id failures are REPORTED, not silently dropped, so a
// caller can tell "nothing found" from "we couldn't look".
const LINEAGE_BATCH_MAX = 25;   // server enforces the same cap; keep them in step
export async function getListingsLineageBatch(listingIds, { agentId } = {}) {
    const ids = [...new Set((listingIds || []).filter(Boolean))];
    if (!ids.length) return { results: {}, errors: {} };
    const out = { results: {}, errors: {} };
    for (let i = 0; i < ids.length; i += LINEAGE_BATCH_MAX) {
        const chunk = ids.slice(i, i + LINEAGE_BATCH_MAX);
        const r = await coreApi('listings/lineage-batch', {
            method: 'POST',
            body: { ids: chunk, ...(agentId ? { agent_id: agentId } : {}) },
        });
        Object.assign(out.results, r.results || {});
        Object.assign(out.errors, r.errors || {});
    }
    return out;
}

// District dispute vote (0101, 2026-07-16) — the lineage modal's one-click "which district
// is right?" question. choice: 'declared' (URL-slug district) | 'polygon' (pin district).
// One vote per agent per property; re-vote replaces. Returns { votes, status, my_vote }.
export async function voteDistrict(propertyId, choice, agentId = null) {
    // agentId is OPTIONAL since B277 (owner ruling 2026-08-02: managers and admins may settle a
    // district too). They have no agents row, so the server falls back to the party bridged to
    // the VERIFIED session (party.auth_user_id). Sending null is the normal case for them, not
    // an error — and the client never sends a party id, because a client-asserted voter would
    // let any caller vote as anyone.
    return coreApi(`property/${propertyId}/district-vote`, {
        method: 'POST', body: { choice, agent_id: agentId || null },
    })
}

// Recent price movements (listing_observation diffs, 0022) — the Desk notifications bell.
// agentId (optional) scopes the changes to that agent's OWN listings — passed for a plain
// agent login so their notifications aren't the whole market's price props (2026-07-05).
// Admin/manager omit it and see everything. Server ignores the param if it doesn't support
// it yet, so this is safe to ship ahead of the server change (falls back to market-wide).
export async function getPriceChanges({ days = 7, agentId } = {}) {
    const q = new URLSearchParams({ days });
    if (agentId) q.set('agent_id', agentId);
    return (await coreApi(`price-changes?${q}`)).changes || [];
}

// B3 crawler "Run test now" (Admin → General) — runs one disappearance/price re-check tick
// on demand and returns its tally. Cadence itself is edited via the config table
// (crawler_settings key), same save path as other General settings.
export async function runCrawlerTest() {
    return (await coreApi('crawler/run-test', { method: 'POST' })).tally || null;
}

// B307 map pins (2026-08-05): every coordinate for the browse corpus in one call, so the
// Desk map can draw the whole book instead of the loaded pages. Positional triples
// [listing_id, lat, lng], one per property — ~3.4k pins ≈ 204 KB, ~3.2s server-side
// (agent_listing_pins_v1 calls the SAME membership definition the feed reads, 0411).
// B719 (2026-08-15): `filters` is optional and changes what this returns. Without it the
// server answers with the whole corpus overlay, as it always has. With it, the server runs the
// SAME search the feed runs and returns a pin for every matching cluster — which is the point:
// a filtered map used to fall back to whatever rows the feed had fetched (PAGE_SIZE 30 × 3
// prefetched pages = 90 pins under a counter reading 273).
// The filters are serialised by `applySearchFilterParams`, the same function `getAgentListings`
// uses, so the map cannot drift from the feed by construction. `leadStatus` rides along because
// it decides WHICH pool is being searched, not how it is narrowed.
export async function getAgentListingPins({ scope = 'all', agentId, since, filters, leadStatus, status, claimedByMe, workedByMe } = {}) {
    refuseInvalidBounds(filters)
    const q = new URLSearchParams({ scope })
    if (agentId) q.set('agent_id', agentId)
    if (since) q.set('since', since)
    // Same word the feed sends, reaching the same RPC (0600), so a filtered map and its count
    // cannot disagree. Sent whenever set — 'active' is a real filter, not a default: the RPC's
    // no-op value is 'all', which this surface no longer offers.
    if (status) q.set('status', status)
    if (leadStatus) q.set('lead_status', leadStatus)
    // ⚠️ No `sort` — a map has no order, and passing one would make the server's own
    // "is this filtered?" test say yes for every request. That mistake shipped once and put a
    // bare map request through a full-corpus search: 125 seconds, then a 504.
    if (filters) applySearchFilterParams(q, filters, { scope })
    // The two leads lenses, the SAME words the feed sends (see getAgentListings). The pins route
    // resolves the viewer itself and refuses (401) when it cannot — never from an id sent here.
    // Supported server-side since 2026-09-17; before that the route answered 400 and the leads
    // map simply did not ask.
    if (claimedByMe && scope === 'leads') q.set('claimed', 'me')
    if (workedByMe && scope === 'leads') q.set('worked', 'me')
    return coreApi(`agent-listings/pins?${q}`)   // { pins: [[id, lat, lng]...], total, filtered? }
}

// B576 (2026-08-16): the two numbers on the our-work status pills. Corpus-wide, from the
// SAME v6 the feed and the pins read — the Desk cannot compute these itself, which is why
// those buttons carried no numbers for as long as they existed (a client-side predicate can
// only count the page it was handed).
// Returns { active, archived } or null; null means "no numbers", never zero — a facet that
// renders 0 because a request failed is a lie, and this rail already distinguishes the two
// (a total that has not arrived renders as no number, B321).
export async function getAgentListingStatusCounts({ scope = 'all', agentId, since } = {}) {
    const q = new URLSearchParams({ scope })
    if (agentId) q.set('agent_id', agentId)
    if (since) q.set('since', since)
    try {
        const d = await coreApi(`agent-listings/status-counts?${q}`)
        return d?.counts || null
    } catch { return null }
}

// B307c pin briefs (2026-08-05): the hover half of the dots-then-detail map. A pin carries
// coordinates and an id and nothing else — that is what makes 29k of them affordable — so a
// bare dot cannot say what it is until something asks. This asks, for the few ids a mouse
// actually crosses. Batched: the caller coalesces a hover window into ONE call (the server
// caps at 40 ids, which is the nginx 8KB request-line ceiling, not a rate limit).
// Returns [{ id, intent, price, currency, kind, rooms, bedrooms, area_sqm, district_code }].
// Facts only — the same ones search filters on. Address/contact/owner deliberately absent:
// a hover must not read identity that the card endpoint would mask.
// B1021 (2026-08-27): the card face's window into `property_feature`. One batched call per
// page of cards, the 🤝 coop-chips contract — `group` names a feature CATEGORY the server
// allowlists, never a code list, so a fifth defect word needs a `feature` row and nothing
// here. Server cluster-expands and keys the answer by the id YOU asked for, so a defect read
// off a same_as sibling's photographs lands on this card without the caller knowing about
// clusters.
//
// ⚠️ 200-id cap is the server's, and it is not a rate limit — `in.()` past ~200 uuids walks
// into nginx's 8KB request-line ceiling, the wall the cooperation batch already hit.
// The Public Registry's own answer for a property's parcel (core 1555, lib core_cadastral.js).
// Reads what we ALREADY hold - no upstream call is made by this path, so an empty answer means
// "not resolved yet", not "no such parcel". One property may hold two codes (an advert selling
// two parcels), so this returns a list.
// ⚠️ It deliberately carries no owner identity. The registry publishes owners' names and personal
// numbers; the owner's ruling (2026-09-20) is that Desk may hold them and nothing public may show
// them, and that is a separate change with its own processing-register entry. Do not add it here.
export async function getPropertyParcels(propertyId, { signal } = {}) {
    if (!propertyId) return []
    const r = await coreApi(`cadastral/property/${propertyId}`, { signal })
    return Array.isArray(r?.parcels) ? r.parcels : []
}

// The micro-district / quarter / settlement / neighbourhood a property is in (core 1654-1660,
// property_area_v1, rebuilt hourly). null = no area known.
export async function getPropertyArea(propertyId, { signal } = {}) {
    if (!propertyId) return null
    const r = await coreApi(`area/property/${propertyId}`, { signal })
    return r && r.area ? r : null
}

// An open street ambiguity (core 1723/1724): the street is not forced by any witness, so a person is asked.
// { question: null } when there is nothing to ask. choice = a candidate street id, or 'skip'.
export async function getStreetQuestion(propertyId, { signal } = {}) {
    if (!propertyId) return null
    const r = await coreApi(`street-question/property/${propertyId}`, { signal })
    return r && r.question ? r.question : null
}
export async function answerStreetQuestion(propertyId, choice, agentId = null) {
    return coreApi('street-question/answer', { method: 'POST', body: { property_id: propertyId, choice, agent_id: agentId || null } })
}

export async function getPropertyChips(propertyIds, { group = 'defect', signal } = {}) {
    const list = [...new Set((propertyIds || []).filter(Boolean))]
    if (!list.length) return {}
    const out = {}
    for (let i = 0; i < list.length; i += 200) {
        const q = new URLSearchParams({ property_id: list.slice(i, i + 200).join(','), group })
        const r = await coreApi(`property-chips?${q}`, { signal })
        Object.assign(out, r.chips || {})
    }
    return out
}

// 0894 (2026-09-06): the card face's "what is unusual about this flat" note. Same batched-per-page
// contract as getPropertyChips (one call per page, 200-id cap for the same nginx reason). The
// server keys the answer by the id YOU asked for after resolving it to its cluster; a property with
// a rent and a sale advert returns TWO rows and the card picks by its listing's intent
// (DistinctNote.pickDistinct). Returns { [propertyId]: [row, …] } — an empty array is "computed,
// nothing to say", which the card must treat as a fact and not as still-loading.
export async function getPropertyDistinctiveness(propertyIds, { signal } = {}) {
    const list = [...new Set((propertyIds || []).filter(Boolean))]
    if (!list.length) return {}
    const out = {}
    for (let i = 0; i < list.length; i += 200) {
        const q = new URLSearchParams({ property_id: list.slice(i, i + 200).join(',') })
        const r = await coreApi(`property-distinctiveness?${q}`, { signal })
        Object.assign(out, r.items || {})
    }
    return out
}

// ქონების შეფასება (1065, owner 2026-09-12): the deterministic analysis box — statistics groups
// and the normal price, traits with coverage, the most-alike active flats, the questions for the
// call. One server route, one SQL function, no model. `answerCardQuestion` records what the agent
// learned on the call (a chip answer or a number) and returns the RECOMPUTED analysis, so the tab
// redraws its numbers from the same round trip.
export async function getCardAnalysis(propertyId, { signal } = {}) {
    return coreApi(`card-analysis?property_id=${encodeURIComponent(propertyId)}`, { signal })
}
export async function answerCardQuestion(body, { signal } = {}) {
    return coreApi('card-answer', { method: 'POST', body, signal })
}
// Agent valuation adjustments (core 1994, owner 2026-09-23) — kept apart from the statistical estimate.
export async function getValuationAdjustments(propertyId, { signal } = {}) {
    return coreApi(`valuation-adjustments?property_id=${encodeURIComponent(propertyId)}`, { signal })
}
// The statistics engine for one property (2026-09-23): { kind, deal, market (stats_answer_v1 summary for
// the district, flats by rooms), valuation (stats_valuation_v1 for house/land/commercial) }.
export async function getMarketStats(propertyId, { signal } = {}) {
    return coreApi(`property/${encodeURIComponent(propertyId)}/market-stats`, { signal })
}
export async function addValuationAdjustment(body, { signal } = {}) {
    return coreApi('valuation-adjustment', { method: 'POST', body, signal })
}

// Every model expense, every caller, one ledger (owner 2026-09-07). Management only — the server
// resolves the viewer token to a role and answers 403 otherwise. Rows come from the core
// ai_expense_daily RPC; an Orpheus day with notes but no cost row arrives as source
// 'orpheus_notes_unlogged' with cost null, which the panel must show as a gap, not as $0.
export async function getAiExpenses({ days = 30, signal } = {}) {
    return coreApi(`ai-expenses?days=${encodeURIComponent(days)}`, { signal })
}

export async function getListingBriefs(ids, { signal } = {}) {
    const list = (ids || []).filter(Boolean)
    if (!list.length) return []
    const q = new URLSearchParams({ ids: list.join(',') })
    return (await coreApi(`listing-brief?${q}`, { signal })).briefs || []
}

// B306 complex-linker "Run test now" (Admin → General). The server defaults this to
// dryRun and caps the batch at 10 — a dry run reports what it WOULD ask without
// spending anything, which is the only safe way to look at a worker whose every
// decision is permanent (a property that gets a verdict never returns to the frontier).
// `force` is implied server-side, so the button works while the worker is switched off.
// Returns the tick tally as-is: { fetched, considered, linked, rejected, ambiguous,
// ai_errors } or { skipped: 'nothing_answerable' | 'already_running' }.
export async function runComplexLinkTest({ dryRun = true, max = 5 } = {}) {
    return coreApi('complex-link/run-test', { method: 'POST', body: { dryRun, max } });
}

// B92 discovery profiles (Admin → General): the EFFECTIVE list the walker runs —
// config row's discovery_profiles, or the module SEED_PROFILES when none stored
// (fromConfig says which). Edits persist through the shared crawler_settings config
// blob (merge-over-stored — see CrawlerSettings' 🚨 note), never a separate PUT.
export async function getDiscoveryProfiles() {
    return coreApi('discovery/profiles');
}
// One bounded dry-run walk of a single profile (server caps pages at 5 / details at
// 15 — nginx 60s). Returns the tick tally; per-profile numbers live in
// tally.profiles[profileId]: { idsSeen, portalTotal, pagesWalked, gated, ... }.
export async function runDiscoveryProfileTest(profileId) {
    return coreApi('discovery/run-test', { method: 'POST', body: { profileId, dryRun: true, maxPages: 2 } });
}

// Mirror this agent into core as a party (migration 0040 resolve_agent_party), so their
// reshares/leads/lineage have an identity to hang off (2026-07-02 agency-layer design).
// Called alongside createSentavusUser/updateSentavusUser in useSchedule.js — same
// non-blocking mirror pattern, just a second target DB.
export async function syncAgentToCore({ agentId, name, email, phone }) {
    return coreApi("agents/sync", {
        method: "POST",
        body: { agent_id: agentId, name, email, phone },
    }); // { party_id }
}

// ── AI parse + commit (Intake Contract — core/INTAKE_CONTRACT.md) ─────────────
// Layering: AI emits a decision envelope → this adapter normalizes it → the
// execute_proposal SQL gateway commits. parse() only ever reads; nothing is
// written until commit() is called with an envelope the human has reviewed
// (and possibly edited) on screen.

export async function parseIntakeEnvelope({ raw_text, channel }) {
    return coreApi("intake/parse", {
        method: "POST",
        body: { raw_text, channel },
    }); // { intake_id, envelope, anchors, candidates, usage }
}

export async function commitIntakeEnvelope({ raw_text, channel, envelope, anchors, intake_id, agent_id }) {
    return coreApi("intake/commit", {
        method: "POST",
        body: { raw_text, channel, envelope, anchors, intake_id, agent_id },
    }); // { intake_id, decision, results }
}

// search_polygons: { requirement_id: GeoJSON Polygon } for whichever proposals in this page have
// a client with a drawn search area — powers the mini area-preview on client cards.
// agentId: scopes the feed to one agent's own entries (agent role must never see the full feed —
// pass undefined/omit for admin/manager, who see everything).
// kind: optional, 'client' asks for client proposals only — see mergeProposalFeeds (deskFeed.js)
// for why the Desk needs a window of its own for them.
export async function getRecentProposals(limit = 20, since, agentId, kind) {
    // counts=0: skip the match-count enrichment (the endpoint's main latency) — the Desk
    // paints first and merges the pills in via getProposalMatchCounts below.
    const q = new URLSearchParams({ limit, counts: '0' })
    if (since) q.set('since', since)
    if (agentId) q.set('agent_id', agentId)
    if (kind) q.set('kind', kind)
    const d = await coreApi(`proposals?${q}`)
    return { proposals: d.proposals || [], searchPolygons: d.search_polygons || {} }
}

// Deferred "N candidate listings" pills for client cards — { requirement_id: count }.
// Chunked at 80 ids, same wall as listCooperations (BACKLOG B65). TWO bugs were stacked
// here, both silent:
//   1. URL cap — an all-time client search passes up to 500 requirement ids, and
//      500 × 37 bytes (uuid + comma) is a ~18.6KB request line, past nginx's 8KB cap.
//      The request 414s and the caller's `.catch` swallows it, so NO count pills paint
//      at all. Breaks at ~221 ids, which real feeds already reach. Exact signature of
//      the notifications outage (memory: desk-notifications-url-cap).
//   2. Server-side truncation — /api/core/proposal-match-counts does `.slice(0, 100)`
//      on the id list, so even a URL that FIT silently returned counts for only the
//      first 100 clients; the rest showed no pill and looked like "0 candidates".
// Chunking at 80 keeps every request both under the URL cap and under that 100 slice,
// so all ids are actually counted. Chunk anything that interpolates an id list.
export async function getProposalMatchCounts(requirementIds = []) {
    const ids = [...new Set(requirementIds.filter(Boolean))]
    if (!ids.length) return {}
    const chunks = []
    for (let i = 0; i < ids.length; i += 80) chunks.push(ids.slice(i, i + 80))
    const results = await Promise.all(chunks.map(part =>
        coreApi(`proposal-match-counts?requirement_ids=${part.join(',')}`)))
    return Object.assign({}, ...results.map(d => d.counts || {}))
}

// Archive/dismiss a PENDING proposal — the server flips its status to 'dismissed' (migration
// 0045). Nothing is deleted; the draft just leaves the "needs review" list. Throws (409) if the
// proposal was already reviewed/executed. Requires the server /proposal/:id/dismiss route live.
export async function dismissProposal(id) {
    return coreApi(`proposal/${id}/dismiss`, { method: "POST" }); // { id, status }
}

// Retire a FAILED save attempt (2026-08-01) — the red "not saved" card that can't be
// opened because nothing was ever created. dismissProposal above REFUSES these (it is
// pending-only, by design), and the commit door's retry sweep only clears corpses of the
// SAME intake, so a re-add typed later never retires the earlier one. Called by the
// failed-card retry once the attempt is genuinely resolved: `by` = the proposal id of the
// save that succeeded, or `resolvedPartyId` = the person it turned out to already be.
// Server re-checks that the row really is unexecuted-with-an-error; the original error is
// kept, never overwritten. Idempotent → { already: true } on a second call.
export async function supersedeFailedProposal(id, { by = null, resolvedPartyId = null } = {}) {
    return coreApi(`proposal/${id}/supersede`, {
        method: 'POST', body: { by, resolved_party_id: resolvedPartyId },
    }); // { id, superseded_by } | { id, already }
}

// The CURRENT materialized state of one demand (requirement + criteria + party), not an
// event replay — powers the Data Desk's "click a card to edit" modal.
export async function getRequirement(id) {
    // viewer identity rides along (2026-07-16) so the server can mask the client's
    // phone/email when the demand belongs to ANOTHER agent — the representing agent's
    // number comes back in `agent.phone` instead. Admin (no viewer) sees everything.
    const vp = viewerParam();
    return coreApi(`requirement/${id}${vp ? `?${vp.slice(1)}` : ""}`); // { id, status, person, requirement, criteria, agent, contact_masked }
}

// One person, both sides at once: their demands (requirements) + supply (authored listings →
// property). Powers the Data Desk person card. See party_overview RPC, migration 0042.
export async function getParty(id) {
    // viewer identity rides along (2026-07-16) — same cross-agent contact masking as
    // getRequirement: another agent's client hides phone/email/contacts, the
    // representative comes back in `agent`. Admin (no viewer) sees everything.
    const vp = viewerParam();
    return coreApi(`party/${id}${vp ? `?${vp.slice(1)}` : ""}`); // { id, name, phone, email, contacts, contact_book[], demands[], listings[], listing_count, agent, contact_masked }
}

// ── Party contact book (core 0141/0142) ──────────────────────────────────────
// Contacts are OBSERVATIONS: adding never edits, a dead number is marked
// unreachable (kept as evidence), and "who do we call" is the preferred/oldest
// ACTIVE row — projected server-side into party.primary_phone. The write gate
// mirrors the person card's contact mask (owned client → owning agent/mgmt only).
export async function addPartyContact(partyId, { kind, value, channel = null, preferred = false }) {
    return coreApi(`party/${partyId}/contact-book`, { method: "POST", body: { kind, value, channel, preferred } });
}
// One logged reveal for the WHOLE book (the ContactPill pattern: opening the popover is
// the deliberate reveal; rows then show values). Legacy scalar rides as a pseudo-row.
export async function revealContactBook(partyId, context = null, requirementId = null) {
    // requirementId (0157): the reveal door is REQUIREMENT-scoped when the surface is a
    // demand card — the server checks active representation of that one requirement.
    return coreApi('contact/reveal', { method: 'POST',
        body: { target_party_id: partyId, book: true, context, requirement_id: requirementId } });
}
export async function updatePartyContact(contactId, { status = null, note = null, preferred = null } = {}) {
    return coreApi(`party/contact-book/${contactId}`, { method: "POST", body: { status, note, preferred } });
}

// ── Listing entry (core/LISTING_OBSERVATION_CONTRACT.md, 2026-07-03) ──────────
// Manual typed entry: pressing Save IS the review — one call commits through the
// single supply path (prepareListingObservation → ingestListing server-side).
// entry_id MUST be minted once per logical Save and reused on retry (the server
// derives source_listing_id from it; a fresh id per retry would duplicate rows).
// agent_id (0064): stamps this save's claims with the agent's core party, so each
// agent keeps their OWN version per attribute (two agents editing the same property
// no longer overwrite each other), and auto-adds the property to their inventory.
// property_id (unified property card, 2026-07-08): when EDITING an already-open
// property, anchor the observation to that exact id — identity re-resolution by
// address would mint a duplicate property row (no cadastral → always inserts).
// ⚠️ THIS DESTRUCTURE IS A WHITELIST, and it silently drops anything not named. Found by audit
// 2026-09-02: `entrance_apt` — the flat number — was collected by the property modal, sent
// TOP-LEVEL, read by the server (core_manual.js stamps it into listing meta), and never arrived,
// because it was missing from this line. The modal's own comment beside the field reads "a
// control that collects a value nobody stores is a lie to the agent", which is exactly what it
// had become.
// 📌 A field nested inside `property` rides along because that object passes through whole; a
// TOP-LEVEL field has to be named here. That difference is invisible at both ends — the caller
// sends it and the server reads it — which is why it survived since 2026-08-19.
// T24/0846: facts_retract = attributes the agent CLEARED among their own, retracted server-side.
// ⚠️ No comments INSIDE the body literal below: saveListingEntry.test.mjs parses it textually and a
// colon in a comment swallows the key after it (it reported facts_retract as never sent).
// chip_states: the yes/no answers for lift / parking / heating from the property form. Only a
// strict true or false is an observation; the server rejects anything else (core_prepare.js).
// (Kept out of the body literal below: saveListingEntry.test.mjs reads that literal as text and
// splits it on commas, so a comment inside it hides the field from the test.)
// based_on_listing_id: when this save is an agent's VERSION of a scraped ad, the ad it was opened from
// (owner 2026-09-28 — the version shows that ad's photos + the agent's own; the ad itself is never edited).
export async function saveListingEntry({ origin, entry_id, proposal_id, source_kind, property, listing, facts, owner, provenance, agent_id, property_id, tags, entrance_apt, facts_retract, features_retract, chip_states, based_on_listing_id }) {
    return coreApi("listing/save", {
        method: "POST",
        body: { origin, entry_id, proposal_id, source_kind, property, listing, facts, owner, provenance, agent_id: agent_id || null, property_id: property_id || null, tags: tags || undefined,
        // '' is sent on purpose: it clears a saved entrance/apartment detail (absent = untouched).
        entrance_apt: entrance_apt === '' ? '' : (entrance_apt || undefined),
        facts_retract: facts_retract && facts_retract.length ? facts_retract : undefined,
        features_retract: features_retract && features_retract.length ? features_retract : undefined,
        chip_states: chip_states || undefined,
        based_on_listing_id: based_on_listing_id || undefined },
    }); // { propertyId, listingId, rawId, generationId, unresolved, ... }
}

// ── Listing images & documents (Add-property modal, lib/core_media.js server-side) ───
// kind: 'photo' (publishable gallery) | 'document' (agent-only: plans, PDFs/sheets,
// cadastral-code photos — never reaches publication; core migration 0062).
// Upload sends RAW file bytes (Content-Type = the file's mime), NOT coreApi JSON —
// the server route uses express.raw (15mb) precisely because base64-in-JSON would
// blow the global 4mb JSON cap on ordinary phone photos.
// `meta` is what src/lib/exifRead.js read from the ORIGINAL file, before downscalePhoto
// re-encoded it through a canvas and destroyed it. Owner ruling 2026-09-03 is "read it into the
// DB, strip it from the stored file"; without this the read never happens for any photo over
// 1 MB — which is every photo a phone takes. The server prefers its OWN read and falls back to
// these, so a build that stops sending them degrades to today's behaviour rather than breaking.
export async function uploadListingImage(listingId, file, { kind = 'photo', meta = null } = {}) {
    const r = await fetch(`${SNTV_API}/api/core/listing/images?listing_id=${encodeURIComponent(listingId)}&kind=${kind}`, {
        method: "POST",
        // Same raw-fetch caveat as searchListingsByImage above (B53 step 2a).
        headers: {
            "Content-Type": file.type || "application/octet-stream",
            ...(await viewerToken().then(t => t ? { "x-viewer-token": t } : {})),
            // ⚠️ A CUSTOM HEADER MAKES THE REQUEST PREFLIGHTED, and these two cost ~40 minutes of
            // broken photo uploads on 2026-09-04 before the allow-list caught up. That list is in
            // NGINX (/etc/nginx/sites-available/default), not in server.js — live server.js says
            // something different from what the wire serves, so patching it would have changed
            // nothing and read as a failed fix. If uploads ever start failing with "Failed to
            // fetch" behind a 204 preflight, check that nginx line first.
            ...(meta?.takenAt ? { "x-photo-taken-at": meta.takenAt } : {}),
            ...(meta?.geo ? { "x-photo-geo": `${meta.geo.lat},${meta.geo.lng}` } : {}),
        },
        body: file,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
        // ⚠️ A 413 here is usually NOT ours: nginx enforces client_max_body_size at the proxy
        // and answers with an HTML error page, so `data.error` is empty and the raw status is
        // all the caller would otherwise get. Name the file and its size — that is the fact
        // the agent needs to act on. (2026-08-19: three retries against a 1 MB proxy default,
        // with "Upload failed. Please try again." as the only feedback.)
        if (r.status === 413) {
            const mb = (file.size / 1048576).toFixed(1);
            throw new Error(`"${file.name}" is too large to send (${mb} MB) — 413 from the server`);
        }
        throw new Error(data.error || `${r.status} ${r.statusText || ''}`.trim());
    }
    return data; // { id, url, kind, position, duplicate? }
}
// { images: [{id,url,position,source,kind}] } — the DISPLAY rows (clean renditions).
// includeOriginals also returns `originals`: the portal's own full-size copies, which on
// ss/myhome/home.ge carry the portal watermark and are excluded from every publisher.
// They are read-only evidence — never reorder, remove or re-kind them (see core_media.js).
export async function listListingImages(listingId, { includeOriginals = false } = {}) {
    const inc = includeOriginals ? "&include=originals" : "";
    return coreApi(`listing/images?listing_id=${encodeURIComponent(listingId)}${inc}`);
}
export async function setListingImageKind(imageId, kind) {
    return coreApi(`listing/images/${imageId}`, { method: "PATCH", body: { kind } }); // { updated, kind }
}
export async function reorderListingImages(listingId, ids) {
    return coreApi("listing/images/order", { method: "PUT", body: { listing_id: listingId, ids } }); // { ok, count }
}
export async function removeListingImage(imageId) {
    return coreApi(`listing/images/${imageId}`, { method: "DELETE" }); // { removed }
}

// ── One-time fill-form links (core 0095/0096, server lib/core_fill.js) ────────
// The agent mints a token here; the CLIENT-facing page is /fill.html?t=<token>
// (a separate public Vite entry — never imports this file, so SCHED_KEY stays
// out of that bundle). agent_name is display-only (shown as "X invited you").
// A ten-minute signed url for one uploaded CV. The candidate row stores a storage PATH, not a
// link: the bucket is private because a CV carries a person's name, phone, address and work
// history, so every read is minted for the manager who asked, and expires.
export async function getCandidateCvLink(path) {
    return coreApi(`vacancy/cv-link?path=${encodeURIComponent(path)}`)   // { url }
}

export async function createFillLink({ kind, agent_id, agent_name, prefill }) {
    return coreApi("fill-link", {
        method: "POST",
        body: { kind, agent_id: agent_id || null, agent_name: agent_name || null, prefill: prefill || {} },
    }); // { id, token, kind, expires_at }
}
export async function listFillLinks(agentId = null) {
    // agentId optional (0156): omitted → the server lists the VERIFIED viewer's own
    // links (agents by agents.id, admins by their staff party + legacy auth-uid rows).
    return coreApi(`fill-links${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`); // { links: [{token,kind,status,prefill,...}] }
}

// AI paste → PENDING extract_listing proposal (the draft in the "AI drafts" list).
// Requires core migration 0044 applied. Nothing is committed until a human Saves.
// agent_id = provenance stamp (created_by_agent_id): without it the pending draft
// only appears in the admin/manager feed, never the creating agent's (2026-07-05).
export async function parseListingText({ raw_text, channel, agent_id, want_features = false }) {
    return coreApi("listing/parse", {
        method: "POST",
        // want_features: only the add-property form asks (server ignores it for facebook_group).
        body: { raw_text, channel, agent_id: agent_id || null, ...(want_features ? { want_features: true } : {}) },
    }); // { intake_id, proposal_id, draft, missing_fields, usage }
}

// saveMeetingProposal REMOVED 2026-07-24 (owner: "AI shouldn't intake meetings").
// It logged an audit-only schedule_meeting proposal for an AI-assisted meeting; with
// that path gone it had no callers. Historical schedule_meeting proposals still
// RENDER in Recent Entries — nothing writes another one. Meetings are created from
// core records via MeetingModal (core/MEETINGS_MODEL.md §3).

// ── Cooperation (agency↔listing-contact relationship, core/migrations/0046) ───
// Identity = property + contact party + transaction intent; participants and source
// listings are evidence, not identity (see the migration's header). All writes go
// through these four gateway RPCs — nothing else touches the `cooperation` tables.

export async function getCooperation(id) {
    return coreApi(`cooperation/${id}`); // { ...row, contact, participants[], listings[] }
}

// propertyId may be one id or an array — the Desk fetches cooperations for a whole page of
// listing cards in one go (property_id is the canonical access key; everything else is a
// filter dimension). Chunked at 80 ids per request: ~200 uuids in one query string pushes the
// URL past nginx's default 8KB request-line cap (same wall as the agent-listings link queries).
// ⚠️ There is NO "show me the numbers" parameter, deliberately. Cooperation figures are
// unlocked by a FACT the server checks for itself — whether this viewer has a meeting on
// that property (owner ruling 2026-08-01, core_cooperation.js meetingUnlockedProperties).
// A first cut keyed it on a `for_meeting` query param and the numbers appeared in the
// meeting modal while the property card for the SAME flat stayed locked. Don't reintroduce
// a client-supplied unlock: one fact, one answer, every surface.
export async function listCooperations({ propertyId, contactPartyId } = {}) {
    const propIds = Array.isArray(propertyId) ? propertyId.filter(Boolean) : (propertyId ? [propertyId] : [])
    if (propIds.length > 1) {
        const chunks = []
        for (let i = 0; i < propIds.length; i += 80) chunks.push(propIds.slice(i, i + 80))
        const results = await Promise.all(chunks.map(ids => {
            // with_contact: the owner counterparty's display_name — a cooperation is an
            // agent↔OWNER fact, and the UI must be able to name the owner (2026-07-29;
            // phone/email stay projection-masked server-side, the name is an agency fact).
            const q = new URLSearchParams({ property_id: ids.join(','), with_contact: '1' })
            if (contactPartyId) q.set('contact_party_id', contactPartyId)
            return coreApi(`cooperations?${q}`)
        }))
        return results.flatMap(r => r.cooperations || [])
    }
    const q = new URLSearchParams({ with_contact: '1' })
    if (propIds.length) q.set('property_id', propIds[0])
    if (contactPartyId) q.set('contact_party_id', contactPartyId)
    return (await coreApi(`cooperations?${q}`)).cooperations || []
}

// Desk home "ongoing deals": cooperations the agent actively participates in, with the
// property + contact summaries embedded. The agent is identified by their scheduler
// agents.id — the server resolves the bridged core party (0040) itself.
// Cooperations OPENED in a time window, by agent (Desk Activity rail diamonds, owner 2026-11-03):
// [{ id, at, agent_id (scheduler agents.id), property_id, listing_id, intent }]
export async function getCooperationsOpened({ from, to } = {}) {
    const q = new URLSearchParams({ from, to })
    return (await coreApi(`cooperations/opened?${q}`)).opened || []
}

export async function listMyCooperations(agentId, { status = 'negotiating,agreed', limit = 100 } = {}) {
    const q = new URLSearchParams({ participant_agent_id: agentId, status, with_property: '1', with_contact: '1', limit: String(limit) })
    return (await coreApi(`cooperations?${q}`)).cooperations || []
}

// Interactive party pre-check (core 0123, KNOWLEDGE_MODEL §6): existing parties matching a
// typed phone/email, with enough context (listings/requirements/cooperations counts) for
// the agent to decide. Their explicit pick — passed as person.party_id on commit (0124) —
// is the human verdict; silent reuse stays forbidden (0027).
export async function findPartyCandidates({ phone, email } = {}) {
    const q = new URLSearchParams()
    if (phone) q.set('phone', phone)
    if (email) q.set('email', email)
    if (![...q.keys()].length) return { candidates: [] }
    return coreApi(`party/candidates?${q}`)
}

// ─── Party dedup review (core 0125) ─────────────────────────────────────────
// Verdict = evidence, never a merge; party_cluster() is the only collapse
// surface. Queue is easy-confirms-first (identical names sort to the top).
export async function getPartyMergeCandidates({ limit = 50 } = {}) {
    return coreApi(`parties/merge-candidates?limit=${limit}`)
}
export async function getPartyDuplicates(partyId) {
    return coreApi(`party/${partyId}/duplicates`)
}
// B216.3 reader (0264): machine-surfaced office/shared-line candidates — numbers with
// >=3 holders and >=3 distinct real names. Management-gated server-side.
export async function getSharedLines() {
    return coreApi('parties/shared-lines')   // { lines: [{phone_norm, holder_count, distinct_names, our_agents, sample_names}] }
}
// agentId → bridged agent judge (scheduler_agent_id). adminToken → for an admin with
// no agents-table row (deliberate — the Tako/Mariam hijack): the admin's OWN scheduler
// session access_token, server-reverified against app_users.role (never a client claim).
// Same dual-identity shape as recordPropertyVerdict.
export async function recordPartyVerdict({ partyA, partyB, verdict, note, agentId, adminToken }) {
    return coreApi('party/verdict', { method: 'POST',
        body: { party_a: partyA, party_b: partyB, verdict, note: note || null,
                agent_id: agentId || null, admin_token: adminToken || null } })
}

// Find-or-create on the active (property, contact, intent) triple — re-opening an
// already-active cooperation returns existed:true and never touches its terms.
export async function openCooperation({ propertyId, contactPartyId, transactionIntent, terms, participants, listingId, status, notes }) {
    return coreApi('cooperation/open', {
        method: 'POST',
        body: { property_id: propertyId, contact_party_id: contactPartyId, transaction_intent: transactionIntent,
                terms, participants, listing_id: listingId, status, notes },
    }); // { cooperation_id, existed }
}

// Op-list revision (requirement-style): fields are the stable columns
// (commission_percent, exclusive, min_price, min_price_currency, valid_until,
// last_confirmed_at, notes) or 'terms.<key>' for policy-flavored conditions.
// ops: [{ op: 'set'|'clear', field, value }]
// participantPartyId (0122): when set, ops target THAT agent's OWN terms row
// (per-agent negotiated terms) instead of the shared parent; the server rolls the
// parent's effective view up from the most recently confirmed participant terms.
export async function amendCooperation(id, ops, participantPartyId = null) {
    return coreApi(`cooperation/${id}/amend`, { method: 'POST', body: { ops, participant_party_id: participantPartyId } }); // { cooperation_id, version }
}

// Evented lifecycle move: proposed | negotiating | agreed | rejected | ended.
export async function setCooperationStatus(id, status, reason) {
    return coreApi(`cooperation/${id}/status`, { method: 'POST', body: { status, reason } });
}

// ops: [{ op:'add', party_id, role?, commission_share? } | { op:'remove', party_id } |
//       { op:'set_share', party_id, commission_share }]
export async function updateCooperationParticipants(id, ops) {
    return coreApi(`cooperation/${id}/participants`, { method: 'POST', body: { ops } });
}

// ─── Deal closures (0086) ────────────────────────────────────────────────────
// A meeting closed the deal: records the sold/rented lifecycle observation on the
// listing, ends the matching active cooperation(s) with reason deal_closed, and
// emits deal.closed per closing agent — the ranking's points source.
// body: { outcome:'sold'|'rented_out', scheduler_agent_ids:[uuid], viewing_id?,
//         commission_amount?, commission_currency? }
// Close a deal ON A PROPERTY with an EXPLICIT ad (2026-07-30 review ruling). The UI's anchor
// is a property; record_deal_closure is keyed on a LISTING because it writes that listing's
// lifecycle. The server NEVER bridges that gap by inference — a property legitimately carries
// expired ads, several agencies' postings, rent AND sale, reposts and duplicates, so choosing
// one automatically would let an algorithm decide which agent gets the attribution.
// No listing_id → 422 { error:'listing_required', candidates:[...] } and a human picks.
// viewer_agent_id (2026-08-01): only so each candidate comes back with `is_mine` — the
// picker groups yours vs other agents' ads. A LABEL, never a filter: every ad on the
// property is still returned and still pickable.
export async function getDealCloseCandidates(propertyId) {
    const q = _viewerAgentId ? `?viewer_agent_id=${encodeURIComponent(_viewerAgentId)}` : ''
    return coreApi(`property/${propertyId}/deal-close-candidates${q}`); // { candidates:[...] }
}
// CORRECTING a closed deal's figures (0729 door, owner 2026-08-26). Not for filling a gap —
// the close requires both figures since 0728 — for the one that was wrong. The server events
// the change and, when the FEE moves, re-issues the ranking credit at the new amount.
export async function correctDealFigures(dealId, body) {
    return coreApi(`deal/${dealId}/figures`, { method: 'PATCH', body });
}

// A KNOWN deal for the market record (statistics line, owner 2026-10-07) — a sale / rental we LEARNED of (owner's word,
// another agency's deal), NOT our own close (that is reportPropertyDealClosure below). Server: core_deals.js
// POST /api/core/property/:id/confirmed-deal → core's confirmed_deal_record_v1. body: { deal, price, currency, closed_on?,
// source?, evidence? }. Answers { id, price_usd } or a 400 { error, field }.
export async function recordConfirmedDeal(propertyId, body) {
    return coreApi(`property/${propertyId}/confirmed-deal`, { method: 'POST', body });
}

export async function reportPropertyDealClosure(propertyId, body) {
    return coreApi(`property/${propertyId}/deal-closed`, { method: 'POST', body });
}

export async function reportDealClosure(listingId, body) {
    return coreApi(`listing/${listingId}/deal-closed`, { method: 'POST', body });
}

// Freeze the commission inputs on every closed deal that still has none (admin only, server
// route POST /api/core/deals/stamp-missing). The server computes each deal's links AS OF ITS
// OWN close date and leaves any column already stamped alone, so a hand-corrected override
// survives and re-running changes nothing — it is idempotent by construction.
//
// ⚠️ WHY A BUTTON EXISTS AT ALL. The endpoint shipped with NO caller: not a cron, not a
// control, nothing anywhere in the client or the server tree (searched 2026-09-20). A deal
// whose links are NULL is therefore recomputed by the ledger on every read instead of being
// frozen — correct on screen, but it silently MOVES if coverage or rates are ever edited
// again, which is the whole thing a snapshot exists to prevent.
export async function stampMissingDealSnapshots() {
    return coreApi('deals/stamp-missing', { method: 'POST' }); // { ok, swept, stamped, deals[] }
}

// Undo a close (0304/0305). The two reasons are NOT interchangeable and the UI must not blur
// them, because they differ in whether the agent keeps the ranking credit:
//   deal_fell_through   — the deal happened, then collapsed. It stays in the listing's history.
//   recorded_by_mistake — the close never happened. The observation is invalidated and its
//                         deal.closed credit is retracted by annotation.
// Only the party who RECORDED the close may reopen it (403 otherwise) — they are the one who
// learns it came undone, and anyone else reversing it would overwrite a colleague's attribution.
// body: { reason, note?, scheduler_agent_id }
export async function reopenListing(listingId, body) {
    return coreApi(`listing/${listingId}/reopen`, { method: 'POST', body });
}
// Closed deals ONE ROW PER DEAL, with the scheduler agents on each (2026-08-29). THE deal
// source for both the Stats tab and the Desk leaderboard, via lib/statsModel.buildLedger.
// (getDealClosures / GET /api/core/deal-closures was removed from this client on 2026-09-06:
// it aggregated by core party off the event stream, which emits one deal.closed per closing
// agent, so a co-broked deal's whole commission landed once per co-broker — a ~27%
// overstatement as income — and its party key could only reach a scheduler agent through the
// activity rows. The server endpoint still exists; nothing in this app calls it.)
// → { deals: [{ deal_id, closed_at, commission_amount, commission_currency,
//              agents: [{scheduler_agent_id, roles}], lead_scheduler_agent_id,
//              legacy_viewing_id }], count, unbridged }
export async function getAgentDeals(since, until) {
    const p = new URLSearchParams();
    if (since) p.set('since', since);
    if (until) p.set('until', until);
    const q = p.toString();
    return coreApi(`agent-deals${q ? `?${q}` : ''}`);
}

// Official daily USD→GEL rate (NBG, via our server — one upstream fetch/day,
// last-good persisted server-side). → { rate, rate_date, stale } — stale:true
// means today's refresh failed and this is the previous good value.
// The market's own price ranges (core 2808/3066: 0.5th/50th/99.5th percentile of live asking prices, USD, per deal and kind),
// served under /api/core/ for the Desk (CORS). The typed search reads the rent ceiling from it (searchCompiler v39).
export async function getPriceSense() {
    const d = await coreApi('price-sense');
    if (!Array.isArray(d?.ranges)) throw new Error(d?.error || 'price sense: no ranges');
    return d;
}

export async function getUsdGelRate() {
    // ⚠️ /api/fx/ is NOT under /api/core/, so it needed its own scope in the server's
    // viewer-token middleware (B53 2c) — its guard otherwise falls through to requireAuth,
    // which expects an Authorization: Bearer header the Desk never sends. Symptom if that
    // scope is ever narrowed again: the header's GEL/USD rate silently 401s.
    const token = await viewerToken()
    const r = await fetch(`${SNTV_API}/api/fx/usd-gel`, {
        headers: { ...(token ? { 'x-viewer-token': token } : {}) },
    });
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || `fx ${r.status}`);
    return data;
}

// Full-window per-agent activity counts (0087 activity_ranking RPC) — the Desk
// ranking's data source; the old client-side tally only saw the loaded feed page.
// → { ranking: [{ party_id, display_name, scheduler_agent_id, reshares, scraped }] }
export async function getActivityRanking(since, until) {
    const p = new URLSearchParams({ since });
    if (until) p.set('until', until);
    return coreApi(`activity-ranking?${p}`);
}

// ── Rental terms + owner restrictions (listing_terms 0010, evented gateway 0055) ──
// The row is the current working interpretation; history = event stream (verb
// listing_terms.amended). Write path is amend_listing_terms ONLY — a direct table
// write anywhere trips the 0055 governance trigger (ungoverned_write = bypass alarm).
// tenant_pref content is OWNER-ASSERTED, agent-visibility only — never a filter.

export async function getListingTerms(listingId) {
    return coreApi(`listing/${listingId}/terms`); // { terms: row|null }
}

// ops: [{ op:'set'|'clear', field, value }] — plain columns by name, or
// 'tenant_pref.<key>' / 'utilities_included.<key>'. Unknown fields raise server-side.
// Batch terms+stances for many listings — the lineage modal's per-agent RENTAL
// RESTRICTIONS rows (each agent's reshare is its own listing row, 0041).
export async function getListingTermsBatch(listingIds) {
    const ids = [...new Set((listingIds || []).filter(Boolean))];
    if (!ids.length) return { terms: [], policies: [] };
    return coreApi(`listing-terms-batch?ids=${ids.map(encodeURIComponent).join(',')}`);
}

// Structured owner tenant-acceptance stances (listing_policy, 0018) — subject is a
// tenant_terms feature code, stance ∈ allowed|not_allowed|preferred|only. The
// supply-side mirror of requirement_criteria.household_codes (0076).
// ── restriction RULES (core 0398–0403) ───────────────────────────────────────
// The structured successor to the flat stances below. These ADD to them: the read
// serves real rules AND legacy listing_policy stances projected through
// restriction_rule_v, so a listing that has only ever been chipped still renders.
//
// Vocabulary is static reference data — cached for the session like getFeatures(), and
// for the same reason: the terms modal opens often and the axis lists never change
// mid-session. Cache the in-flight PROMISE so concurrent openers share one request; a
// failure clears it so the next call retries rather than caching the miss.
let _restrictionVocabPromise = null;
export async function getRestrictionVocab() {
    if (!_restrictionVocabPromise) {
        _restrictionVocabPromise = coreApi('restriction-vocab')
            .catch(e => { _restrictionVocabPromise = null; throw e; });
    }
    return _restrictionVocabPromise;
}
// { nationality: { foreigner: { Indian: 'definite' } } } — the shape subsumes() and the
// sentence builder consume. Derived from the same vocab payload so there is one fetch.
export async function getAxisExpansion() {
    const v = await getRestrictionVocab().catch(() => null);
    const map = {};
    for (const e of v?.expansion || []) {
        if (!e?.axis_code) continue;
        ((map[e.axis_code] ||= {})[e.parent_value] ||= {})[e.child_value] = e.tier;
    }
    return map;
}
// F-039 (2026-09-05): ONE request per tick, not one per card. Every card face on a page asked
// `listing/<id>/rules` on mount — 28 requests per Desk load, ~350 ms of server time each, 26 bytes
// each. Callers are unchanged (they still ask for one listing); the ids that arrive within the same
// microtask are collected and answered by `listing-rules-batch` in one round trip, partitioned back
// per id. A server that does not know the batch route yet (404) falls back to the per-id route, so
// deploy order between bundle and lib is free. Shared layer, so every caller benefits:
// CooperationChips (the card face), matchBands, RestrictionRules.
const RULES_BATCH_MAX = 80
let rulesBatch = null   // { ids: Set, resolvers: Map<id, [{resolve, reject}]> }
let rulesBatchRouteMissing = false
async function flushRulesBatch() {
    const b = rulesBatch; rulesBatch = null
    if (!b) return
    const ids = [...b.ids]
    const settle = (id, ok, v) => { for (const p of (b.resolvers.get(id) || [])) (ok ? p.resolve : p.reject)(v) }
    for (let i = 0; i < ids.length; i += RULES_BATCH_MAX) {
        const chunk = ids.slice(i, i + RULES_BATCH_MAX)
        try {
            if (rulesBatchRouteMissing) throw Object.assign(new Error('batch route missing'), { status: 404 })
            const r = await coreApi(`listing-rules-batch?ids=${chunk.map(encodeURIComponent).join(',')}`)
            for (const id of chunk) settle(id, true, (r && r.by_listing && r.by_listing[id]) || { rules: [], hidden: r?.hidden })
        } catch (e) {
            if (e && e.status === 404) {
                rulesBatchRouteMissing = true   // older lib: per-id route, until the next page load
                await Promise.all(chunk.map(id => coreApi(`listing/${id}/rules`).then(v => settle(id, true, v), err => settle(id, false, err))))
            } else for (const id of chunk) settle(id, false, e)
        }
    }
}
export function listRestrictionRules(listingId) {
    return new Promise((resolve, reject) => {
        if (!rulesBatch) { rulesBatch = { ids: new Set(), resolvers: new Map() }; queueMicrotask(flushRulesBatch) }
        rulesBatch.ids.add(listingId)
        if (!rulesBatch.resolvers.has(listingId)) rulesBatch.resolvers.set(listingId, [])
        rulesBatch.resolvers.get(listingId).push({ resolve, reject })
    })   // { rules: [...], hidden } — the same shape the per-id route returns
}
// predicate is {axis: [values]} — OR inside an axis, AND across axes. `verbatim` is what
// the owner actually said and is expected to be EMPTY most of the time (tenant_pref.notes
// is filled on 0 of 651 rows — agents do not type), so nothing here requires it.
// ⚠️ agentId IS NOT OPTIONAL DECORATION — it is what makes the retract guard work, and
// dropping it cost a real restriction (2026-08-07). Both callers in RestrictionRules.jsx
// have always passed it; these two functions silently ignored it, so every rule written
// from the Desk landed on the zero "recorder unknown" sentinel — which the retract
// endpoint treats as belonging to nobody and lets ANY agent withdraw. One did.
// The same `recorded_by_agent_id` spelling the policy endpoint uses; the server accepts
// both spellings through one resolver (0221). See core migration 0475.
export async function addRestrictionRule(listingId, { polarity, predicate, appliesTo, verbatim, note, negotiable }, agentId) {
    return coreApi(`listing/${listingId}/rule`, { method: 'POST',
        // 0927 `negotiable` — the owner refuses but will discuss it. Sent only when true, so a
        // caller that has never heard of it produces the identical request it always did.
        body: { polarity, predicate, applies_to: appliesTo, verbatim, note,
                negotiable: negotiable === true ? true : undefined,
                recorded_by_agent_id: agentId || undefined } });
}
// ── A PER-AXIS CLEARANCE (core 0739) ────────────────────────────────────────────────────
// "ნებისმიერი" on an axis records that the owner places NO limit there — a fact
// restriction_rule cannot hold, because assert_restriction_rule refuses an empty value array
// and every axis is a value enum with no "any" member. It is a different object:
// listing_restriction_clear, whose `axes` column scopes it.
// ⚠️ NOT THE SAME AS THE BLANKET CLEARANCE. `axes: null` means "an agent asked and the owner
// said none" across all 38 subjects in restriction_clear_scope — pets, children, smoking,
// tenant type, every nationality. This call always names its axes, so it can never be mistaken
// for that: passing an empty list is a client bug and the gateway rejects it.
export async function clearRestrictionAxes(listingId, axes, note, agentId) {
    const list = (axes || []).filter(Boolean);
    if (!list.length) throw new Error('clearRestrictionAxes: name at least one axis');
    // One door: the existing restrictions-clear route (core_listing_terms.js), which since
    // 0747 accepts `axes` — a second near-identical route would be the two-definitions disease.
    return coreApi(`listing/${listingId}/restrictions-clear`, { method: 'POST',
        body: { clear: true, axes: list, note, recorded_by_agent_id: agentId || undefined } });
}
// Retract, never delete — the row is a recorded observation. Only your own rules (plan A:
// agents stopped overwriting each other when stances gained a recorder); a 404 here means
// the rule is someone else's, which the UI must show rather than swallow.
export async function retractRestrictionRule(listingId, ruleId, agentId) {
    return coreApi(`listing/${listingId}/rule/${ruleId}/retract`, { method: 'POST',
        body: { recorded_by_agent_id: agentId || undefined } });
}

export async function listListingPolicies(listingId) {
    return coreApi(`listing/${listingId}/policies`); // { policies: [{subject, stance, source_kind, ...}] }
}
// agentId (plan A, 2026-07-15) → recorded_by_agent_id: attributes the stance to its
// recorder so the Desk panel shows it under THEIR name (scheduler agents.id — the
// created_by_agent_id pattern; the shared x-scheduler-key can't identify the agent).
// Omitted/null → server stamps the zero-uuid "recorder unknown" legacy sentinel.
export async function setListingPolicy(listingId, subject, stance, note, agentId) {
    return coreApi(`listing/${listingId}/policy`, { method: 'POST', body: { subject, stance, note, recorded_by_agent_id: agentId || undefined } });
}
// B237 — "asked the owner, and there are no restrictions". NOT a stance: there is no
// subject, because the claim is about the whole listing and is expanded across the
// in-scope vocabulary at READ time (core 0313/0315). Recording it is what makes silence
// mean only one thing — before this, a listing with no restrictions and a listing nobody
// had asked about were the same absence.
// clear:false RETRACTS. The explicit retract exists because prefill without one makes
// withdrawal silently fail: the agent unticks, nothing is sent, and a stale claim keeps
// answering for the owner. Server-side the retraction is a timestamp, never a delete.
export async function setRestrictionsClear(listingId, clear = true, { note, reason } = {}) {
    return coreApi(`listing/${listingId}/restrictions-clear`, { method: 'POST', body: { clear, note, reason } });
}
// B586 — "we asked the owner about this and they wouldn't say". The FOURTH state, and a
// different KIND from the three above: a stance says what the owner allows, a clear says
// nothing is restricted, and both are answers about the FLAT. This one is a fact about the
// PHONE CALL, so it changes what we ask and never what we know.
//
// ⚠️ It is not a matching input and must never become one. The subject stays unknown to the
// scorer, because after a decline it IS unknown — reading it as "does not apply" would walk
// a client into a viewing refused at the door, which is the inversion the 2026-08-04
// restrictions work exists to prevent. Its only consumer is the owner strip, which stops
// offering the question as the next call. Core 0508 carries the full account.
//
// `subject` is a `pol_*` feature code OR a listing_terms column name — the same space
// ownerAnswerKind() already treats as answerable; the gateway validates against both and
// refuses a typo rather than silently retiring a question forever.
// declined:false RETRACTS (the owner may answer later); retraction is a timestamp, never a
// delete, and the explicit op exists because prefill without one makes withdrawal fail
// silently.
export async function setAnswerDeclined(listingId, subject, declined = true, { note, reason, agentId } = {}) {
    return coreApi(`listing/${listingId}/answer-declined`, { method: 'POST',
        body: { subject, declined, note, reason, recorded_by_agent_id: agentId || undefined } });
}
// Supply-side single-feature capture (B42) — records ONE property amenity's presence
// through the commit_feature gateway (evented + attributed, core migration 0126):
// present true = has it, false = confirmed absent (never sent as a default — omit to
// leave unknown). agentId → the acting agent's core party; sourceKind 'agent' (the
// agent's own observation, conf 0.50) or 'owner' (relayed from the owner, 0.70).
export async function setPropertyFeature(propertyId, featureKey, present, agentId, sourceKind = 'agent') {
    return coreApi(`property/${propertyId}/feature`, { method: 'POST', body: { feature_key: featureKey, present, agent_id: agentId || undefined, source_kind: sourceKind } });
}

// agentId (0221) = the scheduler agents.id of whoever is recording, so the row can say
// WHO — same argument, same id space as setListingPolicy's recorder. Optional: omitted
// leaves any existing recorder untouched server-side rather than anonymising the row.
export async function amendListingTerms(listingId, ops, agentId = null) {
    return coreApi(`listing/${listingId}/terms/amend`, { method: 'POST', body: { ops, recorded_by: agentId || undefined } }); // { listing_id, version, created }
}

// ── Property contact resolution (property → ranked action targets) ────────────
// "Who do I call to move this deal?" — a deterministic fallback chain over existing
// evidence (cooperation contact → non-agent listing author → origin-ad author →
// manual contact_of edges). Bridged-agent parties come back labeled (is_agent) and
// are never default_party_id — the listing author being our own agent (their reshare)
// is correct data but the wrong person to call about the property.
// Ownership rule (2026-07-12): when the viewer is a plain AGENT and a property's origin ad
// was scraped by a DIFFERENT agent, the server withholds the owner's contact and returns the
// OWNING AGENT's instead (via_agent: true) — deals route agent → agent → owner. The viewer is
// registered ONCE at the Desk root (setContactViewerAgentId: agent id, or null for admin/GM)
// so every getPropertyContacts call site — card chips, meeting seeds, modals, future ones —
// is covered without threading the id through each component.
let contactViewerAgentId = null;
export function setContactViewerAgentId(id) { contactViewerAgentId = id || null; }
export async function getPropertyContacts(propertyId, { agentId = contactViewerAgentId } = {}) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
    return coreApi(`property/${propertyId}/contacts${q}`); // { contacts: [{party_id,name,phone,email,role,evidence,rank,is_agent,via_agent?}], default_party_id }
}

// WhatsApp reach of numbers already on screen (core phone_reach, owner 2026-11-03): { reach: { "<last 9 digits>": "no"|"yes" } }.
// "unknown" is left out. At most 50 numbers per call.
export async function getPhoneReach(phones) {
    const nine = [...new Set((phones || []).map(p => String(p || '').replace(/\D/g, '').slice(-9)).filter(x => x.length === 9))].slice(0, 50)
    if (!nine.length) return { reach: {} }
    return coreApi(`phone-reach?p=${nine.join(',')}`)
}

// B20 timeline spine: the event feed for one property ENTITY (property + its
// listings + its cooperations), newest first. Keyset paging: pass the last
// event's id as `before` to load older. Raw events — rendering is the UI's job.
//
// propertyId accepts an ARRAY (or a comma-joined string) — an identity cluster
// is several property rows (2026-07-27), and the lineage modal needs the whole
// family's stream, not the anchor row's quarter of it. A single id is unchanged.
// The server caps the list; ids are joined raw because they are uuids (the
// comma is the separator, so it must not be percent-encoded away).
export async function getPropertyEvents(propertyId, { limit = 100, before } = {}) {
    const ids = (Array.isArray(propertyId) ? propertyId : String(propertyId).split(','))
        .map(x => String(x || '').trim()).filter(Boolean)
    const q = new URLSearchParams({ limit: String(limit) })
    if (before) q.set('before', String(before))
    return coreApi(`property/${[...new Set(ids)].map(encodeURIComponent).join(',')}/events?${q}`); // { events, has_more, listings: {id:{source}} }
}

// Whole-cluster aggregate behind the history timeline's pinned header. SEPARATE
// from getPropertyEvents on purpose: the stream is paged, so any count derived
// from it would describe one page and claim to describe the property. Same
// cluster-id list as getPropertyEvents. Cooperation floors arrive null with
// floor_visible:false when the viewer hasn't earned them — the FACT of an
// agreement is never hidden, only its numbers.
export async function getPropertyHistorySummary(propertyId) {
    const ids = (Array.isArray(propertyId) ? propertyId : String(propertyId).split(','))
        .map(x => String(x || '').trim()).filter(Boolean)
    if (!ids.length) return null
    return coreApi(`property/${[...new Set(ids)].map(encodeURIComponent).join(',')}/history-summary`);
}

// Manual contact declaration: party --contact_of--> property link edge (0016 graph),
// role in evidence jsonb (owner | neighbor | keyholder | broker | relative | other).
// Resolves/creates the party from phone/email via resolve_party unless partyId is given.
export async function addPropertyContact(propertyId, { name, phone, email, role, partyId, note, editLinkId } = {}) {
    return coreApi(`property/${propertyId}/contact`, {
        method: 'POST',
        // agent_id = the declarer stamp (2026-07-29): declared contacts are PRIVATE to the
        // agent who added them (their prospective client) — same session viewer id the GET uses.
        // edit_link_id (review 2026-09-23): the declarer's own link being edited, so an edit keeps
        // the same person instead of minting a new one.
        body: { name, phone, email, role, party_id: partyId, note, edit_link_id: editLinkId || undefined, agent_id: contactViewerAgentId || undefined },
    }); // { party_id, link_id, existed }
}

// Dismiss (soft-remove) a manual agent-declared contact edge — only rows the resolver
// returned WITH a link_id are removable; derived contacts (cooperation/author/origin)
// have no edge of their own. Edit = remove + addPropertyContact with the new values.
export async function removePropertyContact(linkId) {
    return coreApi(`property/contact-link/${linkId}`, { method: 'DELETE' }); // { ok: true }
}

// ── Property identity graph v1 (core migration 0063, server lib core_identity.js) ──
// "Are these two property rows the same physical apartment?" A verdict is
// EVIDENCE (reversible, judge-stamped), never a destructive merge — the server
// resolver maintains same_as/different_from edges and property_cluster is the
// only collapse surface. Evidence comes back as SENTENCES, never scores.

export async function getPropertyDuplicates(propertyId) {
    return coreApi(`property/${propertyId}/duplicates`); // { property, candidates[], cluster:{members,contested}, verdicts[] }
}

// The global review queue (pending duplicate-candidate pairs, newest first).
export async function getDuplicateQueue({ status = 'pending', limit = 50 } = {}) {
    return coreApi(`identity/candidates?status=${status}&limit=${limit}`); // { candidates[] }
}

// verdict: 'same' | 'different' | 'unsure'. agent = the logged-in Desk agent
// ({ id, name, email, phone }) — REQUIRED: anonymous verdicts are rejected
// server-side (every verdict must carry its judge; same lesson as 0051).
// Re-judging the same pair by the same agent is an upsert (safe to change your mind).
//
// adminToken: for an admin with no agents-table row (deliberate — see
// memory/agent-login-hijack-stale-authlink, that coupling caused the Tako/Mariam
// hijack). Pass the admin's OWN Supabase access token (session.access_token) —
// the server re-verifies it against the scheduler project + app_users.role
// itself, it never trusts a client-side "I'm admin" claim.
export async function recordPropertyVerdict({ propertyA, propertyB, verdict, note, agent, adminToken }) {
    return coreApi('property/verdict', {
        method: 'POST',
        body: {
            property_a: propertyA, property_b: propertyB, verdict, note,
            agent_id: agent?.id, agent_name: agent?.name, agent_email: agent?.email, agent_phone: agent?.phone,
            admin_token: adminToken,
        },
    }); // { verdict_id, cluster_member_ids, contested }
}

// ── Complex-link review (B122, 2026-07-23) ─────────────────────────────────────
// Complex lookup for the criteria editor (0381 follow-up): `q` type-aheads over
// the 132 rows, `ids` turns a requirement's stored complex_codes back into names.
// Both go through the same endpoint because a picker that cannot name what is
// already selected shows uuids, and a field showing uuids is read-only in practice.
export async function searchComplexes(q) {
    if (!q || q.trim().length < 2) return [];
    return (await coreApi(`complexes?q=${encodeURIComponent(q.trim())}`)).complexes || [];
}
// 0729: one developer's every project — what the search box's "all Archi (34)" family
// row commits. Grouped by the developer COLUMN server-side, never by name-prefix.
export async function getComplexesByDeveloper(developer) {
    if (!developer) return [];
    return (await coreApi(`complexes?developer=${encodeURIComponent(developer)}`)).complexes || [];
}
// B935: street NAMES for a URL-restored filter chip. The chip's only other label source is
// the facet counts, which the server deliberately omits while a street filter is ON — so a
// shared link's chip could never get a name. Mirror of getComplexesByIds.
export async function getStreetsByIds(ids) {
    const list = [...new Set((ids || []).filter(Boolean))].slice(0, 50);
    if (!list.length) return [];
    return (await coreApi(`street-catalog?ids=${list.join(',')}`)).streets || [];
}
// Complex header thumbs (owner 2026-09-10): the linked flats of ONE development, as property
// ids only. Photos then come from getPropertyThumbs — the same ladder the cards use — so the
// header's strip and the card strips can never disagree on which rendition is shown.
export async function getComplexProperties(complexId, limit = 24) {
    if (!complexId) return [];
    return (await coreApi(`complex/${complexId}/properties?limit=${limit}`)).property_ids || [];
}
export async function getComplexesByIds(ids) {
    const list = [...new Set((ids || []).filter(Boolean))].slice(0, 50);
    if (!list.length) return [];
    return (await coreApi(`complexes?ids=${list.join(',')}`)).complexes || [];
}

// What development is THIS flat in? (0726, owner ruling 2026-08-26.)
// The ONE reader — server-side it is property_complex_view_v1, cluster-aware in SQL.
// Returns { linked[], candidates[] }: `linked` is what the linker or a human has
// ESTABLISHED, each row carrying the `basis` that established it ('human' > 'ad_named' >
// 'proximity') and `cluster_rows`; `candidates` are undecided nearby developments, i.e.
// open questions, and never a pair anything has already decided.
// ⚠️ These are CORE complex ids. The listing used to ask the SCHEDULER's separate
// `complexes` table (133 rows against core's 332) and drew a banner only on an agent
// `yes` — of which there were none, ever, so 1,802 linked rows had no display path.
// ⚠️ `distance_m` is report-only. COMPLEX_MATCHING.md: distance settles nothing in either
// direction — a correct link at 1,054 m and a wrong one at 1,087 m are both on record, and
// the discriminator is the preposition, which lives in the AI prompt.
export async function getPropertyComplex(propertyId) {
    if (!propertyId) return { linked: [], candidates: [] };
    return coreApi(`property/${propertyId}/complex`);
}

// The complex linker's AI leaves undecidable (property, complex) pairs as
// status='ambiguous' — this queue is the human half, one group per property
// (address + description + candidates + the AI's own hedge note). Management only.
export async function getComplexReviewQueue(limit = 60) {
    return coreApi(`complex-review/queue?limit=${limit}`); // { groups[], pending_total }
}

// complexId = link that complex (siblings auto-reject); null = "none of these".
// Same judge duality as recordPropertyVerdict: bridged agent row, or the admin's
// own verified session token — never a client-side admin claim.
export async function recordComplexVerdict({ propertyId, complexId, note, agent, adminToken }) {
    return coreApi('complex-review/verdict', {
        method: 'POST',
        body: { property_id: propertyId, complex_id: complexId || null, note, agent_id: agent?.id, admin_token: adminToken },
    }); // { ok, linked, pairs_decided }
}

// ── Far links: a kilometre or more from their own development (0727) ──────────
// Owner ruling 2026-08-26, choosing among three options: review queue yes, re-ask the
// AI no, apply a radius no. So this SAMPLES; it decides nothing.
// ⚠️ Distance is not the verdict and must never be rendered as one. COMPLEX_MATCHING.md
// records a correct link at 1,054 m and a wrong one at 1,087 m — the discriminator is the
// PREPOSITION, which is why every row carries the sentence from the advert that names the
// development. Read the sentence, not the metre.
// Grouped by complex, because `far_links` beside `total_links` is the whole insight:
// 14-of-16 means the PIN is wrong (one decision), 7-of-47 means those links are wrong.
export async function getFarComplexLinks({ minM = 1000, limit = 200 } = {}) {
    return coreApi(`complex-review/far?min_m=${minM}&limit=${limit}`);
    // { groups: [{ complex_id, name, far_links, total_links, farthest_m, properties[] }], … }
}

// action: 'keep' | 'unlink' | 'pin_wrong'. Omitting propertyIds means every undecided far
// link of that development — what the group-level button sends.
// ⚠️ `pin_wrong` exists because it is the right answer for most of this queue and neither
// other verb can say it: unlinking a development's fourteen flats to hide one bad
// coordinate destroys fourteen correct links, and keeping them leaves it 4 km from every
// flat in it. It touches no link — it flags the complex for the pin work (0408/0723).
export async function recordFarComplexVerdict({ complexId, action, propertyIds, note, agent, adminToken }) {
    return coreApi('complex-review/far-verdict', {
        method: 'POST',
        body: { complex_id: complexId, action, property_ids: propertyIds || undefined,
                note, agent_id: agent?.id, admin_token: adminToken },
    }); // { ok, action, links_touched }
}

// ── Photo-conflict review queue (0389, PUBLICATION_AND_OWN_LISTINGS step 4) ────
// listing_candidate = machine-captured cross-cluster photo-identity pairs (raw
// measurements, no verdicts). The Review tab shows unjudged pairs; a verdict is a
// match_verdict row (same|different|unsure) — a human label for the future decider,
// NEVER an auto-merge (0020). Management only, same surface as the other queues.
// ── Canonical property face review (core 1309, core/CANONICAL_PROPERTY_FACE.md §3) ──
// Admin only. Price: a represented flat whose publications quote different prices shows NO
// price publicly until a human names one publication as the price authority for that intent.
// Splits: a public pid that split never redirects on its own — a human names the continuing
// child (its old URL then 301s there) or declines (different flats; the old URL stays a split).
export async function getPublicPriceReviewQueue(limit = 60) {
    return coreApi(`public-face-review/price?limit=${limit}`); // { items[], authorities[], pending_total }
}
export async function setPublicPriceAuthority({ pid, intent, publicationId, note, agent, adminToken }) {
    return coreApi('public-face-review/price-authority', {
        method: 'POST',
        body: { pid, intent, publication_id: publicationId, note, agent_id: agent?.id, admin_token: adminToken },
    }); // { ok, authority_id }
}
export async function getPublicSplitQueue(limit = 60) {
    return coreApi(`public-face-review/splits?limit=${limit}`); // { items[], pending_total }
}
// childPid = the flat that continues the split pid; null = decline (no successor).
export async function recordPublicContinuity({ splitPid, childPid, note, agent, adminToken }) {
    return coreApi('public-face-review/continuity', {
        method: 'POST',
        body: { split_pid: splitPid, child_pid: childPid || null, note, agent_id: agent?.id, admin_token: adminToken },
    }); // { ok, continued, event_id }
}

// Cleaned photos (core 3005): the PC cleaner's copies of front-page photos with text/numbers/logos removed.
// status 'pending' = waiting for approval; 'approved' = live (taking one down = reject -> the original shows again).
export async function getCleanedPhotos(status = 'pending', limit = 60) {
    return coreApi(`public-face-review/cleaned?status=${status === 'approved' ? 'approved' : 'pending'}&limit=${limit}`); // { items[], pending_total, approved_total }
}
// action: 'approve' | 'reject' | 'approve_all' (sourceHash/cleanHash ignored)
export async function decideCleanedPhoto({ sourceHash, cleanHash, action, agent, adminToken }) {
    return coreApi('public-face-review/cleaned/decide', {
        method: 'POST',
        body: { source_hash: sourceHash, clean_hash: cleanHash, action, agent_id: agent?.id, admin_token: adminToken },
    }); // { ok, changed }
}

export async function getPhotoConflictQueue(limit = 40) {
    return coreApi(`listing-candidates?limit=${limit}`); // { pairs[], pending_total }
}
export async function recordPhotoConflictVerdict({ listingA, listingB, verdict, note, agent }) {
    return coreApi('listing-candidates/verdict', {
        method: 'POST',
        body: { listing_a: listingA, listing_b: listingB, verdict, note, agent_id: agent?.id },
    }); // { ok }
}

// ── Agent property layer v1 (core migration 0064, server lib core_agent_property.js) ──
// The agent's own VERSION of a property — a projection, never a copy:
//   own claim > endorsement (accepted canonical, PINNED) > canonical.
// Endorsements carry ZERO evidentiary weight (adoption, not corroboration) and
// PIN the value: canonical drifting off the pin surfaces as drift, never a
// silent change. Notes are private to the agent (+admin), never on the timeline.

export async function getAgentProperties(agentId) {
    return (await coreApi(`agent-properties?agent_id=${encodeURIComponent(agentId)}`)).properties || [];
}

// Org-wide same_as cluster map: { property_id: cluster_key }, non-singleton components only.
// For admin/manager Desk (scope='all') — agent-properties above is single-agent-scoped, so an
// admin viewing other agents' listings had no cluster keys and one flat scraped by two agents
// rendered as two un-stacked cards (2026-07-08).
export async function getPropertyClusters() {
    return (await coreApi('property-clusters')).clusters || {};
}

// agentId is OPTIONAL. It used to be interpolated unconditionally, so an admin — who has no
// roster row and therefore no agent id — sent `agent_id=null` and the server resolved nobody.
// Omitted, the server falls back to resolving the viewer from their session (core_agent_property
// callerParty), which is the door that knows about accounts with no roster row.
export async function getMyPropertyVersion(propertyId, agentId) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
    return coreApi(`property/${propertyId}/my-version${q}`);
    // { facts: {attr:{state:'own'|'accepted'|'unendorsed', value, canonical, canonical_source, drift}}, endorsements[], notes[], in_inventory }
}

// What the reshare tab hands the extension for a repost (B244, lib/core_reshare_payload.js).
// Assembled server-side from the ORIGIN capture, overlaid with this agent's own edited
// version (state==='own'), then AI-enhanced exactly as the extension does it.
// ⚠️ This is the HANDOVER, not a prediction of the final portal form — the extension still
// applies forced chips, the description-cleaner, the field→description fallback and the
// per-platform property-type selection on top. Render it as "what we send", never as
// "what korter will show", or the preview makes a promise nothing can keep.
// AI runs server-side on this call, so it is slower than a plain read on first open for a
// given listing (the ai_cache key folds in current field values, so the extension's earlier
// analysis of the same ad usually does NOT hit). Callers should show a loading state.
// Remote share phase 1 (2026-10-08): the caller's own browsers running the extension, and the
// portal accounts each is logged into. { sessions: [{ device_id, device_name, ext_version,
// online, last_seen, portals: { ss|myhome|korter: { logged_in, name, phone_tail } } }] }
export async function getExtSessions() {
    return coreApi('ext-sessions')
}

// Remote share phase 2: queue a reshare for one of the caller's own online browsers, then poll it.
// job.state: queued → claimed → running → filled (form ready, waiting to publish) | error | expired
export async function queueExtJob(deviceId, target, payload) {
    return coreApi('ext-jobs', { method: 'POST', body: { device_id: deviceId, target, payload } })
}
export async function getExtJob(id) {
    return coreApi(`ext-jobs/${encodeURIComponent(id)}`)
}

// Scheduled shares (server/lib/share_plans.js, core 3135).
export async function createSharePlan(plan) {
    return coreApi('share-plans', { method: 'POST', body: plan })
}
export async function getSharePlans(listingId) {
    return coreApi(`share-plans?listing_id=${encodeURIComponent(listingId)}`)
}
export async function cancelSharePlan(id) {
    return coreApi(`share-plans/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} })
}
export async function getSharePlanNotices() {
    return coreApi('share-plans/notices')
}

// WhatsApp clients waiting 30+ minutes on the signed-in agent's own line, with the suggested reply when one was
// drafted (core 4226, owner 2026-11-04). Scoped by the verified viewer server-side: { rows: [...] }.
export async function getClientWaiting() {
    return coreApi('client-waiting')
}

// Activity panel: each agent's last ss / myhome balance as their PC saw it on a pay step (owner 2026-10-19)
// the signed-in agent's own worker switch (core 3662): { mode: auto|paused|running, in_office, paused }
export async function getMyWorker() {
    return coreApi('my-worker')
}
export async function setMyWorker(mode) {
    return coreApi('my-worker', { method: 'POST', body: { mode } })
}

// every agent's worker switch, for managers / admins (owner 2026-10-20): { states: { login: { mode, in_office, paused } } }
export async function getWorkerSwitches(logins) {
    return coreApi(`worker-switches?logins=${encodeURIComponent((logins || []).join(','))}`)
}
// one part only (owner 2026-10-28, core 4015): part 'reshare' (automatic reposts) | 'wa' (WhatsApp texting), off true/false
// admin only (owner 2026-11-02): limit / reset an agent's automatic ss + myhome for `hours`, or 'clear' the override
export async function setPaidOverride(login, mode, hours) {
    return coreApi('paid-override', { method: 'POST', body: { login, mode, hours } })
}

export async function setWorkerSwitchPart(login, part, off) {
    return coreApi('worker-switches', { method: 'POST', body: { login, part, off } })
}
export async function setMyWorkerPart(part, off) {
    return coreApi('my-worker', { method: 'POST', body: { part, off } })
}
// AD REMOVER (owner 2026-10-23): our agents' live adverts of a flat + each one's removal state; managers / admins only
// the blocked adverts of these flats with the portals' reasons (core 4030, owner 2026-10-29)
export async function getBlockedAds(propertyIds) {
    return coreApi(`blocked-ads?property_id=${encodeURIComponent(propertyIds.join(','))}`)
}
export async function getAdTakedown(propertyId) {
    return coreApi(`ad-takedown?property_id=${encodeURIComponent(propertyId)}`)
}
// reason (owner 2026-10-28, core 4017): sold_rented | agent_stopped | owner_asked | owner_exclusive_other | owner_no_reshare | other
export async function requestAdTakedown(listingIds, reason, note) {
    return coreApi('ad-takedown', { method: 'POST', body: { listing_ids: listingIds, reason, note: note || null } })
}

// RESHARE POOLS PER AGENT (core 3830, owner 2026-10-23): the agent's own pool filters + each pool's counts; managers pass an email
export async function getResharePrefs(email = null) {
    return coreApi(`reshare-prefs${email ? `?email=${encodeURIComponent(email)}` : ''}`)
}
export async function saveResharePrefs(prefs) {
    return coreApi('reshare-prefs', { method: 'POST', body: prefs })
}

export async function getPortalBalances() {
    return coreApi('portal-balances')
}

// Activity panel: advert ids posted AUTOMATICALLY (scheduled / pool / cooperation runs, edits) in a window - ringed dots
export async function getAutoPostIds(fromIso, toIso) {
    return coreApi(`share-plans/auto-ids?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`)
}

export async function getResharePayload(listingId, agentId) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''
    return coreApi(`listing/${encodeURIComponent(listingId)}/reshare-payload${q}`)
    // { version, listing:{...}, payload:{...}, provenance:{field:'origin'|'own'|'ai'|'listing'},
    //   ai:{ran,cached,model}, images:{total,dropped_watermarked,used_thumbs}, own_fields_applied }
}

// B894 Phase 2b/4 — the Facebook share composer. Server assembles the fact package,
// renders (or serves the generate-once cache), splices `contact` at read time.
// targets default to the two FB posts server-side.
export async function getOrpheusRenders(listingId, { targets, contact, force, share_contact, share_price, share_exact_location, card_fields } = {}) {
    return coreApi('orpheus/render', { method: 'POST',
        body: { listing_id: listingId, targets, contact, force, share_contact, share_price, share_exact_location, card_fields } })
}

// 0688: the metro station catalogue for the Desk facet — 22 rows with the count of
// properties whose ad NAMES each station (the same set the v48 filter matches).
export async function getMetroStations() {
    return (await coreApi('metro/stations'))?.rows || []
}

// B868 (a): owner-stated cooperation offers (coop_offer_v1 — newest gate-v9+ claim per
// property). Chunked at 80 like listCooperations. Returns a MAP property_id → offer row.
export async function listCoopOffers(propertyIds) {
    const ids = [...new Set((propertyIds || []).filter(Boolean))]
    const out = {}
    for (let i = 0; i < ids.length; i += 80) {
        const r = await coreApi(`coop-offers?property_id=${ids.slice(i, i + 80).join(',')}`)
        for (const row of r?.rows || []) out[row.property_id] = row
        // The owner's cooperation STANCE rides the same response (core_coop_offers, 2026-09-12)
        // and was dropped here, so the refusal and its quote never reached a card. Carried on
        // the same map entry as `stance`; an entry may now hold a stance and no percent.
        for (const s of r?.stances || []) {
            out[s.property_id] = { ...(out[s.property_id] || { property_id: s.property_id }), stance: s }
        }
    }
    return out
}

// Owner 2026-08-20: a multi-group FB spree is ONE publication row + counts. This reads the
// counts (post_count/group_count per facebook listing) for the lineage modal's badge —
// the canonical link itself rides the listing row's own source_url, nothing extra.
export async function getFbShareStats(propertyId) {
    return coreApi(`fb-share/stats?property_id=${encodeURIComponent(propertyId)}`)
    // { rows: [{ listing_id, source_url, listed_at, post_count, group_count, updated_at }] }
}

export async function setPropertyInventory(propertyId, agentId, action = 'add') {
    return coreApi(`property/${propertyId}/inventory`, { method: 'POST', body: { agent_id: agentId, action } });
}

// T27 — an agent stops being offered as a CONTACT for this flat (0829).
// 🔴 NOT a permission and never to be used as one: representation drives masked-property
// access, meeting attendance and the commission split. This answers only "may we publish this
// person as a contact for this flat", and the server keeps the two apart deliberately.
// ⚠️ Read this even when the agent is NOT currently listed — §8's trap, hit three times in this
// codebase: a control gated on its own filtered output erases itself, so opting out would remove
// the very button that opts you back in. That is why the response names the caller's own state
// (`i_am_opted_out`) instead of leaving the UI to search a list it may not be in.
// §7/T25/T26 — who may be published as a contact on a Facebook share, ALREADY FILTERED by
// T27 opt-outs on the server. ⚠️ Never re-filter client-side: the rule has one home, and a second
// copy drifts — the drifting one being what publishes a number somebody asked us not to publish.
export async function getPropertyShareContacts(propertyId) {
    return coreApi(`property/${propertyId}/share-contacts`)
    // { contacts: [{ party_id, agent_id, name, phone, source, resigned }], opted_out_count }
}

// T37 (0838) — admin comments: the owner's note to whoever works the flat next. ADMIN ONLY,
// enforced on the server from the verified session; a non-admin gets a 403 whose message says so.
// Read by Claude sessions through scripts/admin-comments.mjs; resolved with what was done.
// T39 (0847) — roads and places an agent marked on the map for a property. Cluster-aware on the
// server; `road_distance_m` is the SERVER's number (derived at read from the pin), never computed
// on the client — the card, the claim and the picker must all say the same metres.
// T38/B1184 — the running note: what was pasted onto this record, when, and what it did.
// kind is 'property' | 'requirement'; the server decides who may read a client's (design §7.2).
export async function getRunningNote(kind, id) {
  return coreApi(`${kind === 'requirement' ? 'requirement' : 'property'}/${id}/notes`)
}

export async function getPropertyMarks(propertyId) {
    return coreApi(`property/${propertyId}/marks`)
    // { marks: [{ id, property_id, kind, geojson, label, created_by_party_id, created_at }], road_distance_m }
}
export async function addPropertyMark(propertyId, agentId, { kind, geojson, label = null }) {
    return coreApi(`property/${propertyId}/mark`, { method: 'POST', body: { agent_id: agentId || undefined, kind, geojson, label } })
}
export async function retractPropertyMark(markId, agentId) {
    return coreApi(`property-mark/${markId}/retract`, { method: 'POST', body: { agent_id: agentId || undefined } })
}

export async function getAdminComments(propertyId) {
    return coreApi(`property/${propertyId}/admin-comments`)
    // { comments: [{ id, property_id, body, created_by_party_id, created_at, resolved_at, resolved_by_party_id, resolution }] }
}
export async function addAdminComment(propertyId, body) {
    return coreApi(`property/${propertyId}/admin-comment`, { method: 'POST', body: { body } })
}
export async function resolveAdminComment(id, resolution) {
    return coreApi(`admin-comment/${id}/resolve`, { method: 'POST', body: { resolution } })
}

export async function getPropertyOptouts(propertyId, agentId) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''
    return coreApi(`property/${propertyId}/optouts${q}`)
    // { opted_out: [{ agent_party_id, opted_out_at, acted_by_party_id, reason }], i_am_opted_out, me_party_id }
}

// retract:true opts back in. Cluster-wide on BOTH sides — an agent who opted out from the ss.ge
// advert and opts back in from its korter sibling must find their own opt-out, or they are stuck
// in a state whose only exit is the surface that just told them they were never in it.
export async function setPropertyOptout(propertyId, agentId, { retract = false, reason = null, targetPartyId = null } = {}) {
    // targetPartyId acts on SOMEONE ELSE and is management-only (§8 OPEN-22) — enforced on the
    // server by asking resolveViewer for the caller's role, never by trusting this argument.
    // Omitted entirely when absent so the server's "target defaults to the caller" stays the
    // path an ordinary agent takes.
    return coreApi(`property/${propertyId}/optout`, { method: 'POST',
        body: { agent_id: agentId, retract, reason, ...(targetPartyId ? { target_party_id: targetPartyId } : {}) } });
}

// Market reports (0258) — "I called the owner and it is gone / taken".
// NO cooperation required, by owner ruling 2026-07-29: the agent who phones an owner
// and is told the flat is rented must be able to save that, or the agency loses the
// knowledge. Stored per PROPERTY, never on property_lead — leads exist only for
// crawler-discovered listings, so a lead-scoped write would silently no-op on every
// extension-captured property (see the 0258 header).
// Unverified by construction: a report lowers visibility and accumulates, and never
// archives, so one agent cannot remove another's property (0178 claims-vs-facts).
export async function recordMarketReport(propertyId, { reason, note, agentId, cooperationId } = {}) {
    return coreApi(`property/${propertyId}/market-report`, {
        method: 'POST',
        body: { reason, note: note || null, agent_id: agentId || null, cooperation_id: cooperationId || null },
    });   // { report_id, was_new, report_count, lowers_visibility, archives }
}

// agentId (2026-07-30): lets the server stamp is_self on the viewer's own rows —
// without it retract never renders and the same agent can stack duplicate reports.
export async function getMarketReports(propertyId, agentId = null) {
    const q = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
    return coreApi(`property/${propertyId}/market-report${q}`);   // { reports:[…], report_count }
}

// Batch counts for a page of cards — same shape and reasoning as getDormancy: a
// separate side-read rather than widening agent-listings, which is already the
// heaviest handler in the app. Returns a Map so callers index by property_id.
// Fails SOFT to an empty Map: losing a badge is a worse-looking feed, not a wrong
// one, and this decides nothing a client sees (that is the match gate's job).
export async function getMarketReportCounts(propertyIds) {
    if (!propertyIds?.length) return new Map();
    const d = await coreApi('market-reports', { method: 'POST', body: { property_ids: propertyIds } });
    return new Map((d.statuses || []).map(s => [s.property_id, s]));
}

// Retracts YOUR OWN report only — the server pins reported_by to the resolved caller.
// Soft (retracted_at): the count drops, the evidence trail does not.
export async function retractMarketReport(propertyId, agentId) {
    const qs = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
    return coreApi(`property/${propertyId}/market-report${qs}`, { method: 'DELETE' });
}

// attribute omitted = bulk-endorse every canonical fact the agent hasn't touched
// (the one-click "✓ these imported facts represent my understanding").
export async function endorsePropertyFact(propertyId, agentId, { attribute, action } = {}) {
    return coreApi(`property/${propertyId}/endorse`, { method: 'POST', body: { agent_id: agentId, attribute, action } });
}

// Value assumptions (core 1228): a number or currency read as "most likely meant". Confirm keeps
// the assumed value; reject restores what the source stated.
export async function getPropertyAssumptions(propertyId) {
    return coreApi(`property/${propertyId}/assumptions`); // { assumptions: [...] }
}
export async function decideAssumption(assumptionId, decision) {
    return coreApi(`assumption/${assumptionId}/decide`, { method: 'POST', body: { decision } });
}

// Property promises (core 1227): what the seller promises that is not there yet, and whether it
// is in the current price. Write path is amend_property_promises only (add / set / retract ops).
export async function getPropertyPromises(propertyId) {
    return coreApi(`property/${propertyId}/promises`); // { promises: [...] }
}
export async function amendPropertyPromises(propertyId, ops) {
    return coreApi(`property/${propertyId}/promises/amend`, { method: 'POST', body: { ops } }); // { property_id, applied, promises }
}

export async function addPropertyNote(propertyId, agentId, body) {
    return coreApi(`property/${propertyId}/note`, { method: 'POST', body: { agent_id: agentId, body } });
}

export async function deletePropertyNote(noteId, agentId) {
    return coreApi(`property/note/${noteId}?agent_id=${encodeURIComponent(agentId)}`, { method: 'DELETE' });
}

// ── Agent pool (step 3, core migrations 0065/0069, server lib core_pool.js) ───
// One card per physical property (identity cluster); publications are OFFERS.
// The OFFER (price + total commission % + intent) is entered in the Publish form
// and stored ON the publication (0069) — a scraped property has no agent-authored
// listing to read a price from. Facts = the agent's own+endorsed beliefs through
// their field_policy. Payout = price × commission% × 0.5 (fixed 50/50 split).
// Offers arrive cheapest-first (named anti-steering policy).

// offer: { price, currency, intent, commission_percent, field_policy? }
export async function publishPropertyToPool(propertyId, agentId, offer = {}) {
    return coreApi(`property/${propertyId}/publish`, {
        method: 'POST',
        body: { agent_id: agentId, price: offer.price, currency: offer.currency, intent: offer.intent,
                commission_percent: offer.commission_percent, field_policy: offer.field_policy || {} },
    });
}

export async function unpublishPropertyFromPool(propertyId, agentId) {
    return coreApi(`property/${propertyId}/unpublish`, { method: 'POST', body: { agent_id: agentId } });
}

// ── PUBLIC audience (B187, 0135) — the public /p/:id page's writer ──────────
// Same publication machinery as the pool, audience='public'. Commission is
// deliberately NOT part of the public offer (0135: pool economics are
// agent-facing; the public offer is just price+intent).
export async function publishPropertyPublic(propertyId, agentId, offer = {}) {
    return coreApi(`property/${propertyId}/publish-public`, {
        method: 'POST',
        body: { agent_id: agentId, price: offer.price, currency: offer.currency, intent: offer.intent,
                field_policy: offer.field_policy || {} },
    });
}

export async function unpublishPropertyPublic(propertyId, agentId) {
    return coreApi(`property/${propertyId}/unpublish-public`, { method: 'POST', body: { agent_id: agentId } });
}

export async function getPublicStatus(propertyId, agentId) {
    return coreApi(`property/${propertyId}/public-status?agent_id=${encodeURIComponent(agentId || '')}`);
    // { published_by_me, published_by_anyone, offer, suggested_price, suggested_currency, suggested_intent, url }
}

// Prefills for the Publish form: observed price + cooperation commission (auto-
// filled, editable) + whether the agent already has an active offer to edit.
export async function getPublishDefaults(propertyId, agentId) {
    return coreApi(`property/${propertyId}/publish-defaults?agent_id=${encodeURIComponent(agentId)}`);
    // { already_published, offer, suggested_price, suggested_currency, suggested_intent, suggested_commission }
}

// Leads (0163, 2026-07-21): crawler-discovered owner supply — NOT matched until a
// cooperation promotes it. Claims are non-exclusive work markers, never rights.
// ⚠️ CHUNKED AT 80 ids, the same wall as listCooperations and getListingThumbs: ~200 uuids
// in one query string pushes the request line past nginx's 8KB cap, and the failure is
// OPAQUE — the browser reports "Failed to fetch" with no status and the server logs nothing,
// so the feed just renders empty. Found 2026-07-29 wiring the possible-matches shelf into
// Leads: the overlay then had to cover matched AND shelved rows (~390 ids) and went straight
// through the cap. It was already at ~7.4KB for a single busy matched page, i.e. one step
// from breaking on its own.
const leadsPage = async ({ status, limit, offset, ids }) => {
    const p = new URLSearchParams();
    // ids (0187): metadata-provider mode — lead overlay (status/claims/contact density) for
    // exactly the visible page of properties; listing content comes from
    // getAgentListings({scope:'leads'}) via the shared search pipeline.
    if (ids?.length) p.set('property_id', ids.join(','));
    if (status) p.set('status', status);
    if (limit) p.set('limit', limit);
    if (offset) p.set('offset', offset);
    const qs = p.toString();
    return (await coreApi(`leads${qs ? '?' + qs : ''}`)).leads || [];
};
// valuations started on sentavus.com and not sent (owner 2026-10-07) — core_leads.js /leads/unsent-valuations
// { valuations, total } — total is the real count (owner 2026-10-28: "it's stuck at 50"), the page is limit/offset
export async function getUnsentValuations({ limit = 50, offset = 0 } = {}) {
    const r = await coreApi(`leads/unsent-valuations?limit=${limit}&offset=${offset}`);
    return { valuations: r.valuations || [], total: Number.isFinite(r.total) ? r.total : (r.valuations || []).length };
}
// every saved answer with its question's wording (no phone) — core_unsent_valuations.js
export async function getUnsentValuation(id, lang) {
    return coreApi(`leads/unsent-valuations/${encodeURIComponent(id)}?lang=${encodeURIComponent(lang || 'ka')}`);
}
// a link that opens the visitor's answers in the real sentavus.com form; opening it is recorded like a phone reveal
export async function finishUnsentValuation(id) {
    return coreApi(`leads/unsent-valuations/${encodeURIComponent(id)}/finish`, { method: 'POST', body: {} });
}
export async function revealUnsentValuationPhone(id) {
    return coreApi(`leads/unsent-valuations/${encodeURIComponent(id)}/phone`, { method: 'POST', body: {} });
}
export async function getLeads({ status, limit, offset, propertyIds } = {}) {
    if (!propertyIds?.length) return leadsPage({ status, limit, offset, ids: null });
    const chunks = [];
    for (let i = 0; i < propertyIds.length; i += 80) chunks.push(propertyIds.slice(i, i + 80));
    // limit/offset are deliberately NOT forwarded to the chunks: they page the lead POOL,
    // which is a different question from "overlay these exact properties", and applying
    // them per chunk would silently truncate each one.
    const parts = await Promise.all(chunks.map(ids => leadsPage({ status, ids })));
    return parts.flat();
}

// ♥ Favorites (0191): per-agent bookmarks with a display snapshot frozen at ♥ time —
// the Favorites view renders from these rows alone; lineage modal shows live truth.
export async function getFavorites(agentId) {
    return coreApi(`favorites?agent_id=${encodeURIComponent(agentId)}`);
}
export async function addFavorite({ agentId, propertyId, listingId, snapshot }) {
    return coreApi('favorites', { method: 'POST', body: { agent_id: agentId, property_id: propertyId, listing_id: listingId || null, snapshot: snapshot || null } });
}
export async function removeFavorite(agentId, propertyId) {
    return coreApi('favorites/remove', { method: 'POST', body: { agent_id: agentId, property_id: propertyId } });
}

// 🗄 Archive (0375): the SAME shape as ♥ above, deliberately — a per-agent shelf of
// properties this agent doesn't want in their own feed. It is NOT the leads Archive
// (that one is company-wide, sets lead_status, and only exists for leads) and NOT a
// market report (0178: a report never archives, so one agent can't remove another's
// property). Nobody else can see this shelf and it says nothing about the property.
export async function getArchived(agentId) {
    return coreApi(`archived?agent_id=${encodeURIComponent(agentId)}`);
}
export async function addArchived({ agentId, propertyId, listingId, snapshot }) {
    return coreApi('archived', { method: 'POST', body: { agent_id: agentId, property_id: propertyId, listing_id: listingId || null, snapshot: snapshot || null } });
}
export async function removeArchived(agentId, propertyId) {
    return coreApi('archived/remove', { method: 'POST', body: { agent_id: agentId, property_id: propertyId } });
}

// "Is this ad posted by an agent?" (0376) — three sources for one question:
// the PORTAL's own declaration (`declared`, B219's declared_author_role — 16,939
// claims that until now nothing read), the scraped agency roster (`roster`, 0204),
// and our own agents' first-hand reports (`reported_agent`/`reported_owner`), which
// are the only source that can see an agency agent posting under a private-person
// account. `elsewhere` = other properties the same number was reported on, computed
// at read time; it is never a stored fact about the person (B219: role is
// listing-scoped, the same human is owner on one ad and agent on another).
// A MARK, NOT A GATE: it never changes lead status or end_reason.
//
// Per-page side-read, same pattern and same soft failure as getMarketReportCounts:
// an empty Map means cards render without the badge, never that nothing is marked.
// Counts only — phone numbers never ride a batch response.
export async function getAuthorRoleMarks(propertyIds) {
    if (!propertyIds?.length) return new Map();
    const d = await coreApi('author-role/by-property', { method: 'POST', body: { property_ids: propertyIds } });
    return new Map((d.marks || []).map(m => [m.property_id, m]));
}
export async function getAuthorRole(propertyId) {
    return coreApi(`author-role/${encodeURIComponent(propertyId)}`);
}

// answer: 'yes' = this AD was posted by an agent, 'no' = it was not. 'no' says
// NOTHING about who owns the place — ownership is declared by contact_of role edges,
// and a second way to say it would be a second source of truth (owner correction
// 2026-08-02). Scoped to the LISTING, never the person.
export async function reportAuthorRole({ listingId, answer, agentId, note }) {
    return coreApi('author-role/report', { method: 'POST', body: { listing_id: listingId, answer, agent_id: agentId || null, note: note || null } });
}

// F4 conflict gate (2026-08-30): attributes where the current value silently overrode a different
// known value from another source (scrape vs ai, etc.). Read-only; only fields a machine picked.
export async function getClaimConflicts(subjectId) {
    return coreApi(`claims/conflicts?subject_id=${encodeURIComponent(subjectId)}`);
}
// Resolve one — commit the chosen value as this viewer's decision (admin 0.95 / agent 0.50), which
// wins by confidence and clears the conflict. Server resolves the actor from the session + agent_id.
export async function resolveClaimConflict({ subjectType = 'property', subjectId, attribute, valueText = null, valueNum = null, valueJson = null }) {
    return coreApi('claims/resolve', { method: 'POST', body: {
        subject_type: subjectType, subject_id: subjectId, attribute,
        value_text: valueText, value_num: valueNum, value_json: valueJson,
        agent_id: _viewerAgentId || null,
    } });
}

// ── CLIENT leads (0462/0463) — the DEMAND side ───────────────────────────────
// ⚠️ Same word, opposite direction. getLeads() above is owner SUPPLY: flats we could
// sell. This is client DEMAND: people who want one. Owner ruling 2026-08-06 puts them
// in the same Leads view under a `კლიენტი` type, because an agent hunting new business
// wants both — they share nothing else. See FACEBOOK_ADS_PLAN_2026-08-06.md §4A.
//
// A client lead is an active requirement with NO live representative (0157: "'unknown'
// is NOT a role — it is the absence of rows"). Picking one up writes that row, which
// removes it from this feed by definition.
export async function getClientLeads({ intent, kind, districtCodes, priceMinUsd, priceMaxUsd,
                                       roomsMin, roomsMax, limit, offset, withCount = true } = {}) {
    const p = new URLSearchParams();
    if (intent) p.set('intent', intent);
    if (kind) p.set('kind', kind);
    if (districtCodes?.length) p.set('district_codes', searchedDistricts(districtCodes).join(','));   // Gori town = its municipality
    if (priceMinUsd != null) p.set('price_min_usd', priceMinUsd);
    if (priceMaxUsd != null) p.set('price_max_usd', priceMaxUsd);
    if (roomsMin != null) p.set('rooms_min', roomsMin);
    if (roomsMax != null) p.set('rooms_max', roomsMax);
    if (limit) p.set('limit', limit);
    if (offset) p.set('offset', offset);
    if (!withCount) p.set('count', '0');
    const qs = p.toString();
    const r = await coreApi(`client-leads${qs ? '?' + qs : ''}`);
    // count_capped: the RPC counts through its own 300-row ceiling, so `count` saturates
    // rather than overstating. Callers must render "300+" when capped — a number that is
    // not a total may never be drawn as one (the 2026-08-05 `92 → 162 → 3141` rule).
    return { leads: r.leads || [], count: r.count ?? null, countCapped: !!r.count_capped };
}

// 409 = someone already picked this client up. That is a REAL answer, not an error to
// swallow: unlike a property lead (claims deliberately non-exclusive — several agents may
// chase one owner), a client is exclusive. Two agents calling the same person about the
// same search is the owner-fatigue problem pointed at our own customer.
export async function claimClientLead(requirementId, agentId) {
    return coreApi(`client-lead/${requirementId}/claim`, { method: 'POST', body: { agent_id: agentId } });
}

// Two verbs on a client's representation, and they are NOT variations of each other
// (owner 2026-09-02: "opting out mean giving away as lead but sharing mean they stay and add
// someone else too").
//
// share  — adds a SECOND representative and keeps yours. The colleague then sees exactly what
//          you see, contact details included, because they are a representative rather than
//          because a permission list grants it.
// leave  — ends only YOUR representation. `back_in_pool` in the response is the fact that
//          matters: it means nobody else holds this client and they are now in the client-lead
//          pool for anyone to claim, which is a different outcome from handing them to a
//          colleague and must be reported as such.
// Who represents this client now. The requirement GET does not carry it, and an empty list is
// a REAL answer — it means nobody does, i.e. the client is back in the lead pool.
export async function getRequirementRepresentatives(requirementId) {
    return (await coreApi(`requirement/${requirementId}/representatives`)).representatives || [];
}
export async function shareRequirement(requirementId, { toAgentId, toPartyId, agentId } = {}) {
    return coreApi(`requirement/${requirementId}/share`, { method: 'POST',
        body: { to_agent_id: toAgentId, to_party_id: toPartyId, agent_id: agentId } });
}
// STOP a share you made (owner 2026-09-08). Not the same act as leaving: leaving ends YOUR
// own representation and can put the client back in the pool; this ends someone else's.
// ⚠️ The server allows it only for whoever granted that row, or management — the read's
// `can_remove` says which chips may show an ✕, so the UI never offers one that 403s.
export async function unshareRequirement(requirementId, { toAgentId, toPartyId, agentId } = {}) {
    return coreApi(`requirement/${requirementId}/unshare`, { method: 'POST',
        body: { to_agent_id: toAgentId, to_party_id: toPartyId, agent_id: agentId } });
}
export async function unshareProperty(propertyId, { toAgentId, toPartyId } = {}) {
    return coreApi(`property/${propertyId}/unshare`, { method: 'POST',
        body: { to_agent_id: toAgentId, to_party_id: toPartyId } });
}
export async function leaveRequirement(requirementId, agentId) {
    return coreApi(`requirement/${requirementId}/leave`, { method: 'POST', body: { agent_id: agentId } });
}

// ── SHARING A PROPERTY (0983, owner 2026-09-08: "same should go with property") ──────────
// The property twin of shareRequirement/leaveRequirement above. It grants REPRESENTATION, so
// the colleague sees what you see and the flat joins their book — and, like every other
// representation source, they join the meeting attendee list the equal-split commission
// divides. Only a current representative or management may call it; the server decides that.
export async function sharePropertyWith(propertyId, { toAgentId, toPartyId, note } = {}) {
    return coreApi(`property/${propertyId}/share`, { method: 'POST',
        body: { to_agent_id: toAgentId, to_party_id: toPartyId, note } });
}
// ⚠️ Ends a GRANT only. Capture-derived representation (you authored the ad, you scraped it)
// is a record of something that happened and is not handed back — the response's
// `still_representative` says so rather than reporting a success that changed nothing.
export async function leaveProperty(propertyId) {
    return coreApi(`property/${propertyId}/leave`, { method: 'POST', body: {} });
}
// { representatives: [{ party_id, agent_id, name, granted }] } — `granted` distinguishes a
// share from capture, so a panel can offer Leave only where there is something to leave.
export async function getPropertyRepresentatives(propertyId) {
    return coreApi(`property/${propertyId}/representatives`);
}

export async function claimLead(propertyId, agentId) {
    return coreApi(`lead/${propertyId}/claim`, { method: 'POST', body: { agent_id: agentId } });
}

export async function releaseLead(propertyId, agentId, outcome) {
    return coreApi(`lead/${propertyId}/release`, { method: 'POST', body: { agent_id: agentId, outcome: outcome || undefined } });
}

// Part C3 (2026-07-14): access requests — demand-driven owner-contact release.
// Grant = standing reveal permission for (requester, that one property), honored
// by the reveal endpoint. Admin decides; nothing transfers, nothing goes public.
export async function fileAccessRequest(propertyId, context) {
    return coreApi('access-request', {
        method: 'POST',
        body: { property_id: propertyId, context: context || null, viewer_agent_id: _viewerAgentId || null },
    }); // { id, status, notified } or { existing: true }
}
// 0158: requirement-scoped access — the grant means "the contact attached to THIS
// requirement" (invariant 6), decided by the requirement's representative, not admin.
export async function fileRequirementAccessRequest(requirementId, context) {
    return coreApi('access-request', {
        method: 'POST',
        body: { requirement_id: requirementId, context: context || null, viewer_agent_id: _viewerAgentId || null },
    }); // { id, status, notified } or { existing: true }
}

export async function getAccessRequests(scope) {
    const p = scope === 'admin' ? 'scope=admin' : `scope=${scope}&viewer_agent_id=${encodeURIComponent(_viewerAgentId || '')}`;
    return coreApi(`access-requests?${p}`); // { requests[], rep_counters?[] }
}

// scope (0195) applies to action='grant' only: 'this_meeting' | 'until_revoked'.
// ⚠️ Intent, not enforcement — once a number is seen it cannot be unseen. Never
// surface these as "temporary"/"permanent" access (MEETINGS_MODEL.md §4.3).
// Omitted → the server defaults to 'until_revoked', the pre-0195 behaviour.
export async function decideAccessRequest(id, action, note, scope = null) {
    return coreApi(`access-request/${id}/decide`, {
        method: 'POST',
        body: { action, note: note || null, ...(scope ? { scope } : {}) },
    });
}
// 0158 step 5: who can see this demand's contact + who has looked. Rep/admin only.
export async function getRequirementAccessAudit(requirementId) {
    return coreApi(`requirement/${requirementId}/access-audit`); // { grants[], reveals[] }
}

// Part C2 (2026-07-14): dormancy tier — cluster-level 60d activity computed
// server-side (reveals + viewings + fresh captures + availability wake).
// Desk tiering ONLY; never affects matching/pool.
export async function getDormancy(propertyIds) {
    if (!propertyIds?.length) return new Map();
    const d = await coreApi('dormancy', { method: 'POST', body: { property_ids: propertyIds } });
    return new Map((d.statuses || []).map(s => [s.property_id, s]));
}

// adminToken (2026-07-21, owner ruling): a verified scheduler ADMIN may set availability
// without an agents-table row — same dual-identity pattern as recordPartyVerdict. The
// server re-verifies the token against the scheduler project + app_users.role='admin';
// it is never trusted as a bare client flag.
export async function setPropertyAvailability(propertyId, agentId, availableFrom, adminToken = null) {
    return coreApi(`property/${propertyId}/availability`, {
        method: 'POST', body: { agent_id: agentId || null, available_from: availableFrom || null, admin_token: adminToken || null },
    });
}

// Part C1 (2026-07-14): the ONLY path to a contact's phone number — servers no
// longer embed phones in match/lineage payloads. Every call is logged server-side
// as a `contact.revealed` event (the audit trail + the archive activity heartbeat).
export async function revealContact({ targetPartyId, propertyId, listingId, context, ownerOfProperty, contactId }) {
    return coreApi('contact/reveal', {
        method: 'POST',
        body: { target_party_id: targetPartyId || null, property_id: propertyId || null,
                listing_id: listingId || null, context: context || null,
                // contact-book row precision (core 0141): reveal ONE observation's value
                contact_id: contactId || null,
                // C3: ask the server to resolve the property's OWNER itself (a granted
                // requester never has the owner's party id — hidden from non-reps)
                owner_of_property: !!ownerOfProperty,
                // viewer identity: same session viewer registered via setCoreViewer —
                // the server logs the reveal against THIS agent (fails 403 without one)
                viewer_agent_id: _viewerAgentId || null },
    });
    // { name, phone, email, attempt_id }
}

// B111 (2026-07-22): the ONE TAP. A reveal seeds a PENDING contact_attempt
// server-side and returns its id; this reports what happened. Deliberately the
// whole API surface for attempts — if reporting an outcome ever needs more than
// one call with one value, agents will stop doing it and the table goes empty,
// which reads downstream as "nobody called" and is confidently wrong.
export async function recordAttemptOutcome(attemptId, outcome, note) {
    return coreApi(`attempt/${attemptId}/outcome`, {
        method: 'POST',
        body: { outcome, note: note || null, viewer_agent_id: _viewerAgentId || null },
    });
}

// B111: my own unresolved contact touches — the nudge's data. Own rows only;
// the server scopes by the verified viewer party, not by anything sent here.
export async function getOpenTouches() {
    const qs = _viewerAgentId ? `?viewer_agent_id=${encodeURIComponent(_viewerAgentId)}` : '';
    return coreApi(`touches/open${qs}`);
}

// Listing view log (2026-07-15): "who saw my listing" — anti-silent-poaching.
// A view = a deliberate detail-modal open of a NON-own listing (the modal fires
// this; feed impressions stay in recommendation.presented). Silent by design —
// the viewer gets no UI notice. Fire-and-forget: a failed log must never break
// the modal open.
export function logListingView({ propertyId, listingId, context }) {
    if (!propertyId) return;
    coreApi('listing/view', {
        method: 'POST',
        body: { property_id: propertyId, listing_id: listingId || null,
                context: context || null, viewer_agent_id: _viewerAgentId || null },
    }).catch(() => {});
}

// Batched 👁 lookup for the Desk feed's own cards. Rep-gated server-side: rows
// come back only for properties the session viewer represents (admin sees all);
// non-represented ids are silently omitted. → Map(property_id → {total, viewers[]})
export async function getListingViews(propertyIds) {
    const ids = [...new Set((propertyIds || []).filter(Boolean))];
    if (!ids.length) return new Map();
    const out = new Map();
    for (let i = 0; i < ids.length; i += 200) {
        const d = await coreApi('listing-views', {
            method: 'POST',
            body: { property_ids: ids.slice(i, i + 200), viewer_agent_id: _viewerAgentId || null },
        });
        for (const v of (d.views || [])) out.set(v.property_id, v);
    }
    return out;
}

// ⚠️ POOL REMOVED 2026-08-01 (owner: "remove pool completely"). `getPoolFeed`,
// `getPoolSettings`, `setPoolSettings`, `getPoolCard` and `desk/PoolFeed.jsx` are gone.
// What REMAINS, deliberately, and is now unreachable from any client:
//   · server routes  /api/core/pool, /pool/settings, /pool/card
//   · table          agent_pool_settings  (1 row, auto_publish=false — verified before
//                    removal, so no server-side capture hook is left running blind)
//   · core_ingest.js autoPublishOnCapture, which reads that table
// Retiring those is a SERVER change with its own deploy discipline — see B268. Until then
// the behaviour is inert because the one row opts out.

// ── Recommendation opportunities (scorer + classifier, server lib core_match.js) ──
// Read-only, re-classified on demand. Each opportunity carries classification,
// primary_action, evidence (the ✓/≈/?/✗ checks), coverage, and the privacy-projected
// card fields — masking is done SERVER-side per viewer; the UI only renders what
// arrives. The viewer is registered once per session via setCoreViewer (IntakeTab,
// when myAgentId resolves) — without it the server fails closed to the
// representative (masked) view.

let _viewerAgentId = null;
export function setCoreViewer(agentId) { _viewerAgentId = agentId || null; }
export function getCoreViewerAgentId() { return _viewerAgentId; }
const viewerParam = () => (_viewerAgentId ? `&viewer_agent_id=${encodeURIComponent(_viewerAgentId)}` : "");

// Compat: until the Phase-4 opportunity UI lands, MatchSuggestions still reads
// `matches[]` with `checks` — alias opportunities into that shape (evidence ≡ checks).
const withMatchAlias = (d) => ({
    ...d,
    matches: (d.opportunities || []).map(o => ({ ...o, checks: o.evidence })),
});

// `sort` (designer round 2 §5): fit | fresh | cheap | open. It is a SERVER argument because
// each order must change WHICH rows survive the limit cut — re-sorting the page we already
// hold would answer "the cheapest of the best fits", which is a different list. The response
// echoes `sort`, and an unknown value falls back to `fit` server-side.
export async function getRequirementMatches(requirementId, { limit = 8, force = false, sort = null } = {}) {
    // { considered, eligible_count, opportunities[], diagnostics{reject_count,sample[]}, viewer_party_id }
    // B78: may instead return { too_broad, candidate_count, supply_total, threshold, ask[],
    // district_soft_codes[] } — the shallow-requirement soft gate. force=1 runs the full
    // path anyway (logged server-side).
    return withMatchAlias(await coreApi(`requirement/${requirementId}/matches?limit=${limit}${force ? '&force=1' : ''}${sort ? `&sort=${encodeURIComponent(sort)}` : ''}${viewerParam()}`));
}

// The instant layer (matching-UI v2 §0): stored match_edge rows — group headers, counts
// and order paint from this in one cheap round trip while the live scoring pass runs.
// Ships NO address (D18 mask needs context the stored arm doesn't pay for), no thumbs,
// no checks — rows carry stored:true so the panel knows the detail is still coming.
export async function getRequirementMatchesStored(requirementId, { limit = 50 } = {}) {
    return withMatchAlias(await coreApi(`requirement/${requirementId}/matches?stored=1&limit=${limit}${viewerParam()}`));
}

// The concession ladder's `ნახე` (designer round 3 §1). Returns the rows BEHIND a probe's
// number — the server calls `match_candidates_v2` with the identical arguments the count
// used, and 0255/0256 are parity-locked, so these are the probe's own candidates rather
// than a fresh query whose number would differ.
// ⚠️ `scored: false` rides in the response and is not decoration: these passed the counted
// predicate under one relaxed condition, and NOTHING has scored them. They are what the
// client could see if they moved that condition — never a match list.
export async function getRequirementProbe(requirementId, constraint, { limit = 12 } = {}) {
    return coreApi(`requirement/${requirementId}/probe?constraint=${encodeURIComponent(constraint)}&limit=${limit}${viewerParam()}`);
}

export async function getPropertyMatchingClients(propertyId, { limit = 8 } = {}) {
    return withMatchAlias(await coreApi(`property/${propertyId}/matching-clients?limit=${limit}${viewerParam()}`));
}

// Workflow verdict on a (demand, property) match pair (0102, 2026-07-16): shortlist |
// proposed | rejected | viewing | deal, or 'clear' to remove. Stamped with the session
// viewer agent (same identity setCoreViewer registered) — null for admin is fine.
export async function setMatchVerdict({ requirementId, propertyId, state, reason }) {
    return coreApi("match-verdict", { method: "POST", body: {
        requirement_id: requirementId, property_id: propertyId, state,
        reason: reason || null, agent_id: _viewerAgentId,
    } });
}

// One-tap "client still looking ✓" (0110, 2026-07-18): bumps the demand's
// last-activity clock (requirement.updated_at) through an evented gateway RPC
// WITHOUT touching criteria. Viewer rides the query string — resolveViewer on the
// scheduler-key path reads viewer_agent_id from there, same as the match reads.
export async function confirmRequirementActive(requirementId) {
    return coreApi(`requirement/${encodeURIComponent(requirementId)}/confirm-active?_=1${viewerParam()}`, {
        method: "POST", body: {},
    });
}

// Record an agent interaction with a recommendation (`recommendation.opened` /
// `recommendation.action_taken` — including action_kind:'reject_reason'). The
// `.presented` verb is server-emitted only. Actor = the registered viewer.
export async function logRecommendationEvent({ verb, recommendation_id, requirement_id, listing_id = null, data = {} }) {
    return coreApi("recommendation/event", {
        method: "POST",
        body: { verb, recommendation_id, requirement_id, listing_id, data, agent_id: _viewerAgentId },
    }); // { ok, event_id, actor_party_id }
}

// Desk behaviour telemetry (`desk.*` verbs → core `event` via the 0181 gateway).
// Fire-and-forget like logSearchQuery: a lost row is fine, an error in the UI is
// not. Lives HERE (not a separate file) because coreApi is module-private — the
// semantic boundary is the function name + verb namespace, not a file boundary.
// ⚠️ Do NOT route these through logSearchQuery's context blob: search_query_log
// has search-shaped columns (raw_text/compiled/residual) that a view change
// would leave empty-and-lying. Different stream, different meaning.
// Server resolves the actor from the viewer token (agents AND admins). verb is
// bare ('view_changed'); the 'desk.' prefix is applied here so call sites can't
// stray outside the namespace.
export function trackDeskEvent(verb, data = {}, { subjectType = null, subjectId = null } = {}) {
    coreApi("desk-event", {
        method: "POST",
        body: { verb: `desk.${verb}`, data, subject_type: subjectType, subject_id: subjectId },
    }).catch(() => {})
}

// Read-only outcome funnel (ops diagnostics, D7 — never a scorecard). Pass exactly
// one of the three keys. Stages carry `source` reliability tags; `stages_deferred`
// lists what isn't joined yet (scheduler-DB viewings).
export async function getRecommendationFunnel({ requirementId, propertyId, recommendationId } = {}) {
    const p = requirementId ? `requirement_id=${encodeURIComponent(requirementId)}`
        : propertyId ? `property_id=${encodeURIComponent(propertyId)}`
        : `recommendation_id=${encodeURIComponent(recommendationId)}`;
    return coreApi(`recommendation/funnel?${p}`);
}

// Candidates are created straight into the scheduler's own DB (useCandidates), not through
// core's requirement pipeline — but Recent Entries is meant to be the one ledger for
// everything the Desk buttons produce (see IntakeTab OBJECT_TYPE), so this logs an
// audit-only entry the same way saveMeetingProposal does. intake.kind must be "other" —
// the check constraint doesn't have a candidate-specific value (see core/migrations/0003).
// candidateId is the scheduler DB row id — carried on the proposal so a later click on the
// Recent Entries card can look the candidate back up and open it for editing (see IntakeTab).
export async function logCandidateProposal(candidate, candidateId) {
    const { id: intake_id } = await coreApi("intake", {
        method: "POST",
        body: { raw_text: `Added candidate via Data Desk: ${candidate.name || candidate.phone || "unnamed"}`, kind: "other", channel: "scheduler" },
    });
    return coreApi("proposal", {
        method: "POST",
        body: { intake_id, proposal: { proposal_kind: "add_candidate", candidate_id: candidateId || null, name: candidate.name || null, phone: candidate.phone || null, position: candidate.position || null } },
    }); // { id }
}

// ── Business-for-sale (BUSINESS_ARCHITECTURE.md v1, server lib core_business.js) ──
// Businesses are first-class assets linked to properties via business_property.
// Add-flow entry is PROPERTY-FIRST only (the property card's "Businesses here" section) —
// there is deliberately no standalone add-business button.

export async function getBusinessVocab() {
    return coreApi("business-vocab"); // { categories, feature_definitions, asset_kinds }
}

// property_id → only businesses occupying that property (the property-card section).
export async function getBusinesses({ propertyId, category, status } = {}) {
    const p = new URLSearchParams();
    if (propertyId) p.set("property_id", propertyId);
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    return coreApi(`businesses${p.toString() ? `?${p}` : ""}`); // { businesses, total }
}

export async function getBusiness(id) {
    return coreApi(`business/${encodeURIComponent(id)}`); // { business, locations, features, assets, listings, claims }
}

// Identity-first create: business + occupancy rows (+ optional features/assets) in one call.
// payload: { category_key, name?, description?, legal_form?, ..., properties:[{property_id,tenure,...}], features?, assets? }
export async function createBusiness(payload) {
    return coreApi("business", { method: "POST", body: payload }); // { ok, business_id }
}

export async function updateBusiness(id, patch) {
    return coreApi(`business/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }); // { ok, business }
}

// The SALE listing (step 3, optional/skippable — a business exists before the owner commits).
// price null => price on request ("ფასი შეთანხმებით").
export async function createBusinessListing(businessId, { price = null, currency = "USD", meta = {}, authorParty = null } = {}) {
    return coreApi(`business/${encodeURIComponent(businessId)}/listing`, {
        method: "POST", body: { price, currency, meta, author_party: authorParty },
    }); // { ok, listing_id }
}

// Offers and financial claims (core 3922). A business carries several offers, each with its
// own price and buyer rent — read them one at a time, never merged into one price.
export async function createBusinessOffer(businessId, offer) {
    return coreApi(`business/${encodeURIComponent(businessId)}/offer`, { method: "POST", body: offer }); // { ok, row }
}
export async function updateBusinessOffer(offerId, patch) {
    return coreApi(`business-offer/${encodeURIComponent(offerId)}`, { method: "PATCH", body: patch }); // { ok, row }
}
// Public visibility on sentavus.com (owner 2026-10-26). Name and exact address stay hidden unless ticked.
export async function setBusinessPublication(businessId, { isPublic, showName, showLocation }) {
    return coreApi(`business/${encodeURIComponent(businessId)}/publication`, {
        method: "POST", body: { public: !!isPublic, show_name: !!showName, show_location: !!showLocation },
    }); // { ok, publication }
}
export async function createBusinessFinancial(businessId, claim) {
    return coreApi(`business/${encodeURIComponent(businessId)}/financial`, { method: "POST", body: claim }); // { ok, row }
}
export async function updateBusinessFinancial(claimId, patch) {
    return coreApi(`business-financial/${encodeURIComponent(claimId)}`, { method: "PATCH", body: patch }); // { ok, row }
}

// ── FB pipeline control (owner 2026-09-06: "they must communicate from api") ────────────
// Desk drives the Facebook intelligence pipeline through the server instead of the old
// same-machine desk↔extension bridge, so it works from any device. The extension on gigi's
// Chrome mirrors this config and executes "run now" requests on its ~1-min heartbeat.
// All three are gated server-side to admin/GM (x-viewer-token) or gigi (the extension's JWT).
export async function fbPipelineGet() {
    return coreApi("fb/pipeline"); // { ok, config, status, heartbeat_at, run_requested_at, ... }
}
export async function fbPipelineSetConfig(config) {
    return coreApi("fb/pipeline/config", { method: "POST", body: { config } }); // { ok, config, ... }
}
export async function fbPipelineRun(kind /* 'sweep' | 'dm' */) {
    return coreApi("fb/pipeline/run", { method: "POST", body: { kind } }); // { ok, requested, ... }
}

// Register a reshare the EXTENSION stashed while the agent had no extension login (Desk-only). The
// Desk's own verified session (x-viewer-token) authorises it and the server attributes the agent from
// that session (owner 2026-09-08). `body` is the non-secret reshare payload; the token never leaves this
// fetch (never the page bus), so the Desk↔extension security invariants are untouched.
export async function registerReshare(body) {
    return coreApi("reshare", { method: "POST", body }) // { listing_id, ... } | { error }
}

// ── THE MAILBOX SIGNED CONTRACTS ARE SENT FROM (admin only, owner 2026-09-11) ──────────────
// 🔑 The password travels ONE WAY. `getContractMail` answers the SHAPE — mailbox, host, port,
// whether it is on — and there is no route that reads the secret back, on purpose: a value
// that can be read can be logged, cached and copied. Changing it means typing it again.
// ⚠️ Every one of these answers 403 `admin_only` for anyone but an admin, and the Desk hides
// the card on the same rule; the server's copy is the one that protects it.
// ── WHO MAY SIGN FOR THE AGENCY (owner ruling 2026-09-12, admin only) ────────────────────
// The capability lives in `party_capability` — the same table `represent_property` has used
// since 0233 — so this is a grant, with the audit that comes with one, and not a flag on the
// roster. Both routes answer 403 `admin_only` for anyone else; the switch is hidden on the same
// rule, and hiding it is the cosmetic half.
export async function getAgencySignatories() {
    return coreApi('agency-signatories'); // { ok, signatories:[{party_id, scheduler_agent_id, name_ka, title_ka, is_default, …}] }
}
// `scheduler_agent_id` is what the roster screen holds; the server bridges it to the core party
// itself, because a client that names the party is a client that can name someone else's.
export async function setAgencySignatory({ schedulerAgentId, partyId, on, titleKa, titleEn, nameKa, nameEn }) {
    return coreApi('agency-signatories', { method: 'POST', body: {
        scheduler_agent_id: schedulerAgentId || null, party_id: partyId || null, on: on !== false,
        title_ka: titleKa || null, title_en: titleEn || null, name_ka: nameKa || null, name_en: nameEn || null } });
}
// Public profile on sentavus.com (owner 2026-09-23, core 2113). Admin/owner only — the server checks the role and
// records the admin's own party. Keyed by the scheduler agent id the roster screen already holds.
export async function getAgentPublicProfiles() {
    return coreApi('agent-public-profiles'); // { items:[{ agent_id, ref, public, auto_publish_at, status }] }
}
export async function setAgentPublicProfilePublication(agentId, publish) {
    return coreApi(`agent-public-profiles/${encodeURIComponent(agentId)}/publication`, { method: 'POST', body: { publish: publish === true } });
}
// Messaging (owner 2026-09-23, core 2240 + 2280): website visitors' chats and the team's own chats (direct, a flat,
// a client). The server decides who may read and write; nothing here is trusted for that.
export async function getChatInbox(tab = 'visitors', scope = 'mine') {
    const q = new URLSearchParams({ tab }); if (scope === 'all') q.set('scope', 'all');
    return coreApi(`chat/inbox?${q}`); // { items:[{ id, kind, visitor, with, subject, agent, mine, preview, last_from, at, unread, waiting_since }] }
}
export async function getChatUnread() {
    return coreApi('chat/unread'); // { unread, visitors, team }
}
// every incoming message for me newer than `since` (the server keeps at most 15 minutes) — what the pop-ups show
export async function getChatNotify(since) {
    return coreApi(`chat/notify${since ? `?since=${encodeURIComponent(since)}` : ''}`); // { now, items:[{ conversation_id, kind, message_id, at, from, from_visitor, text, photo, subject }] }
}
export async function getChatThread(id, after = 0) {
    return coreApi(`chat/${encodeURIComponent(id)}?after=${Number(after) || 0}`); // { id, kind, pid, can_write, visitor, with, subject, messages, seen_by_them_at, typing }
}
export async function postChatMessage(id, text, attachment = null) {
    return coreApi(`chat/${encodeURIComponent(id)}/messages`, { method: 'POST', body: { text, ...(attachment ? { attachment } : {}) } });
}
export async function markChatSeen(id) {
    return coreApi(`chat/${encodeURIComponent(id)}/seen`, { method: 'POST', body: {} });
}
export async function sendChatTyping(id) {
    return coreApi(`chat/${encodeURIComponent(id)}/typing`, { method: 'POST', body: {} });
}
// a photo: the server hands a one-time upload link inside this conversation's private folder
export async function uploadChatPhoto(id, file) {
    const r = await coreApi(`chat/${encodeURIComponent(id)}/upload-url`, { method: 'POST', body: { type: file.type } });
    const put = await fetch(r.url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    if (!put.ok) throw new Error(`upload ${put.status}`);
    return { path: r.path };
}
export async function openDirectChat(agentId) {
    return coreApi('chat/direct', { method: 'POST', body: { agent_id: agentId } }); // { id }
}
// groups + the general chat (owner 2026-09-29, core 2717)
export async function openGeneralChat() {
    return coreApi('chat/general', { method: 'POST', body: {} }); // { id }
}
export async function createGroupChat(title, agentIds) {
    return coreApi('chat/group', { method: 'POST', body: { title, agent_ids: agentIds } }); // { id }
}
// mute = no sound, the pop-up stays (owner 2026-09-29, core 2720)
export async function muteChat(conversationId, muted) {
    return coreApi(`chat/${conversationId}/mute`, { method: 'POST', body: { muted: !!muted } }); // { muted }
}
export async function addChatMembers(conversationId, agentIds) {
    return coreApi(`chat/${conversationId}/members`, { method: 'POST', body: { agent_ids: agentIds } }); // { members }
}
export async function openPropertyChat(propertyId) {
    return coreApi('chat/property', { method: 'POST', body: { property_id: propertyId } });
}
export async function openClientChat(requirementId) {
    return coreApi('chat/client', { method: 'POST', body: { requirement_id: requirementId } });
}
// The intake question list for the agent's property card (core 2404, surface 'desk'): which facts a kind × subtype ×
// deal has and which are required — the same definition the sentavus.com owner form reads.
export async function getIntakeDefinition({ kind, subtype, intent } = {}) {
    const q = new URLSearchParams({ kind: kind || '', subtype: subtype || '', intent: intent || '', surface: 'desk' });
    return coreApi(`intake/definition?${q}`); // { questions:[{ key, required, tier, when, ... }], checks }
}
// management only (the server refuses anyone else): agents' team messages that carried a phone number (owner
// ruling 2026-09-23: allowed, flagged for managers)
export async function getChatFlagged() {
    return coreApi('chat/flagged'); // { items:[{ message_id, conversation_id, at, text, from, kind, to, subject }] }
}
// Luca, the site chat answered by Claude sessions (owner 2026-10-31, core 4090/4091) — management only.
// The live board: { board: { admission:{step,reason,responders,capacity,load,oldest_wait_s}, responders:[…], chats:[…], today:{…} } }
export async function getLucaBoard() { return coreApi('chat/luca/board'); }
// Every Luca conversation, newest first: { items:[{ id, pid, guest, status, created_at, last_at, preview, responder,
// handed_over, replies, reviewed, verdicts:{good,fix,bad} }], leads:[{ id, created_at, name, phone, note, pid, lang }] }
export async function getLucaConversations() { return coreApi('chat/luca/conversations'); }
// a callback lead marked called, or reopened (Luca audit 2026-11-01)
export async function markLucaLead(id, { reopen = false } = {}) { return coreApi(`chat/luca/lead/${id}/handled`, { method: 'POST', body: { reopen } }); }
// One interaction in order: { review: { conversation:{…}, events:[{ at, type:'message'|'decision', ref, who, text, actor,
// decision, context, detail, reply_id, review_verdict, review_note }] } }
export async function getLucaReview(id) { return coreApi(`chat/luca/review/${encodeURIComponent(id)}`); }
// A review verdict: kind 'reply' | 'decision', verdict 'good' | 'fix' | 'bad'
export async function markLucaReview({ kind, id, verdict, note }) {
    return coreApi('chat/luca/mark', { method: 'POST', body: { kind, id, verdict, note } });
}
export async function getContractMail() {
    return coreApi('contract-mail'); // { ok, configured, source, host, port, user, secure, from }
}
// Verified BEFORE it is stored: the server opens a real SMTP session and authenticates, and a
// refusal comes back as `detail` in the error body — the provider's own words are what tells an
// admin they need an app password rather than a different host.
export async function setContractMail({ host, port, secure, user, pass, from }) {
    return coreApi('contract-mail', { method: 'POST', body: { host, port, secure, user, pass, from } });
}
// Authenticating and DELIVERING are two different claims; this proves the second.
export async function testContractMail(to) {
    return coreApi('contract-mail/test', { method: 'POST', body: { to } }); // { ok, message_id, accepted }
}

// ── Paste update batches (core 1241) ────────────────────────────────────────────────────────────
// A paste on an existing property becomes a reviewable batch: evidence (intake), the AI proposal, field rows
// compared against what the agent sees, and append-only decisions. adminToken: a roster-less admin, verified
// server-side exactly like setPropertyAvailability.
export async function reviewPasteBatch({ proposalId, propertyId, form, sourceKind, agentId, adminToken = null }) {
    return coreApi('paste-batch/review', { method: 'POST', body: { proposal_id: proposalId, property_id: propertyId, form,
        source_kind: sourceKind || 'owner', agent_id: agentId || null, admin_token: adminToken || null } })
}
// agentId names the viewer: someone who does not work on a hand-made property sees only their own updates
// (core 1264). Only the agent id goes in the URL — never an admin token.
export async function getPasteBatches(propertyId, { agentId = null } = {}) {
    const d = await coreApi(`property/${propertyId}/paste-batches${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`)
    return d.batches || []
}
export async function decidePasteBatch(proposalId, { decisions, sourceKind, entryId, intent, kind, acknowledgeStale = [], agentId, adminToken = null }) {
    return coreApi(`paste-batch/${proposalId}/decide`, { method: 'POST', body: { decisions, source_kind: sourceKind || 'owner',
        entry_id: entryId || undefined, intent: intent || undefined, kind: kind || undefined, acknowledge_stale: acknowledgeStale,
        agent_id: agentId || null, admin_token: adminToken || null } })
}
export async function undoPasteBatch(proposalId, { fields = [], confirm = [], skip = [], agentId, adminToken = null }) {
    return coreApi(`paste-batch/${proposalId}/undo`, { method: 'POST', body: { fields, confirm, skip, agent_id: agentId || null, admin_token: adminToken || null } })
}

// The agent's own Claude connector (Settings → Notifications → Claude; server lib agent_ai_connector.js, owner 2026-10-14).
// The link comes back ONLY from aiConnectorCreate and is never stored or fetched again.
export const aiConnectorState  = () => coreApi('ai-connector')
export const aiConnectorCreate = () => coreApi('ai-connector/link', { method: 'POST', body: {} })
export const aiConnectorOff    = () => coreApi('ai-connector/off', { method: 'POST', body: {} })

// ── Visitor feedback on agents (owner 2026-10-14, core 3386/3390/3391, server lib/public_agent_feedback.js) ──────────
// view: 'held' (waiting for a person) | 'flagged' (published, flagged) | 'published' | 'hidden' | 'attempts' (refused texts)
export async function getAgentFeedbackQueue(view = 'held') {
    return coreApi(`agent-feedback?all=1&view=${encodeURIComponent(view)}`); // { items[], counts: { held, flagged, attempts } }
}
// state: 'published' | 'hidden' (hidden needs a reason; kept, never deleted)
export async function setAgentFeedbackState(id, state, reason) {
    return coreApi(`agent-feedback/${id}/state`, { method: 'POST', body: { state, reason } }); // { ok }
}
